<template>
  <div class="app-shell">
    <header class="topbar">
      <div class="brand-lockup">
        <div class="brand-mark">P</div>
        <div>
          <div class="brand-name">POOLDESK</div>
          <div class="brand-caption">signal intelligence</div>
        </div>
      </div>
      <div class="topbar-center">
        <span class="topbar-kicker">A-SHARE / LIVE MONITOR</span>
        <span class="topbar-divider"></span>
        <span class="topbar-date">{{ marketDate }}</span>
      </div>
      <div class="topbar-actions">
        <button class="alert-setting-button" type="button" @click="thsTradePanelOpen = true">模拟盘</button>
        <button class="alert-setting-button" type="button" :class="{ enabled: notificationPermission === 'granted' }" @click="enableDesktopNotifications">
          🔔 通知 {{ notificationPermission === 'granted' ? '开' : '关' }}
        </button>
        <button class="alert-setting-button" type="button" :class="{ enabled: alertSoundEnabled }" @click="toggleAlertSound">
          🔊 声音 {{ alertSoundEnabled ? '开' : '关' }}
        </button>
        <button
          class="alert-button"
          :class="{ active: highConfidenceAlerts.length }"
          type="button"
          :title="highConfidenceAlerts.length ? `有 ${highConfidenceAlerts.length} 条综合强势提醒` : '暂无综合强势提醒'"
          @click="openMarketPanel"
        >
          <span aria-hidden="true">!</span> 提醒
          <b v-if="highConfidenceAlerts.length">{{ highConfidenceAlerts.length }}</b>
          <small v-if="momentumObservations.length" class="observation-count">观察 {{ momentumObservations.length }}</small>
        </button>
        <button class="dat-points-badge" type="button" :title="datPointsTitle" @click="fetchDatPoints">
          DAT积分 {{ datPoints.status === 'ok' ? datPoints.balance_points : '--' }}
          <small>今日预留 {{ Number(datPoints.today_reserved_points || 0).toFixed(2) }}/{{ datPoints.daily_limit === undefined ? 5 : datPoints.daily_limit }}</small>
        </button>
        <span class="market-state">
          <span class="state-dot" :class="{ live: isMarketOpen }"></span>
          {{ marketStatus }}
        </span>
        <button class="icon-button" :class="{ spinning: loading }" type="button" aria-label="刷新" @click="refreshActiveView">
          ↻
        </button>
      </div>
    </header>

    <transition name="urgent-alert">
      <aside v-if="prominentAlert" class="urgent-alert-banner" role="alert" aria-live="assertive">
        <button class="urgent-alert-main" type="button" @click="openProminentAlert">
          <span class="urgent-alert-icon">!</span>
          <span class="urgent-alert-copy">
            <strong>强势机会提醒 · {{ prominentAlert.name || prominentAlert.code }} {{ prominentAlert.code }}</strong>
            <small>{{ (prominentAlert.reasons || []).join(' · ') }} · 综合 {{ Number(prominentAlert.score || 0).toFixed(1) }} 分<span v-if="newAlertBatchCount > 1"> · 另有 {{ newAlertBatchCount - 1 }} 条</span></small>
          </span>
          <span class="urgent-alert-action">查看详情</span>
        </button>
        <button class="urgent-alert-close" type="button" aria-label="关闭本次强势提醒" @click="dismissProminentAlert">×</button>
      </aside>
    </transition>

    <ths-trade-panel v-if="thsTradePanelOpen" :seed-stock="selectedRow || {}" @close="thsTradePanelOpen = false" />
    <main class="dashboard">
      <section class="decision-overview" aria-label="今日决策概览">
        <div class="decision-overview-head">
          <div>
            <p class="eyebrow">TODAY / DECISION DESK</p>
            <h1>今日短线决策台</h1>
          </div>
          <div class="view-mode-switch" role="tablist" aria-label="看板模式">
            <button type="button" :class="{ active: viewMode === 'trade' }" @click="selectViewMode('trade')">交易</button>
            <button type="button" :class="{ active: viewMode === 'research' }" @click="selectViewMode('research')">研究</button>
            <button type="button" :class="{ active: viewMode === 'etf' }" @click="selectViewMode('etf')">ETF</button>
            <button v-if="factorResearchEnabled" type="button" :class="{ active: viewMode === 'factor' }" @click="selectViewMode('factor')">因子研究</button>
          </div>
        </div>
        <div v-if="viewMode !== 'etf'" class="decision-metrics">
          <article>
            <span>风格状态 · 收盘确认</span>
            <strong>{{ styleAlphaStateText }}</strong>
            <small>{{ styleAlphaMomentumText }}</small>
          </article>
          <article>
            <span>持仓 Alpha · 收盘确认</span>
            <strong>{{ styleAlphaReadyHoldings }}/{{ styleAlphaHoldings.length }}</strong>
            <small>{{ styleAlphaHoldingText }}</small>
          </article>
          <article class="decision-positive">
            <span>风险闸门 · 收盘确认</span>
            <strong>{{ styleAlphaRiskText }}</strong>
            <small>{{ styleAlphaRiskDetail }}</small>
          </article>
          <article class="decision-risk">
            <span>日线数据截止</span>
            <strong>{{ styleAlpha.data_as_of || '待接入' }}</strong>
            <small>{{ styleAlphaQualityText }} · 盘中行情状态见下方</small>
          </article>
        </div>
        <div
          v-if="viewMode !== 'etf'"
          class="intraday-prompt"
          :class="{ warning: styleAlphaIntraday.alerts.some(item => item.level === 'warning'), caution: styleAlphaIntraday.alerts.length && !styleAlphaIntraday.alerts.some(item => item.level === 'warning') }"
          role="status"
          aria-live="polite"
        >
          <strong>盘中估算提示</strong>
          <div class="intraday-prompt-body">
            <span v-if="['ready', 'degraded'].includes(styleAlphaIntraday.status)" class="intraday-metrics">{{ styleAlphaIntradayMetrics }}</span>
            <span v-if="['ready', 'degraded'].includes(styleAlphaIntraday.status) && styleAlphaIntraday.alerts.length">
              {{ styleAlphaIntraday.alerts.map(item => item.text).join('；') }}
            </span>
            <span v-else-if="styleAlphaIntraday.status === 'ready'">当前未触发风格走弱或风险升温提示</span>
            <span v-else-if="styleAlphaIntraday.status === 'degraded'">{{ styleAlphaIntraday.message }}</span>
            <span v-else>{{ styleAlphaIntraday.message || '等待指数实时行情' }}</span>
            <small>
              {{ styleAlphaIntraday.as_of ? `行情 ${alertTime(styleAlphaIntraday.as_of)}` : '非实时/覆盖不足时不生成盘中信号' }}
              · 覆盖 {{ styleAlphaIntraday.coverage.available || 0 }}/{{ styleAlphaIntraday.coverage.required || 4 }}
              · 持仓实时 {{ styleAlphaIntradayHoldingsCoverage }}
              · 看板快照 {{ dashboard.updated_at || '--' }}
              · 估算仅供观察，不替代收盘确认
            </small>
          </div>
        </div>
        <details v-if="viewMode !== 'etf'" class="overview-notes">
          <summary>风格–Alpha 数据与口径 <span v-if="styleAlphaIssues.length" class="overview-warning-count">{{ styleAlphaIssues.length }} 条待补</span></summary>
          <div class="overview-context">
            <p>风格：创业板指 / 中证红利相对强弱，观察 20/60 日趋势。</p>
            <p>Alpha：持仓相对创业板指的归一化 20 日收益，不使用绝对价格比解释跑赢。</p>
            <p>风险：创业板 20 日年化波动、ATR Z 分数及回撤，只作观察闸门。</p>
            <p>执行边界：{{ styleAlpha.policy || '只读观察，不参与告警或自动交易。' }}</p>
          </div>
        <div v-if="styleAlphaIssues.length" class="opportunity-warning compact-warning">
          {{ styleAlphaIssues.join('；') }}
        </div>
        <div v-if="styleAlphaHoldings.length" class="style-alpha-holdings">
          <div v-for="item in styleAlphaHoldings" :key="item.code">
            <span>{{ item.name }} · {{ item.code }}</span>
            <strong>{{ item.alpha_20 == null ? '--' : styleAlphaPercent(item.alpha_20) }}</strong>
            <small>{{ item.status === 'ready' ? (item.above_alpha_ma20 ? 'Alpha 均线上方' : 'Alpha 跌破均线') : '历史不足' }}</small>
          </div>
        </div>
        <details v-if="boardFundsObservations.length" class="funds-observations">
          <summary>板块资金观察 · {{ boardFundsObservations.length }} 条</summary>
          <div v-for="board in boardFundsObservations" :key="`${board.source}-${board.board_type}-${board.direction}-${board.code}`">
            <span>{{ board.board_type === 'industry' ? '行业' : '概念' }} · {{ board.name }}</span>
            <strong>{{ supportMoneyText(board.main_net_inflow_yuan) }}</strong>
            <small>{{ l2SourceName(board.source) }} · {{ board.snapshot ? (board.stale ? '资金快照过期' : '资金快照，业务时间待核') : board.confirmation_eligible ? '业务日期已确认' : '日期待核，仅供观察' }} · {{ board.captured_at }}</small>
          </div>
        </details>
        </details>
      </section>

      <etf-momentum-panel v-if="viewMode === 'etf'" ref="etfPanel" />

      <button v-if="viewMode !== 'etf'" class="market-brief" type="button" aria-haspopup="dialog" @click="openMarketPanel">
        <span class="market-brief-label">盘面情报</span>
        <strong>{{ marketDiagnosis.title }}</strong>
        <span class="market-brief-summary">{{ marketDiagnosis.summary }}</span>
        <span class="market-brief-link">异动 / 雷达 <span aria-hidden="true">↗</span></span>
      </button>

      <market-repair-panel v-if="viewMode !== 'etf'" :model="dashboard.market_repair || {}" />
      <chan-market-panel v-if="viewMode !== 'etf'" @open-index="openStockDailyChart" />

      <section v-if="viewMode !== 'etf'" class="workspace">
        <aside class="pool-rail">
          <div class="rail-heading">
            <div>
            <p class="eyebrow">{{ viewMode === 'trade' ? 'WORK QUEUE' : (viewMode === 'factor' ? 'FACTOR LAB' : 'MODEL SOURCES') }}</p>
              <h2>{{ viewMode === 'trade' ? '工作池' : (viewMode === 'factor' ? '因子研究' : '模型源') }}</h2>
            </div>
            <span class="rail-count">{{ visiblePools.length }}</span>
          </div>

          <button
            class="pool-item all-pools"
            :class="{ active: activePoolId === 'all' }"
            type="button"
            @click="selectPool('all')"
          >
            <span class="pool-item-mark mark-all">◎</span>
            <span class="pool-item-copy">
              <strong>{{ viewMode === 'trade' ? '全部工作池' : (viewMode === 'factor' ? '当前因子组合' : '全部模型') }}</strong>
              <small>{{ uniqueTrackedCount }} 只股票</small>
            </span>
            <span class="pool-item-count">{{ visiblePoolCount }}</span>
          </button>

          <button
            v-for="pool in visiblePools"
            :key="pool.id"
            class="pool-item"
            :class="{ active: activePoolId === pool.id, stale: pool.stale }"
            type="button"
            @click="selectPool(pool.id)"
          >
            <span class="pool-item-mark" :class="poolMarkClass(pool.id)">{{ poolMark(pool.id) }}</span>
            <span class="pool-item-copy">
              <strong>{{ pool.short_label || pool.label }}</strong>
            <small>{{ viewMode === 'trade' ? tradePoolDescription(pool) : (viewMode === 'factor' ? `${factorSelectedCount} 个因子 · ${factorCurrentCount} 只` : poolSnapshotCaption(pool)) }}</small>
            </span>
            <span class="pool-item-count">{{ poolDisplayCount(pool) }}</span>
          </button>

          <div class="rail-note">
            <span class="note-line"></span>
            <p v-if="viewMode === 'trade'">一股一行，按决策状态归入唯一工作池。</p>
            <p v-else-if="viewMode === 'factor'">原子因子、组合评分和独立回测。</p>
            <p v-else>保留各底层模型和完整研究字段。</p>
          </div>
        </aside>

        <section class="pool-view">
          <div class="view-header">
            <div class="pool-title">
              <div class="view-kicker">
                <span class="active-pool-dot"></span>
                {{ viewMode === 'trade' ? '交易工作池' : (viewMode === 'factor' ? '因子选股' : '模型研究') }}
              </div>
              <div class="view-title-line">
                <h2 :title="activePoolLabel">{{ activePoolLabel }}</h2>
                <stock-daily-chart ref="stockDailyChart" compact />
              </div>
              <p :title="activePoolDescription">{{ activePoolDescription }}</p>
            </div>
            <div class="view-controls" :class="{ expanded: filtersExpanded }">
              <div class="view-filter-row">
                <label class="search-box">
                  <span>⌕</span>
                  <input v-model.trim="searchText" type="search" placeholder="搜索名称 / 代码">
                </label>
                <button class="mobile-filter-toggle" :class="{ selected: selectedBoardFilter || positiveOnly || selectedStageModelLabel }" type="button" :aria-expanded="String(filtersExpanded)" @click="filtersExpanded = !filtersExpanded">{{ filtersExpanded ? '收起' : '筛选 / 排序' }}<span v-if="selectedBoardFilter || positiveOnly || selectedStageModelLabel"> · 已筛选</span></button>
                <label class="board-filter">
                  <span>板块</span>
                  <select v-model="selectedBoardFilter" aria-label="按板块筛选">
                    <option value="">全部板块（{{ activeViewUniqueCount }}）</option>
                    <optgroup v-if="boardFilterOptions.industries.length" label="行业板块">
                      <option
                        v-for="item in boardFilterOptions.industries"
                        :key="item.value"
                        :value="item.value"
                      >{{ item.label }}（{{ item.count }}）</option>
                    </optgroup>
                    <optgroup v-if="boardFilterOptions.concepts.length" label="概念板块">
                      <option
                        v-for="item in boardFilterOptions.concepts"
                        :key="item.value"
                        :value="item.value"
                      >{{ item.label }}（{{ item.count }}）</option>
                    </optgroup>
                    <optgroup v-if="boardFilterOptions.strategies.length" label="策略板块">
                      <option
                        v-for="item in boardFilterOptions.strategies"
                        :key="item.value"
                        :value="item.value"
                      >{{ item.label }}（{{ item.count }}）</option>
                    </optgroup>
                  </select>
                </label>
                <label v-if="viewMode === 'research' && activePoolId === 'stage_model'" class="board-filter">
                  <span>阶段</span>
                  <select v-model="selectedStageModelLabel" aria-label="量价阶段候选筛选">
                    <option value="">全部阶段（{{ activePoolUniqueCount }}）</option>
                    <option v-for="item in stageModelFilterOptions" :key="item.label" :value="item.label">{{ item.label }}（{{ item.count }}）</option>
                  </select>
                </label>
                <button class="ghost-button" :class="{ selected: positiveOnly }" type="button" @click="togglePositiveOnly">
                  <span class="filter-glyph">↗</span> 只看上涨
                </button>
                <button class="ghost-button" :class="{ selected: hideLimitUp }" type="button" @click="hideLimitUp = !hideLimitUp">
                  过滤已涨停：{{ hideLimitUp ? '开' : '关' }}
                </button>
              </div>
              <div class="view-order-row">
                <span class="toolbar-caption">排序</span>
                <div class="mobile-sort-controls" aria-label="列表排序">
                  <label>
                    <select v-model="sortKey" @change="applyMobileSort" aria-label="选择排序字段">
                      <option value="change_pct">涨跌幅</option>
                      <option value="speed_1m_pct">1分钟涨速</option>
                      <option value="float_net_inflow_sort_pct">流通盘净流入</option>
                      <option value="main_participation_sort_pct">主力介入度</option>
                      <option value="main_net_intensity_pct">主力净流强度</option>
                      <option value="main_occupancy_sort_pct">主力占盘比</option>
                      <option value="score">{{ viewMode === 'trade' ? '共振优先' : (viewMode === 'factor' ? '因子分' : '形态分') }}</option>
                      <option value="price">现价</option>
                    </select>
                  </label>
                  <button
                    type="button"
                    :aria-label="sortDirection === 'desc' ? '切换为升序' : '切换为降序'"
                    @click="toggleSortDirection"
                  >{{ sortDirection === 'desc' ? '↓' : '↑' }}</button>
                </div>
                <label class="refresh-control">
                  <span>自动刷新</span>
                  <select v-model.number="refreshSeconds" @change="restartTimer">
                    <option :value="0">关闭</option>
                    <option :value="15">15秒</option>
                    <option :value="30">30秒</option>
                    <option :value="60">60秒</option>
                  </select>
                </label>
              </div>
            </div>
          </div>

          <div v-if="viewMode === 'research' && activePoolId === 'limit_pullback_repair'" class="repair-stage-tags" role="group" aria-label="连板回调阶段筛选">
            <button type="button" class="ghost-button" :class="{ selected: !selectedRepairStage }" :aria-pressed="String(!selectedRepairStage)" @click="selectedRepairStage = ''">全部（{{ activePoolUniqueCount }}）</button>
            <button v-for="item in repairStageFilterOptions" :key="item.label" type="button" class="ghost-button" :class="{ selected: selectedRepairStage === item.label }" :aria-pressed="String(selectedRepairStage === item.label)" @click="selectedRepairStage = item.label">{{ item.label }}（{{ item.count }}）</button>
            <span>结构仅观察 · 确认以生成时点为准</span>
          </div>

          <section v-if="factorResearchEnabled && viewMode === 'factor'" class="factor-research-panel">
            <div class="factor-panel-head">
              <div>
                <p class="eyebrow">COMBINATION BUILDER</p>
                <h3>组合选股与收益评估</h3>
              </div>
              <span v-if="factorCatalog.length">{{ factorSelectedCount }} / {{ factorCatalog.length }} 个因子</span>
              <span v-else>因子目录未加载</span>
              <button class="factor-secondary-button" type="button" :disabled="factorLoading" @click="fetchFactorResearch">重新读取</button>
            </div>
            <div v-if="factorLoading" class="factor-loading">正在读取因子快照…</div>
            <div v-if="factorResearchError" class="factor-error">{{ factorResearchError }}</div>
            <div v-if="factorCatalog.length" class="factor-panel-body">
              <p class="factor-warning">组合选股范围为已有股池；历史评估基于本地日线样本，尚未提供全市场 IC、回归和分组回测。</p>
              <details class="factor-warning">
                <summary>IC、回归、分组回测分别看什么？</summary>
                <p>IC：检验当日因子值与后续收益的相关性；Rank IC 检验排名关系。持续为正表示因子排序与未来收益方向一致，需同时看稳定性和有效样本数。</p>
                <p>多因子回归：同时放入多个因子，检验控制行业、市值等影响后，各因子是否仍有独立解释力；训练和验证必须按时间分开。</p>
                <p>分组回测：每个信号日按因子值分为五组或十组，比较各组后续收益、最高组减最低组的收益，以及收益是否随组别递增。</p>
                <p>组合评估：选因子、设权重和过滤条件，观察选出股票的后续收益。真实组合收益还需要仓位、调仓、成本和可成交规则。</p>
              </details>
              <div class="factor-groups">
                <div v-for="group in factorGroups" :key="group.name" class="factor-group">
                  <div class="factor-group-title"><strong>{{ group.name }}</strong><span>{{ group.items.length }}</span></div>
                  <label v-for="item in group.items" :key="item.id" class="factor-option">
                    <input v-model="factorControls[item.id].enabled" type="checkbox">
                    <span class="factor-option-copy"><strong>{{ item.name }}</strong><small>{{ item.id }} · {{ item.backtestable ? '可回测' : '仅当前画像' }}</small></span>
                    <input v-model.number="factorControls[item.id].weight" class="factor-weight" type="number" min="0" max="100" step="1" aria-label="因子权重">
                  </label>
                </div>
              </div>
              <div class="factor-runbar">
                <label><span>最低组合分</span><input v-model.number="factorMinScore" type="number" min="0" max="100" step="1"></label>
                <label class="factor-check"><input v-model="factorPositiveOnly" type="checkbox"><span>只看上涨</span></label>
                <label><span>回测开始</span><input v-model="factorBacktestStart" type="date"></label>
                <label><span>回测结束</span><input v-model="factorBacktestEnd" type="date"></label>
                <label><span>持有交易日</span><input v-model.number="factorHoldingDays" type="number" min="1" max="20"></label>
                <button class="factor-action-button" type="button" :disabled="factorLoading || !factorSelectedCount" @click="runFactorCombination">运行组合选股</button>
                <button class="factor-secondary-button" type="button" :disabled="factorBacktestLoading || !factorSelectedCount" @click="runFactorBacktest">评估样本收益</button>
              </div>
              <div v-if="factorBacktest.metrics && factorBacktest.status !== 'idle'" class="factor-metrics">
                <div><span>样本</span><strong>{{ factorBacktest.metrics.sample_count || 0 }}</strong></div>
                <div><span>胜率</span><strong>{{ Number(factorBacktest.metrics.win_rate_pct || 0).toFixed(1) }}%</strong></div>
                <div><span>平均收益</span><strong>{{ signedPercent(factorBacktest.metrics.average_return_pct || 0) }}</strong></div>
                <div><span>中位收益</span><strong>{{ signedPercent(factorBacktest.metrics.median_return_pct || 0) }}</strong></div>
                <div><span>最好单笔</span><strong>{{ factorBacktest.metrics.best_return_pct == null ? '—' : signedPercent(factorBacktest.metrics.best_return_pct) }}</strong></div>
                <div><span>最差单笔</span><strong>{{ factorBacktest.metrics.worst_return_pct == null ? '—' : signedPercent(factorBacktest.metrics.worst_return_pct) }}</strong></div>
              </div>
              <div v-if="factorBacktest.scope" class="factor-warning">日线覆盖 {{ factorBacktest.scope.covered_codes }} / {{ factorBacktest.scope.requested_codes }} 只；默认样本为本地股票库按代码排序前 1000 只。</div>
              <div v-if="factorBacktest.warnings && factorBacktest.warnings.length" class="factor-warning">{{ factorBacktest.warnings.join('；') }}</div>
              <div v-if="factorBacktestError" class="factor-warning">{{ factorBacktestError }}</div>
            </div>
          </section>

          <details v-if="viewMode === 'research' && activePoolId === 'bear_overnight'" class="overnight-method">
            <summary class="overnight-method-head"><strong>熊市观察 · 筛选规则</strong><span>14:30 起筛选 · 展开查看</span></summary>
            <p>涨幅 3%–5% · 总市值 50–200 亿 · 近 30 天有收盘涨停 · 量比 &gt;1 · 换手 5%–10%</p>
            <p>{{ overnightSettings.tdx_channel_model === 'tdx_formula_endpoint' ? '通达信公式当日端点' : overnightSettings.tdx_channel_model === 'ma10_endpoint' ? 'MA10近似通道' : '旧版通道（待重建）' }}：通道位置供参考，不作为入池条件。</p>
            <p>确认：站上成交均价与 MA5、跑赢大盘；全天均价上方时间 ≥{{ overnightSettings.min_above_vwap_time_pct }}%，或午后时间 ≥{{ overnightSettings.afternoon_min_above_vwap_time_pct }}%。有效采样覆盖需 ≥{{ overnightSettings.min_sample_coverage_pct }}%，近 {{ overnightSettings.entry_lookback_minutes }} 分钟突破守住或回踩企稳。</p>
            <p class="overnight-focus"><strong>{{ overnightFocus.code ? `${activePool && activePool.stale ? '历史焦点' : '唯一焦点'}：${overnightFocus.name} ${overnightFocus.code}` : '暂无唯一焦点' }}</strong> · {{ overnightFocus.message || '等待完整分时、资金与承接数据' }}<span v-if="overnightFocus.leader_code && !overnightFocus.code"> · 观察领先 {{ overnightFocus.name }}，连续 {{ overnightFocus.consecutive_scans }} 轮</span></p>
            <p>焦点分结合分时、资金持续性、L2观测承接、日线位置与板块。禁止行动时仅作研究；不自动下单，阈值尚未收益回测。</p>
            <p class="overnight-data-state">{{ overnightDiagnostics.message || '等待尾盘行情快照' }}<span v-if="activePool && activePool.stale"> · 历史快照，请核对交易日期</span></p>
            <details>
              <summary>筛选口径与确认边界</summary>
              <p>“近30天”采用自然日，“市值”采用总市值。未满足日线评分项或分时入场条件的股票仍保留候选。分时覆盖不足、指数缺失时标记待核对，不将缺失数据当作通过；优化参数尚未收益回测。</p>
              <p v-for="note in overnightImplementation" :key="note">{{ note }}</p>
            </details>
          </details>

          <details v-if="viewMode === 'research' && activePoolId === 'macd_bear_rebound'" class="overnight-method">
            <summary class="overnight-method-head"><strong>空头反抽 · 筛选规则</strong><span>{{ macdReboundSettings.start_time || '14:30' }}–{{ macdReboundSettings.end_time || '14:50' }} · 展开查看</span></summary>
            <p>零轴下死叉 · 昨日为本轮绿柱峰值 · 今日为峰后第一根缩短柱 · 实际放量上涨<span v-if="macdReboundSettings.require_bullish_candle !== false">收阳</span>。</p>
            <p>起量按今日实际累计成交量 &gt; 昨日全天成交量 × {{ macdReboundSettings.min_volume_ratio || 1 }} 判断。MACD 使用 12、26、9 参数，以实时价格计算今日暂定日线柱。</p>
            <p>尾盘观察，次日冲高兑现。每轮更新符合条件的股票，{{ macdReboundSettings.end_time || '14:50' }} 后保留最后一轮结果。</p>
            <p class="overnight-data-state">{{ macdReboundDiagnostics.message || '等待交易日14:30后生成股池' }}<span v-if="activePool && activePool.stale"> · 历史快照，请核对交易日期</span></p>
            <p v-if="macdReboundDiagnostics.quote_count !== undefined">本轮新鲜行情 {{ macdReboundDiagnostics.quote_count }} / 本地股票库 {{ macdReboundDiagnostics.universe_count }} 只 · 完成评估 {{ macdReboundDiagnostics.evaluated_count }} 只</p>
            <details v-if="macdReboundDiagnostics.rejection_counts">
              <summary>筛选淘汰与数据缺失</summary>
              <p v-for="(count, reason) in macdReboundDiagnostics.rejection_counts" :key="reason">{{ reason }}：{{ count }} 只</p>
              <p>昨日成交量或本轮成交量缺失：{{ macdReboundDiagnostics.missing_volume_count || 0 }} 只 · 真实开盘价缺失：{{ macdReboundDiagnostics.missing_open_count || 0 }} 只</p>
            </details>
          </details>

          <details v-if="viewMode === 'research' && activePoolId === 'limit_pullback_repair'" class="overnight-method">
            <summary class="overnight-method-head"><strong>连板回踩止跌 · 筛选规则</strong><span>主板真涨停 / 20cm三日强势 · 展开查看</span></summary>
            <p>主板必须连续至少3日收盘封死涨停；20厘米须连续3日上涨且累计30%–40% · 结构结束后2–15个交易日 · 首次回踩动态MA10 ±2%。</p>
            <p>入场观察区为MA10下方1%至上方1.5%；最近五分钟低点稳定、价格回收并有真实增量成交才提示。采样不足仅列观察，不再硬性要求反弹1.5%。</p>
            <p>固定一个主要比较题材，同期断板优选高板；低板及连续一字较多的降为次选。首次承接确认保留提示，已经修复、离开低吸区或支撑失效的同一结构不重入。</p>
            <p>同一结构仅提示一次；主板、20cm分开记录。资金和上一完整交易日筹码是辅助证据，缺失不冒充通过。研究提醒不自动买入。</p>
            <p class="overnight-data-state">{{ ((activePool || {}).method || {}).diagnostics ? activePool.method.diagnostics.message : '等待交易日09:30后行情与日线入库' }}<span v-if="activePool && activePool.stale"> · 历史快照，请核对交易日期</span></p>
            <p>强势结构展示已识别但未满足回踩条件的股票，点击个股查看未通过原因；回踩观察须满足原筛选规则，盘中确认须有五分钟承接证据。各阶段按股票去重，空标签隐藏。</p>
            <p>退出计划：真实成本4%价格保护、首次回踩低点、两根完整分钟破均线；三板2/3日、四五板3/4日时间退出。T+1和无买盘可能无法卖出，阈值尚未收益验证。</p>
            <details v-if="((activePool.method || {}).research_records || []).length"><summary>MA5首次回踩 · 独立研究记录（不自动买入）</summary><p v-for="item in activePool.method.research_records" :key="item.limit_pullback_repair_detail.cycle_id">{{ item.code }} {{ item.name }} · {{ item.limit_pullback_repair_detail.strength_label }} · {{ item.limit_pullback_repair_detail.peers.primary_theme }} · {{ item.limit_pullback_repair_detail.risk_plan.reference_at }} · 当前本地题材映射，非已验证龙头</p></details>
            <details v-if="((activePool.method || {}).risk_observations || []).length"><summary>跌停/翘板风险观察（不进入买入候选）</summary><p v-for="item in activePool.method.risk_observations" :key="item.code">{{ item.code }} {{ item.name }} · {{ item.reason }} · {{ item.at }}</p></details>
          </details>

          <div v-if="viewMode === 'factor'" class="count-legend factor-legend">
            当前组合 {{ activePoolUniqueCount }} 只股票 · 因子缺失不按负分处理 · 点击标的查看完整画像
          </div>
          <div v-else-if="viewMode === 'research'" class="count-legend">
            <template v-if="activePoolId === 'all'">
              跨池去重后 {{ activePoolUniqueCount }} 只股票 · 各池合计 {{ activePoolRecordCount }} 条信号记录
            </template>
            <template v-else>
              {{ activePoolUniqueCount }} 只股票
            </template>
            · 点击标的查看完整信号
          </div>
          <div v-else class="count-legend decision-legend">
            精选 {{ activePoolUniqueCount }} / 原始 {{ activeTradeSourceCount }} 只 · 已过滤 {{ activeTradeFilteredCount }} 只未达资金、L2与金叉共振门槛的观察
          </div>

          <div v-if="activeConfirmationNotice" class="notice-bar confirmation-notice">
            <span>i</span>
            <div>
              <strong>承接确认状态</strong>
              <p>{{ activeConfirmationNotice }}</p>
            </div>
          </div>

          <div v-if="error" class="notice-bar error-notice">
            <span>!</span>
            <div>
              <strong>行情聚合接口暂不可用</strong>
              <p>{{ error }}。页面保留本次已加载的数据。</p>
            </div>
            <button type="button" @click="refreshActiveView">重试</button>
          </div>
          <div v-else-if="dashboardCacheNotice" class="notice-bar">
            <span>i</span><div><strong>{{ dashboardCacheNotice }}</strong><p>{{ quoteUpdatedText }}</p></div>
          </div>

          <div class="table-card">
            <div class="table-head">
              <span class="head-rank">#</span>
              <span>标的 / {{ viewMode === 'trade' ? '模型来源' : (viewMode === 'factor' ? '因子状态' : '信号阶段') }}</span>
              <span class="head-price">
                <button
                  class="sort-button"
                  :class="{ active: sortKey === 'price' }"
                  type="button"
                  :aria-label="`按现价${sortKey === 'price' && sortDirection === 'asc' ? '升序' : '降序'}排序`"
                  @click="sortBy('price')"
                >
                  现价 <span class="sort-indicator">{{ sortIndicator('price') }}</span>
                </button>
                <button
                  class="sort-button"
                  :class="{ active: sortKey === 'change_pct' }"
                  type="button"
                  :aria-label="`按涨跌幅${sortKey === 'change_pct' && sortDirection === 'asc' ? '升序' : '降序'}排序`"
                  @click="sortBy('change_pct')"
                >
                  涨跌 <span class="sort-indicator">{{ sortIndicator('change_pct') }}</span>
                </button>
                <button
                  class="sort-button"
                  :class="{ active: sortKey === 'speed_1m_pct' }"
                  type="button"
                  title="当前价相对约1分钟前价格的涨幅，随行情刷新更新"
                  :aria-label="`按1分钟涨速${sortKey === 'speed_1m_pct' && sortDirection === 'asc' ? '升序' : '降序'}排序`"
                  @click="sortBy('speed_1m_pct')"
                >
                  涨速 <span class="sort-indicator">{{ sortIndicator('speed_1m_pct') }}</span>
                </button>
              </span>
              <span class="head-score">
                <button
                  class="sort-button"
                  :class="{ active: sortKey === 'score' }"
                  type="button"
                  :aria-label="`按评分${sortKey === 'score' && sortDirection === 'asc' ? '升序' : '降序'}排序`"
                  @click="sortBy('score')"
                >
                  {{ viewMode === 'trade' ? '共振' : (viewMode === 'factor' ? '因子分' : '校准/形态分') }} <span class="sort-indicator">{{ sortIndicator('score') }}</span>
                </button>
              </span>
              <span class="head-sector">{{ viewMode === 'trade' ? '触发 / 承接 / 结构位' : (viewMode === 'factor' ? '因子标签 / 覆盖率' : '板块 / 线索 / 结构位') }}</span>
              <span class="head-action">状态</span>
            </div>
            <div v-if="loading && !loaded" class="loading-state">正在加载股池快照…</div>
            <div v-else-if="filteredRows.length === 0" class="empty-state">
              <div class="empty-symbol">∅</div>
              <h3>当前筛选没有标的</h3>
              <p>可以切换股池、板块，或清空搜索条件。</p>
            </div>
            <template v-else>
              <button
                v-for="(row, index) in renderedRows"
                :key="row.pool_id + '-' + row.code + '-' + row.stage"
                class="stock-row"
                :class="{ selected: selectedRow && selectedRow.code === row.code && selectedRow.pool_id === row.pool_id }"
                type="button"
                @click="selectRow(row)"
              >
                <span class="row-rank">{{ String(index + 1).padStart(2, '0') }}</span>
                <span class="stock-identity">
                    <span class="stock-name-line">
                      <strong>{{ row.name || row.code }}</strong>
                      <span v-if="row.role_text" class="role-badge" :class="roleClass(row.role)">{{ row.role_text }}</span>
                      <span
                        v-if="viewMode !== 'factor' && capitalFlowBadge(row)"
                        class="capital-flow-badge"
                        :class="`capital-flow-${capitalFlowBadge(row).tone}`"
                      >{{ capitalFlowBadge(row).label }}</span>
                      <span v-if="fundsObservationLabel(row)" class="capital-flow-badge capital-flow-neutral"
                        :title="fundsObservationHint(row)">{{ fundsObservationLabel(row) }}</span>
                      <span v-if="kaipanlaSnapshot(row)" class="capital-flow-badge capital-flow-neutral"
                        :title="kaipanlaFundsHint(row)">开盘啦{{ kaipanlaSnapshotStale(kaipanlaSnapshot(row)) ? '快照过期' : '资金观察' }}</span>
                    </span>
                  <small :class="{ 'pool-membership-summary': viewMode === 'research' }">{{ row.code }} · {{ viewMode === 'research' ? researchPoolText(row) : (row.setup_type || row.stage || row.pool_label) }}</small>
                  <small v-if="row.stock_capital_flow && row.stock_capital_flow.snapshot" :title="kaipanlaFundsHint(row)">{{ capitalFlowText(row) }}</small>
                  <small v-if="viewMode === 'research' && researchStageText(row)" class="research-stage-summary" :title="researchStageText(row)">{{ researchStageText(row) }}</small>
                  <small v-if="row.evidence_total" class="evidence-summary">证据 {{ row.evidence_count }}/{{ row.evidence_total }} · {{ row.board_name || row.sector || row.industry || '主线待确认' }}</small>
                  <span v-if="viewMode === 'trade'" class="trade-confirmation-tags">
                    <span
                      v-for="item in tradeConfirmationTags(row)"
                      :key="item.key"
                      class="trade-confirmation-badge"
                      :class="{ passed: item.passed, pending: item.pending, failed: item.failed }"
                    >{{ item.label }}</span>
                  </span>
                  <span v-if="activeMaCrossSignals(row).length" class="ma-cross-tags">
                    <span
                      v-for="signal in activeMaCrossSignals(row)"
                      :key="`${signal.label}-${signal.confirmation_type}`"
                      :title="maCrossDetailText(signal)"
                      class="ma-cross-badge"
                      :class="maCrossClass(signal)"
                    >{{ signal.label }} · {{ maCrossStateText(signal) }}</span>
                  </span>
                  <span v-if="viewMode === 'factor' && row.factor_tags && row.factor_tags.length" class="factor-tags">
                    <span v-for="tag in row.factor_tags" :key="tag.id" class="factor-tag" :class="`factor-tag-${tag.tone}`">{{ tag.label }}</span>
                  </span>
                </span>
                <span class="stock-price">
                  <strong>{{ priceText(row.price) }}</strong>
                  <span :class="changeClass(row.change_pct)">{{ signedPercent(row.change_pct) }}</span>
                  <small class="stock-speed" :class="changeClass(speedValue(row))" :title="speedHint(row)">1分 {{ speedText(row) }}</small>
                </span>
                <span class="score-cell">
                  <strong>{{ row.action_grade || scoreText(poolDisplayScore(row)) }}</strong>
                  <small>{{ row.action_grade ? `行动分 ${tradeScoreText(row)}` : (viewMode === 'factor' ? `覆盖 ${Number(row.factor_coverage_pct || 0).toFixed(0)}%` : (viewMode === 'research' ? poolScoreCaption(row) : (actionReference(row) || row.tier || scoreLabel(row.score)))) }}</small>
                </span>
                <span class="signal-cell">
                  <small v-if="bottomRepairText(row)" class="bottom-repair-row" :title="`已完成日线 ${row.bottom_repair.trade_date || '--'} · 点击日K查看四项证据`">{{ bottomRepairText(row) }}</small>
                  <span class="sector-name" :title="row.trigger_text || row.stage">{{ viewMode === 'factor' ? (row.factor_missing && row.factor_missing.length ? `待补 ${row.factor_missing.length} 项` : '组合因子均可用') : (row.trigger_text || row.stage || '结构观察') }}</span>
                  <small class="support-signal" :class="supportToneClass(row)" :title="row.support_signal_text || row.support_text || primaryReason(row)">{{ viewMode === 'factor' ? `${row.factor_coverage_pct || 0}% 覆盖 · ${factorSourceText(row)}` : (row.support_signal_text || row.support_text || primaryReason(row)) }}</small>
                  <small
                    v-if="row.platform_levels && row.platform_levels.available"
                    class="box-row-signal"
                    :class="platformToneClass(row.platform_levels)"
                  >{{ platformListText(row) }}</small>
                </span>
                <span class="row-status">
                  <span class="status-chip" :class="decisionStatusClass(row)">{{ row.decision_state_text || statusText(row) }}</span>
                  <span class="row-chevron">›</span>
                </span>
              </button>
            </template>
            <button
              v-if="hasMoreRenderedRows"
              class="load-more-button"
              type="button"
              @click="loadMoreRows"
            >加载更多 · 剩余 {{ filteredRows.length - renderedRows.length }} 条</button>
            <div v-if="filteredRows.length > 0" class="table-foot">
              显示 {{ renderedRows.length }} 条记录 / 当前筛选 {{ filteredRows.length }} 条 · {{ filteredUniqueCount }} 只股票 · 按{{ sortLabel }}排序
            </div>
          </div>
        </section>

        <button
          v-if="detailPanelOpen || marketPanelOpen"
          class="detail-backdrop"
          type="button"
          aria-label="关闭侧面板"
          @click="closeDetailPanel"
        ></button>

        <section v-show="detailPanelOpen || marketPanelOpen" ref="insightPanel" class="insight-grid" :class="{ 'detail-open': detailPanelOpen || marketPanelOpen, 'market-open': marketPanelOpen }" role="dialog" aria-modal="true" :aria-label="marketPanelOpen ? '盘面情报' : '个股信号详情'" tabindex="-1" @keydown.tab="trapPanelFocus">
        <article v-if="!marketPanelOpen" class="insight-card detail-card">
          <div class="card-topline">
            <span class="eyebrow">SELECTED SIGNAL</span>
            <div class="detail-top-actions">
              <span v-if="selectedRow" class="selected-pool">{{ selectedRow.work_pool_label || selectedRow.pool_label }}</span>
              <button class="detail-close" type="button" aria-label="关闭信号详情" @click="closeDetailPanel">×</button>
            </div>
          </div>
          <div v-if="selectedRow" class="selected-content">
            <div class="selected-title">
              <div>
                <h3>{{ selectedRow.name }}</h3>
                <p>{{ selectedRow.code }} · {{ selectedRow.stage || '未分组' }}</p>
                <button class="stock-daily-trigger" type="button" @click="openStockDailyChart(selectedRow)">日K / 均线 ↗</button>
              </div>
              <div class="selected-price">
                <strong>{{ priceText(selectedRealtimePrice) }}</strong>
                <span :class="changeClass(selectedRealtimeChange)">{{ signedPercent(selectedRealtimeChange) }}</span>
              </div>
            </div>
            <details v-if="viewMode === 'research'" class="selected-models">
              <summary>所属股池与完整信号</summary>
              <p>{{ researchPoolText(selectedRow) }}</p>
              <p>{{ researchStageText(selectedRow) }}</p>
            </details>
            <section class="decision-detail-summary">
              <div><span>决策</span><strong>{{ selectedRow.decision_state_text || statusText(selectedRow) }}</strong></div>
              <div><span>地位</span><strong>{{ selectedRow.role_text || '地位待确认' }}</strong></div>
              <div><span>触发</span><strong>{{ selectedRow.trigger_text || selectedRow.stage || '等待触发' }}</strong></div>
              <div v-if="viewMode === 'trade'"><span>交易共振</span><strong>{{ Number(selectedRow.trade_confirmation_count || 0) }}/{{ Number(selectedRow.trade_confirmation_total || 4) }} 项</strong></div>
              <div><span>个股资金</span><strong>{{ capitalFlowText(selectedRow) }}</strong></div>
              <div v-if="kaipanlaSnapshot(selectedRow)"><span>资金口径</span><small>{{ kaipanlaFundsHint(selectedRow) }}</small></div>
              <div><span title="主力净流入额 ÷ 实时流通市值">流通盘净流入</span><strong>{{ capitalMetricText(selectedRow, 'float') }}</strong></div>
              <div><span title="主力买入额与卖出额之和 ÷ 当日总成交额">主力介入度</span><strong>{{ capitalMetricText(selectedRow, 'participation') }}</strong></div>
              <div><span title="数据源提供的主力净流入方向强度，正值为净流入">主力净流强度</span><strong>{{ capitalMetricText(selectedRow, 'intensity') }}</strong></div>
              <div><span title="主力买入额与卖出额之和 ÷ 实时流通市值">主力占盘比</span><strong>{{ capitalMetricText(selectedRow, 'occupancy') }}</strong></div>
              <div class="decision-invalid"><span>失效条件</span><strong>{{ selectedRow.invalid_condition || '原模型结构失效' }}</strong></div>
            </section>
            <section class="support-strip" :class="supportToneClass(selectedSupport)">
              <p v-if="selectedRow.capital_metric_source" class="support-strip-note">资金来源：{{ capitalSourceText(selectedRow) }}</p>
              <div class="support-strip-head">
                <div>
                  <span>{{ selectedL2SourceLabel }} 承接观察</span>
                  <small>{{ selectedSupport.freshness }} · {{ selectedSupport.confidence }} · {{ selectedL2Version }}</small>
                </div>
                <strong>{{ selectedSupport.signal }}</strong>
              </div>
              <div v-if="selectedSupport.hasMetrics" class="support-strip-grid">
                <div v-if="selectedSupport.coverage"><span>即时</span><strong>{{ supportScoreText(selectedSupport.score) }}</strong></div>
                <div v-if="selectedL2Factor.support_window && selectedL2Factor.support_window.support_decay_score !== undefined"><span>滚动</span><strong>{{ supportScoreText(selectedSupport.rollingScore) }}</strong></div>
                <div v-if="hasMetric(selectedSupport.bookImbalance)" :title="supportMetricSource('book')"><span>盘口</span><strong>{{ ratioText(selectedSupport.bookImbalance, true) }}</strong></div>
                <div v-if="hasMetric(selectedL2Factor.bid_amount)" :title="supportMetricSource('book')"><span>委买额</span><strong>{{ supportMoneyText(selectedL2Factor.bid_amount) }}</strong></div>
                <div v-if="hasMetric(selectedL2Factor.ask_amount)" :title="supportMetricSource('book')"><span>委卖额</span><strong>{{ supportMoneyText(selectedL2Factor.ask_amount) }}</strong></div>
                <div v-if="hasMetric(selectedSupport.activeBuyRatio)" :title="supportMetricSource('active')"><span>主动买</span><strong>{{ ratioText(selectedSupport.activeBuyRatio, false) }}</strong></div>
                <div v-if="hasMetric(selectedSupport.queueImbalance)" :title="supportMetricSource('queue')"><span>队列</span><strong>{{ ratioText(selectedSupport.queueImbalance, true) }}</strong></div>
                <div v-if="hasMetric(selectedSupport.cancelImbalance)" :title="supportMetricSource('cancel')"><span>撤单方向</span><strong>{{ ratioText(selectedSupport.cancelImbalance, true) }}</strong></div>
                <div v-if="hasMetric(selectedSupport.netFlow)" :title="supportMetricSource('capital_flow')"><span>主力净额</span><strong>{{ supportMoneyText(selectedSupport.netFlow) }}</strong></div>
                <div v-if="hasMetric((selectedL2Factor.big_order_flow || {}).net_inflow_yuan)" :title="supportMetricSource('big_order_flow')"><span>大单净额</span><strong>{{ supportMoneyText(selectedL2Factor.big_order_flow.net_inflow_yuan) }}</strong></div>
              </div>
              <small v-else class="support-source-note">{{ l2Watch.d202Message || '当前没有有效L2指标，等待采集回传' }}</small>
              <small v-if="selectedSupport.hasMetrics" class="support-strip-updated">{{ selectedSupport.updatedAt || '等待实时承接数据' }}</small>
              <small v-if="selectedSupport.hasMetrics" class="support-source-note">{{ supportSourceSummary }} · 评分按{{ l2SourceName(selectedL2Factor.score_source || selectedL2Factor.source) }}原口径</small>
              <div v-if="l2Watch.tqBook" class="support-strip-grid">
                <div><span>TQ 买一 · 五档</span><strong>{{ ((l2Watch.tqBook.bids || [])[0] || {}).price || '--' }}</strong></div>
                <div><span>TQ 卖一 · 五档</span><strong>{{ ((l2Watch.tqBook.asks || [])[0] || {}).price || '--' }}</strong></div>
              </div>
              <small v-if="l2Watch.tqBook" class="support-source-note">TQ 五档快照 · {{ l2Watch.tqBook.captured_at }} · {{ l2Watch.tqBook.update_received ? '收到订阅更新' : '行情业务时间待确认' }}</small>
              <div class="support-paid-actions">
                <span class="support-paid-label">手动补充 L2 · 点击后采样一次</span>
                <small class="support-paid-status" :class="{ blocked: !datPoints.paid_available }">{{ paidL2AvailabilityText }}</small>
                <button type="button" :disabled="!datPoints.paid_available || l2PaidPending" @click="samplePaidL2('d204')">{{ l2PaidPending === 'd204' ? 'D204采样中…' : 'D204积分补充' }}</button>
                <button type="button" :disabled="!datPoints.paid_available || l2PaidPending" @click="samplePaidL2('d203')">{{ l2PaidPending === 'd203' ? 'D203采样中…' : 'D203成交/撤单补充' }}</button>
                <button v-if="!datPoints.paid_available" type="button" :disabled="datPointsLoading" @click="fetchDatPoints">{{ datPointsLoading ? '检查积分中…' : '重新检查积分' }}</button>
                <small>手动单次采样；完成即退订；余额扣减以客户端结算为准</small>
                <small v-for="item in selectedPaidSamples" :key="item.channel">{{ paidSampleText(item) }}</small>
              </div>
              <small v-if="l2Watch.error" class="support-source-note">{{ l2Watch.error }}</small>
            </section>
            <div v-if="selectedRow.evidence_items && selectedRow.evidence_items.length" class="evidence-matrix">
              <span
                v-for="item in selectedRow.evidence_items"
                :key="item.key"
                :class="{ passed: item.passed, unknown: item.unknown }"
              >{{ item.label }} · {{ item.passed ? '通过' : (item.unknown ? '待补' : '未通过') }}</span>
            </div>
            <section v-if="viewMode === 'factor' && selectedRow.factor_profile && selectedRow.factor_profile.length" class="factor-profile-detail">
              <div class="factor-profile-head"><span>原子因子画像</span><small>{{ selectedRow.factor_coverage_pct || 0 }}% 可用 · {{ selectedRow.factor_version || 'research-v1' }}</small></div>
              <div class="factor-profile-list">
                <div v-for="factor in selectedRow.factor_profile" :key="factor.id" class="factor-profile-row" :class="{ unavailable: !factor.available }">
                  <span><strong>{{ factor.name }}</strong><small>{{ factor.source || 'unknown' }} · {{ factor.status }}</small></span>
                  <b>{{ factor.score === null || factor.score === undefined ? '--' : Number(factor.score).toFixed(1) }}</b>
                  <em>{{ factor.raw_value === null || factor.raw_value === undefined ? '--' : factor.raw_value }}</em>
                </div>
              </div>
            </section>
            <div v-if="viewMode === 'research'" class="selected-metrics">
              <div><span>{{ selectedRow.buy_score_status === '已校准' ? '样本外校准分' : '池内形态分' }}</span><strong>{{ scoreText(poolDisplayScore(selectedRow)) }}</strong></div>
              <div><span>验证状态</span><strong>{{ selectedRow.buy_score_status || '未验证' }}</strong></div>
              <div v-if="selectedRow.buy_score_status !== '已校准'"><span>说明</span><strong>{{ selectedRow.buy_score_reason || '该分仅表示本股池内形态符合度，不代表胜率' }}</strong></div>
              <div v-if="selectedRow.buy_score_status === '已校准' && selectedRow.expected_return_pct != null"><span>历史T+3均收益</span><strong>{{ signedPercent(selectedRow.expected_return_pct) }}</strong></div>
              <div v-if="selectedRow.buy_score_status === '已校准' && selectedRow.predicted_win_rate_pct != null"><span>样本外T+3胜率</span><strong>{{ Number(selectedRow.predicted_win_rate_pct).toFixed(1) }}%</strong></div>
              <div><span>行动参考</span><strong>{{ actionReference(selectedRow) || '未分层' }}</strong></div>
              <template v-if="isStageModelSelected">
                <div><span>当前阶段进入日</span><strong>{{ currentStagePhase.interval_start || '--' }}</strong></div>
                <div><span>最近确认日</span><strong>{{ currentStagePhase.last_confirmed_date || selectedRow.tracking_last_signal_date || '--' }}</strong></div>
                <div><span>阶段持续</span><strong>{{ currentStagePhase.inferred_bars || (Number(selectedRow.tracking_age_bars || 0) + 1) }} 个交易日</strong></div>
              </template>
              <div v-if="isStageModelSelected && selectedRow.tdx_channel && selectedRow.tdx_channel.trade_date">
                <span>通达信通道 · {{ selectedRow.tdx_channel.trade_date }}</span>
                <strong>{{ selectedRow.tdx_channel_not_gun ? '未出通道' : '未满足/数据不足' }}</strong>
              </div>
              <div v-if="isStageModelSelected && selectedRow.tdx_channel && selectedRow.tdx_channel.lower > 0">
                <span>MA10通道下轨 / 上轨</span>
                <strong>{{ priceText(selectedRow.tdx_channel.lower) }} / {{ priceText(selectedRow.tdx_channel.upper) }}</strong>
              </div>
              <div v-if="isStageModelSelected && selectedRow.tdx_channel_cao">
                <span>通达信 · 操（独立信号）</span>
                <strong>{{ selectedRow.tdx_channel.cao_reclaimed ? '已收回下轨' : '尚未收回下轨' }}</strong>
              </div>
              <div v-if="isStageModelSelected && selectedRow.tdx_channel_cao">
                <span>操 · 开盘价 / 下轨</span>
                <strong>{{ priceText(selectedRow.tdx_channel.open) }} / {{ priceText(selectedRow.tdx_channel.lower) }}</strong>
              </div>
              <div v-if="isStageModelSelected && selectedRow.chip_peak_detail && selectedRow.chip_peak_detail.trade_date">
                <span>筹码峰 · {{ selectedRow.chip_peak_detail.trade_date }}</span>
                <strong>{{ priceText(selectedRow.chip_peak_detail.peak_price) }} · 较峰 {{ signedPercent(selectedRow.chip_peak_detail.distance_pct) }}</strong>
              </div>
              <div v-if="isStageModelSelected && selectedRow.chip_peak_detail && selectedRow.chip_peak_detail.trade_date">
                <span title="原字段 chip_concentration_pct，实际含义是15%至85%筹码成本区间的相对带宽；越低越集中">筹码成本带宽</span><strong>{{ unsignedPercentOrDash(chipBandWidth(selectedRow.chip_peak_detail)) }}</strong>
              </div>
              <div v-if="isStageModelSelected && selectedRow.chip_peak_detail && selectedRow.chip_peak_detail.trade_date">
                <span title="展示评分=100×(1-成本带宽/12%)，限制在0至100；只用于直观展示，不改变选股门槛">筹码密集度评分</span><strong>{{ densityScoreText(chipDensityScore(selectedRow.chip_peak_detail)) }}</strong>
              </div>
              <div v-if="isStageModelSelected && selectedRow.chip_peak_detail && selectedRow.chip_peak_detail.trade_date">
                <span>带宽较昨日 / 3日</span>
                <strong>{{ signedPointsOrDash(selectedRow.chip_peak_detail.width_change_1d_pct_points) }} / {{ signedPointsOrDash(selectedRow.chip_peak_detail.width_change_3d_pct_points) }}</strong>
              </div>
              <div v-if="isStageModelSelected && selectedRow.chip_peak_detail && selectedRow.chip_peak_detail.trade_date">
                <span>筹码趋势状态</span><strong>{{ selectedRow.chip_peak_detail.trend_state || '观察中' }}</strong>
              </div>
              <div v-if="isStageModelSelected && selectedRow.chip_trend_t5_detail && selectedRow.chip_trend_t5_detail.trade_date">
                <span>T+5观察条件</span>
                <strong>{{ selectedRow.chip_trend_t5 ? '基础条件满足' : '未满足' }}</strong>
              </div>
              <div v-if="isStageModelSelected && selectedRow.chip_trend_t5_detail && selectedRow.chip_trend_t5_detail.trade_date">
                <span>峰距 / 成本带宽</span>
                <strong>{{ signedPercent(selectedRow.chip_trend_t5_detail.distance_pct) }} / {{ unsignedPercentOrDash(chipBandWidth(selectedRow.chip_trend_t5_detail)) }}</strong>
              </div>
              <div v-if="isStageModelSelected && selectedRow.chip_trend_t5_detail && selectedRow.chip_trend_t5_detail.trade_date">
                <span>盘中参与状态</span>
                <strong>{{ selectedRow.chip_trend_t5_participation_ready ? '盘中可参与' : '等待条件' }}</strong>
              </div>
              <div v-if="isStageModelSelected && selectedRow.chip_trend_t5_detail && selectedRow.chip_trend_t5_detail.market_breadth_pct !== undefined">
                <span>日内位置 / 市场宽度</span>
                <strong>{{ percentOrDash(Number(selectedRow.chip_trend_t5_detail.intraday_position || 0) * 100) }} / {{ percentOrDash(selectedRow.chip_trend_t5_detail.market_breadth_pct) }}</strong>
              </div>
              <div v-if="isBullishOversoldSelected && selectedRow.bullish_oversold">
                <span>多头超跌初筛</span>
                <strong>{{ selectedRow.channel_provisional ? '盘中候选 · 待收盘重筛' : '收盘条件满足' }}</strong>
              </div>
              <div v-if="isBullishOversoldSelected && selectedRow.bullish_oversold_detail && selectedRow.bullish_oversold_detail.trade_date">
                <span>MA13 / MA17 / MA55</span>
                <strong>{{ priceText(selectedRow.bullish_oversold_detail.ma13) }} / {{ priceText(selectedRow.bullish_oversold_detail.ma17) }} / {{ priceText(selectedRow.bullish_oversold_detail.ma55) }}</strong>
              </div>
              <div v-if="isBullishOversoldSelected && selectedRow.bullish_oversold_detail && selectedRow.bullish_oversold_detail.trade_date">
                <span>均线粘合 / 筹码离散</span>
                <strong>{{ percentOrDash(selectedRow.bullish_oversold_detail.ma_spread_pct) }} / {{ percentOrDash(selectedRow.bullish_oversold_detail.chip_concentration_pct) }}</strong>
              </div>
              <div v-if="isBullishOversoldSelected && selectedRow.bullish_oversold_detail && selectedRow.bullish_oversold_detail.trade_date">
                <span>筹码峰 / 信号日{{ ((selectedRow.bullish_oversold_detail || {}).channel_bottom || {}).model_label || '通道' }}下轨</span>
                <strong>{{ priceText(selectedRow.bullish_oversold_detail.chip_peak_price) }} / {{ priceText(selectedRow.bullish_oversold_detail.causal_lower) }}</strong>
              </div>
              <div v-if="isBullishOversoldSelected && (selectedRow.bullish_oversold_detail || {}).channel_bottom">
                <span>{{ selectedRow.channel_provisional ? '盘中现价' : '信号日收盘' }} / {{ (selectedRow.bullish_oversold_detail.channel_bottom || {}).model_label || '通道' }}底部区间</span>
                <strong>{{ priceText(selectedRow.bullish_oversold_detail.channel_bottom.price) }} / {{ priceText(selectedRow.bullish_oversold_detail.channel_bottom.lower) }}–{{ priceText(selectedRow.bullish_oversold_detail.channel_bottom.bottom_upper) }}</strong>
              </div>
              <div v-if="isBullishOversoldSelected && (selectedRow.bullish_oversold_detail || {}).channel_bottom">
                <span>通道位置 · 供参考</span>
                <strong>{{ percentOrDash(selectedRow.bullish_oversold_detail.channel_bottom.position_pct) }} · {{ selectedRow.bullish_oversold_detail.channel_bottom.available ? (selectedRow.bullish_oversold_detail.channel_bottom.matched ? '底部' : '非底部') : '数据不足' }}</strong>
              </div>
              <div v-if="['ma_cross_channel', 'stage_model', 'first_board_discovery'].includes(selectedRow.pool_id) && (selectedRow.channel_bottom || {}).available">
                <span>{{ selectedRow.channel_provisional ? '盘中现价' : '入选价格' }}（{{ selectedRow.channel_bottom.trade_date }}） / {{ selectedRow.channel_bottom.model_label || '通道' }}底部区间</span>
                <strong>{{ priceText(selectedRow.channel_bottom.price) }} / {{ priceText(selectedRow.channel_bottom.lower) }}–{{ priceText(selectedRow.channel_bottom.bottom_upper) }}</strong>
              </div>
              <div v-if="['ma_cross_channel', 'stage_model', 'first_board_discovery'].includes(selectedRow.pool_id) && (selectedRow.channel_bottom || {}).available">
                <span>通道下轨 / 上轨</span>
                <strong>{{ priceText(selectedRow.channel_bottom.lower) }} / {{ priceText(selectedRow.channel_bottom.upper) }}</strong>
              </div>
              <div v-if="selectedRow.channel_provisional">
                <span>盘中候选状态</span><strong>{{ selectedRow.source_pattern_date }}结构候选 · 待收盘确认</strong>
              </div>
              <div v-if="['ma_cross_channel', 'stage_model', 'first_board_discovery'].includes(selectedRow.pool_id) && (selectedRow.channel_bottom || {}).available">
                <span>通道日期 {{ selectedRow.channel_bottom.trade_date }} · 低点 / 价格位置{{ selectedRow.pool_id === 'stage_model' ? ' · 底部状态' : ' · 供参考' }}</span>
                <strong>{{ priceText(selectedRow.channel_bottom.intraday_low) }} / {{ percentOrDash(selectedRow.channel_bottom.position_pct) }} · {{ selectedRow.channel_bottom.match_label || '非底部' }}</strong>
              </div>
              <div v-if="selectedRow.pool_id === 'ma_cross_channel' && (selectedRow.entry_triggers || []).length">
                <span>入池触发</span><strong>{{ selectedRow.entry_triggers.join('、') }}</strong>
              </div>
              <div v-if="selectedRow.pool_id === 'ma_cross_channel' && (selectedRow.limit_pullback_detail || {}).limit_date">
                <span>涨停回调 · {{ selectedRow.limit_pullback_detail.limit_date }}</span>
                <strong>{{ selectedRow.limit_pullback_detail.matched ? '老金叉守低通过' : selectedRow.limit_pullback_detail.reason }}</strong>
              </div>
              <div v-if="selectedRow.pool_id === 'ma_cross_channel' && (selectedRow.limit_pullback_detail || {}).limit_date">
                <span>涨停日低点 / 此后回调最低</span>
                <strong>{{ priceText(selectedRow.limit_pullback_detail.limit_low) }} / {{ priceText(selectedRow.limit_pullback_detail.pullback_low) }}</strong>
              </div>
              <div v-if="isBullishOversoldSelected && selectedRow.bullish_oversold">
                <span>盘中承接状态</span>
                <strong>{{ selectedRow.bullish_oversold_confirmation_ready ? '承接确认' : '等待承接' }}</strong>
              </div>
              <div v-if="isBullishOversoldSelected && selectedRow.signal_tier">
                <span>信号分层 / 信号日</span>
                <strong>{{ selectedRow.signal_tier }} / {{ selectedRow.signal_trade_date || '--' }}</strong>
              </div>
              <div v-if="isBullishOversoldSelected && selectedRow.lifecycle">
                <span>生命周期</span>
                <strong>{{ selectedRow.tracking_status || '跟踪中' }} · T+{{ selectedRow.tracking_age_bars || 0 }}</strong>
              </div>
              <div v-if="isBullishOversoldSelected && selectedRow.checkpoints && Object.keys(selectedRow.checkpoints).length">
                <span>T+3 / T+5 / T+10</span>
                <strong>
                  {{ selectedRow.checkpoints['3d_close'] ? signedPercent(selectedRow.checkpoints['3d_close'].pct) : '--' }} /
                  {{ selectedRow.checkpoints['5d_close'] ? signedPercent(selectedRow.checkpoints['5d_close'].pct) : '--' }} /
                  {{ selectedRow.checkpoints['10d_close'] ? signedPercent(selectedRow.checkpoints['10d_close'].pct) : '--' }}
                </strong>
              </div>
              <div v-if="isBullishOversoldSelected && selectedRow.checkpoints && Object.keys(selectedRow.checkpoints).length">
                <span>同期全A等权超额</span>
                <strong>
                  {{ selectedRow.checkpoints['3d_close'] && selectedRow.checkpoints['3d_close'].excess_pct !== null ? signedPercent(selectedRow.checkpoints['3d_close'].excess_pct) : '--' }} /
                  {{ selectedRow.checkpoints['5d_close'] && selectedRow.checkpoints['5d_close'].excess_pct !== null ? signedPercent(selectedRow.checkpoints['5d_close'].excess_pct) : '--' }} /
                  {{ selectedRow.checkpoints['10d_close'] && selectedRow.checkpoints['10d_close'].excess_pct !== null ? signedPercent(selectedRow.checkpoints['10d_close'].excess_pct) : '--' }}
                </strong>
              </div>
              <div v-if="isBullishOversoldSelected && selectedRow.bullish_oversold_detail && selectedRow.bullish_oversold_detail.market_breadth_pct !== undefined">
                <span>日内位置 / 市场宽度 / 开盘缺口</span>
                <strong>{{ percentOrDash(Number(selectedRow.bullish_oversold_detail.intraday_position || 0) * 100) }} / {{ percentOrDash(selectedRow.bullish_oversold_detail.market_breadth_pct) }} / {{ percentOrDash(selectedRow.bullish_oversold_detail.open_gap_pct) }}</strong>
              </div>
              <div v-if="isFirstBoardSelected && selectedRow.signal_tier">
                <span>信号分层 / 信号日</span>
                <strong>{{ selectedRow.signal_tier }} / {{ selectedRow.signal_trade_date || '--' }}</strong>
              </div>
              <div v-if="isFirstBoardSelected && selectedRow.auction_amount_yuan">
                <span>竞价额 / 昨日成交额</span>
                <strong>{{ amountText(selectedRow.auction_amount_yuan) }} / {{ percentOrDash(selectedRow.auction_amount_to_previous_pct) }}</strong>
              </div>
              <div v-if="isFirstBoardSelected && selectedRow.auction_amount_to_float_bps">
                <span>竞价额/流通市值 · 买卖盘比</span>
                <strong>{{ Number(selectedRow.auction_amount_to_float_bps).toFixed(2) }}bp / {{ selectedRow.auction_bid_ask_ratio ? Number(selectedRow.auction_bid_ask_ratio).toFixed(2) : '--' }}</strong>
              </div>
              <div v-if="isFirstBoardSelected && selectedRow.auction_sector_breadth_pct">
                <span>板块上涨宽度 / 平均涨幅</span>
                <strong>{{ percentOrDash(selectedRow.auction_sector_breadth_pct) }} / {{ percentOrDash(selectedRow.auction_sector_average_change_pct) }}</strong>
              </div>
              <div v-if="isFirstBoardSelected && selectedRow.lifecycle && selectedRow.lifecycle.outcome_ready">
                <span>结果 · {{ selectedRow.outcome_trade_date || '--' }}</span>
                <strong>{{ selectedRow.is_first_board ? '已封首板' : '炸板/未封' }} · 开板{{ selectedRow.open_times || 0 }}次</strong>
              </div>
              <div><span>竞价涨幅</span><strong>{{ percentOrDash(selectedRow.auction_change_pct) }}</strong></div>
              <div><span>较竞价</span><strong>{{ percentOrDash(selectedRow.current_vs_auction_pct) }}</strong></div>
              <div v-if="selectedRow.auction_state"><span>竞价状态机</span><strong>{{ selectedRow.auction_state_text }}</strong></div>
              <div v-if="selectedRow.market_cycle_state"><span>市场周期 / 仓位</span><strong>{{ selectedRow.market_cycle_state }} · {{ selectedRow.position_recommendation }}</strong></div>
              <div><span>行情源</span><strong>{{ selectedRow.quote_status === 'live' ? '实时' : '快照' }}</strong></div>
              <div v-if="isLiveActionPool(selectedRow) && selectedRow.intraday_action_score !== null && selectedRow.intraday_action_score !== undefined">
                <span>实时行动分 / 数据质量</span>
                <strong>{{ scoreText(selectedRow.intraday_action_score) }} / {{ intradayQualityText(selectedRow.intraday_action_data_quality) }}</strong>
              </div>
            </div>
            <section v-if="viewMode === 'research' && isStageModelSelected" class="stage-timeline-panel">
              <div class="stage-timeline-head">
                <strong>量价阶段时间轴</strong>
                <span>{{ stageTimelineLoading ? '计算中' : (stageTimeline.as_of_date || '') }}</span>
              </div>
              <p v-if="stageTimeline.error" class="stage-timeline-message">{{ stageTimeline.error }}</p>
              <p v-else-if="!stageTimeline.cycles || !stageTimeline.cycles.length" class="stage-timeline-message">
                {{ stageTimelineLoading ? '正在读取该标的日线并回放阶段' : '尚无可回放的完整阶段周期' }}
              </p>
              <div v-else class="stage-cycle-list">
                <article v-for="cycle in stageTimeline.cycles" :key="cycle.cycle_id" class="stage-cycle">
                  <div class="stage-cycle-heading">
                    <strong>周期 {{ cycle.cycle_id }}</strong>
                    <span>{{ cycle.start_date }} ~ {{ cycle.end_date }} · {{ cycle.status }}</span>
                  </div>
                  <div v-for="(phase, index) in cycle.phases" :key="`${cycle.cycle_id}-${index}`" class="stage-phase-row">
                    <strong>{{ phase.stage }}</strong>
                    <span>区间 {{ phase.interval_start || '--' }} ~ {{ phase.interval_end || '--' }}</span>
                    <span>确认 {{ phase.first_confirmed_date || '--' }}<template v-if="phase.last_confirmed_date && phase.last_confirmed_date !== phase.first_confirmed_date"> ~ {{ phase.last_confirmed_date }}</template></span>
                    <small>{{ phase.status || '状态未知' }} · 确认 {{ phase.confirmed_bars }} 日 / 推断 {{ phase.inferred_bars }} 日</small>
                  </div>
                </article>
              </div>
              <small v-if="stageTimeline.method_note" class="stage-timeline-note">{{ stageTimeline.method_note }}</small>
            </section>
            <div
              v-if="viewMode === 'research' && stageTrackingText(selectedRow)"
              class="selected-stage-state"
              :class="stageTrackingClass(selectedRow)"
            >
              <span>当前跟踪状态</span>
              <strong>{{ stageTrackingText(selectedRow) }}</strong>
              <small>正式阶段与评分仍以收盘日线确认；盘中状态仅用于跟踪。</small>
            </div>
            <div class="daily-position-panel">
              <div class="daily-position-head">
                <span>日线位置</span>
                <small>
                  {{ selectedDailyPositions.trade_date || '--' }}
                  · {{ selectedDailyPositions.daily_bar_incomplete ? '盘中合成日K' : (selectedDailyPositions.available ? '正式日线' : '暂无数据') }}
                </small>
              </div>
              <div class="daily-ma-grid">
                <div
                  v-for="item in dailyMaRows"
                  :key="item.key"
                  class="daily-ma-item"
                  :class="dailyMaClass(item.key)"
                >
                  <span>{{ item.label }}</span>
                  <strong>{{ priceText(selectedDailyPositions[item.key]) }}</strong>
                  <small>
                    {{ dailyMaStateText(item.key) }}
                  </small>
                </div>
              </div>
              <div class="daily-range">
                <div>
                  <span>日内最高</span>
                  <strong>{{ priceText(selectedDailyPositions.intraday_high) }}</strong>
                </div>
                <div>
                  <span>日内最低</span>
                  <strong>{{ priceText(selectedDailyPositions.intraday_low) }}</strong>
                </div>
                <small>
                  {{ selectedDailyPositions.intraday_source === 'tdx_realtime_quote' ? '实时行情' : '日线快照' }}
                </small>
              </div>
            </div>
            <div v-if="viewMode === 'research' || selectedMaCrossSignals.length" class="ma-cross-panel">
              <div class="ma-cross-panel-head">
                <span>均线信号状态</span>
                <small>MA13/17高于MA55 · {{ maReclaimRuleText }} · 收盘/现价站回均线</small>
              </div>
              <div v-if="selectedMaCrossSignals.length" class="ma-cross-detail-list">
                <div
                  v-for="signal in selectedMaCrossSignals"
                  :key="`${signal.label}-${signal.confirmation_type}`"
                  class="ma-cross-detail"
                  :class="maCrossClass(signal)"
                >
                  <strong>{{ signal.label }}</strong>
                  <span>{{ maCrossStateText(signal) }}</span>
                  <small>{{ maCrossDetailText(signal) }}</small>
                </div>
              </div>
              <div v-else class="ma-cross-empty">当前未触发金叉状态</div>
            </div>
            <div v-if="selectedRow.pool_id === 'bear_overnight'" class="ma-cross-panel overnight-checks">
              <div class="ma-cross-panel-head"><span>一夜持股条件核对</span><small>入池时状态 · 完整分时需人工复核</small></div>
              <p v-if="selectedRow.overnight_detail">总市值 {{ (Number(selectedRow.overnight_detail.market_cap_yuan || 0) / 100000000).toFixed(1) }} 亿 · 换手 {{ Number(selectedRow.overnight_detail.turnover_rate_pct || 0).toFixed(2) }}% · 量比 {{ Number(selectedRow.overnight_detail.volume_ratio || 0).toFixed(2) }}</p>
              <p>入选 {{ selectedRow.selected_at || (selectedRow.overnight_detail || {}).source_quote_at || '--' }} · {{ priceText(selectedRow.selected_price) }} 元 · {{ selectedRow.selected_change_pct == null ? '--' : signedPercent(selectedRow.selected_change_pct) }}；当前 {{ priceText(selectedRow.price) }} 元 · {{ signedPercent(selectedRow.change_pct) }}</p>
              <p v-if="(selectedRow.overnight_detail || {}).channel_bottom">{{ selectedRow.overnight_detail.channel_bottom.trade_date }} {{ selectedRow.overnight_detail.channel_bottom.model_label || '通道' }}位置 {{ percentOrDash(selectedRow.overnight_detail.channel_bottom.position_pct) }} · {{ selectedRow.overnight_detail.channel_bottom.available ? (selectedRow.overnight_detail.channel_bottom.matched ? '底部' : '非底部') : '数据不足' }}（供参考）</p>
              <p>结构：{{ (selectedRow.overnight_detail || {}).confirmation_type || '尚未确认' }} · 焦点分 {{ overnightMetric(selectedRow.overnight_detail, 'focus_score') }} / 100 · 均价偏离 {{ overnightMetric(selectedRow.overnight_detail, 'vwap_deviation_pct') }}%</p>
              <p>分时评分 {{ overnightMetric(selectedRow.overnight_detail, 'intraday_score') }} / 100 · {{ (selectedRow.overnight_detail || {}).score_complete ? '数据完整' : '数据待补，缺失项不加分' }}</p>
              <p>均价上方时间 {{ overnightMetric(selectedRow.overnight_detail, 'above_vwap_time_pct') }}% · 超额涨幅 {{ overnightMetric(selectedRow.overnight_detail, 'excess_change_pct') }} 个百分点</p>
              <p>最长跌破 {{ overnightMetric(selectedRow.overnight_detail, 'max_below_vwap_minutes') }} 分钟 · 最大深度 {{ overnightMetric(selectedRow.overnight_detail, 'max_below_vwap_depth_pct') }}% · 分时覆盖 {{ overnightMetric(selectedRow.overnight_detail, 'sample_coverage_pct') }}%</p>
              <p>尾盘触发：{{ (selectedRow.overnight_detail || {}).entry_trigger || '等待放量突破或回踩企稳' }}</p>
              <p v-if="(selectedRow.overnight_detail || {}).afternoon">午后覆盖 {{ overnightMetric(selectedRow.overnight_detail.afternoon, 'sample_coverage_pct') }}% · 均价上方 {{ overnightMetric(selectedRow.overnight_detail.afternoon, 'above_vwap_time_pct') }}% · {{ selectedRow.overnight_detail.confirmed_entry_trigger || '尾盘触发待确认' }}</p>
              <template v-if="(selectedRow.overnight_detail || {}).evidence">
                <p>近30分钟主力净额变化 {{ overnightFlowText(selectedRow.overnight_detail.evidence.funds.delta_30m_yuan) }} · 同期净流强度 {{ overnightMetric(selectedRow.overnight_detail.evidence.funds, 'intensity_30m_pct') }}% · 正流入窗口 {{ selectedRow.overnight_detail.evidence.funds.positive_windows }} / {{ overnightSettings.funds_total_windows }}</p>
                <p>资金 {{ selectedRow.overnight_detail.evidence.funds.complete ? '窗口完整' : '待补' }} · L2 {{ selectedRow.overnight_detail.evidence.l2.confirmed ? '观测承接通过' : '未确认 / 待补' }}；观测承接不代表完整 L2。</p>
              </template>
              <p v-if="(selectedRow.overnight_detail || {}).focus_unmet && selectedRow.overnight_detail.focus_unmet.length">焦点待满足：{{ selectedRow.overnight_detail.focus_unmet.join('、') }}</p>
              <div v-for="(points, label) in (selectedRow.overnight_detail || {}).score_components" :key="`score-${label}`" class="overnight-check"><span>{{ label }}</span><strong>{{ Number(points).toFixed(1) }} 分</strong></div>
              <div v-for="(passed, label) in (selectedRow.overnight_detail || {}).checks" :key="label" class="overnight-check"><span>{{ label }}</span><strong>{{ passed === true ? '通过' : (passed === false ? '未通过 / 等待' : '待核对') }}</strong></div>
              <p>下一交易日早盘冲高兑现；未走出预期按计划退出。</p>
            </div>

            <div v-if="selectedRow.pool_id === 'macd_bear_rebound' && selectedRow.macd_bear_rebound_detail" class="ma-cross-panel">
              <h4>空头反抽 · 峰后首次缩柱</h4>
              <p>绿柱段开始 {{ selectedRow.macd_bear_rebound_detail.green_wave_start_date || '--' }} · 峰值日 {{ selectedRow.macd_bear_rebound_detail.green_wave_peak_date || '--' }} · 峰值柱 {{ macdHistogramText(selectedRow.macd_bear_rebound_detail.green_wave_peak_hist) }}</p>
              <p>昨日柱值 {{ macdHistogramText(selectedRow.macd_bear_rebound_detail.previous_hist) }} → 本轮柱值 {{ macdHistogramText(selectedRow.macd_bear_rebound_detail.current_hist) }} · 缩短 {{ Number(selectedRow.macd_bear_rebound_detail.shrink_pct || 0).toFixed(2) }}%</p>
              <p>昨日 DIF / DEA {{ macdHistogramText(selectedRow.macd_bear_rebound_detail.previous_dif) }} / {{ macdHistogramText(selectedRow.macd_bear_rebound_detail.previous_dea) }} · 本轮 {{ macdHistogramText(selectedRow.macd_bear_rebound_detail.current_dif) }} / {{ macdHistogramText(selectedRow.macd_bear_rebound_detail.current_dea) }}</p>
              <p>今日已成交 {{ volumeHandText(selectedRow.macd_bear_rebound_detail.current_volume_hand) }} · 昨日全天 {{ volumeHandText(selectedRow.macd_bear_rebound_detail.previous_volume_hand) }} · {{ macdVolumeRatioText(selectedRow.macd_bear_rebound_detail.volume_ratio_to_previous) }} 倍</p>
              <p>上一交易日 {{ selectedRow.macd_bear_rebound_detail.previous_trade_date || '--' }} · 昨收 {{ priceText(selectedRow.macd_bear_rebound_detail.previous_close) }} · 筛选时价格 {{ priceText(selectedRow.selected_price) }}</p>
              <p>开盘 {{ priceText(selectedRow.macd_bear_rebound_detail.open_price) }} · 筛选时涨幅 {{ selectedRow.selected_change_pct == null ? '--' : signedPercent(selectedRow.selected_change_pct) }} · {{ selectedRow.macd_bear_rebound_detail.bullish_candle === true ? '上涨阳线' : (selectedRow.macd_bear_rebound_detail.bullish_candle === false ? '收阴 / 非阳线' : '阳线待核对') }}</p>
              <p>筛选行情 {{ selectedRow.macd_bear_rebound_detail.source_quote_at || '--' }} · 盘中日线 MACD，尚未收盘确认</p>
              <div v-for="(passed, label) in selectedRow.macd_bear_rebound_detail.checks" :key="`macd-${label}`" class="overnight-check"><span>{{ label }}</span><strong>{{ passed === true ? '通过' : '待核对' }}</strong></div>
            </div>
            <div v-if="selectedRow.pool_id === 'limit_pullback_repair' && selectedRow.limit_pullback_repair_detail" class="ma-cross-panel">
              <p><strong>{{ repairStageLabel(selectedRow) }}</strong> · {{ repairDetail.structure_only ? '仅结构预筛，不是买入信号' : '回踩观察；确认以快照生成时点为准' }}</p>
              <p v-for="reason in repairDetail.rejection_reasons || []" :key="`repair-reject-${reason}`">未进入回踩观察：{{ reason }}</p>
              <h4>连板回踩修复 · {{ selectedRow.stage }}</h4>
              <p>{{ repairDetail.streak_start_date }}–{{ repairDetail.streak_end_date }} · {{ repairDetail.strength_label }} · 结束后第{{ repairDetail.sessions_after_streak }}个交易日</p>
              <p>支撑 {{ repairDetail.support_kind }} · 首次回踩 {{ (repairDetail.touch || {}).trade_date || '--' }} · 回踩价 {{ priceText((repairDetail.touch || {}).low) }} · 当时动态均线 {{ priceText((repairDetail.touch || {}).reference_ma) }} · 偏离 {{ signedPercent((repairDetail.touch || {}).gap_pct) }}</p>
              <p>动态MA10 {{ priceText(repairDetail.dynamic_ma10) }} / MA20 {{ priceText(repairDetail.dynamic_ma20) }} · 距MA10 {{ signedPercent(repairDetail.ma10_gap_pct) }} · 成交均价 {{ priceText((repairDetail.intraday || {}).vwap) }} · MA10{{ repairDetail.ma10_rising ? '抬升' : '未抬升' }}</p>
              <p v-if="(repairDetail.floor || {}).label">{{ repairDetail.floor.label }} · 高风险观察，需等待支撑修复</p>
              <p v-for="event in (repairDetail.floor || {}).events || []" :key="event.trade_date">{{ event.trade_date }} · {{ event.label }} · 跌停价 {{ priceText(event.limit_down_price) }} · 打开时刻 {{ event.opened_at || '证据待补' }} · {{ event.closed ? '已收盘' : '盘中未定' }}</p>
              <p>{{ (repairDetail.candle || {}).label || 'K线形态待补' }} · 量能 {{ (repairDetail.candle || {}).volume_quality === 'available' ? '可核验' : '待补' }}<span v-if="(repairDetail.candle || {}).unresolved"> · 大阴风险未修复</span></p>
              <p v-if="!repairDetail.structure_only">初步止跌 {{ repairDetail.first_stop_at || '--' }} · 反弹 {{ Number(repairDetail.rebound_from_low_pct || 0).toFixed(2) }}% · {{ repairDetail.signal_confirmed ? '首次承接确认' : '仅观察，等待承接采样' }} · {{ (repairDetail.acceptance || {}).reason }}</p>
              <p>主要比较题材 {{ (repairDetail.peers || {}).primary_theme || '覆盖待补' }} · {{ (repairDetail.peers || {}).priority }} · {{ (repairDetail.peers || {}).reason }} · 历史一字 {{ repairDetail.one_word_count || 0 }} 次</p>
              <p v-if="repairDetail.risk_plan">退出参考：成本下方{{ repairDetail.risk_plan.hard_stop_pct }}%或首次回踩低点，完整分钟破MA{{ repairDetail.risk_plan.stop_ma_period }}；第{{ repairDetail.risk_plan.soft_days }}日未见预期反弹退出、最长{{ repairDetail.risk_plan.max_days }}日。真实成交及可卖数量以模拟盘为准。</p>
              <p v-if="(repairDetail.peers || {}).best">{{ repairDetail.peers.best.group }} · 同期强势样本第 {{ repairDetail.peers.best.rank }} / {{ repairDetail.peers.best.count }} · 本地覆盖样本，非全市场龙头认定</p>
              <p>承接采样 {{ (repairDetail.acceptance || {}).sample_minutes || 0 }} / 5分钟 · {{ (repairDetail.acceptance || {}).reason || '等待采样' }}</p>
              <p>辅助参考（不作准入）：站上MA10及均价 {{ (repairDetail.intraday || {}).above_minutes || 0 }} 分钟 · 此前20分钟采样高点 {{ priceText((repairDetail.intraday || {}).breakout_price) }} · 覆盖 {{ (repairDetail.intraday || {}).breakout_coverage_pct || 0 }}%</p>
              <p>{{ (repairDetail.chips || {}).label || '筹码待补' }} · 日期 {{ (repairDetail.chips || {}).trade_date || '--' }} · 来源 {{ (repairDetail.chips || {}).source || '--' }}</p>
              <p>筹码参考峰价 {{ priceText((repairDetail.chips || {}).peak_price) }} · {{ (repairDetail.chips || {}).peak_near_support ? '靠近回踩支撑' : '支撑附近筹码待核对' }}<span v-if="(repairDetail.chips || {}).converging"> · 集中度收敛</span><span v-if="(repairDetail.chips || {}).peak_descending"> · 峰价下移</span></p>
              <p v-if="(repairDetail.chips || {}).quality === 'distribution'">主峰±5%筹码占比 {{ Number(repairDetail.chips.peak_mass_pct || 0).toFixed(1) }}% · 上方筹码 {{ Number(repairDetail.chips.overhead_mass_pct || 0).toFixed(1) }}% · 显著峰数 {{ repairDetail.chips.significant_peak_count }}</p>
              <div v-for="(passed, label) in repairDetail.checks" :key="`repair-${label}`" class="overnight-check"><span>{{ label }}</span><strong>{{ passed === true ? '通过' : '未通过 / 等待' }}</strong></div>
              <p>筛选行情 {{ repairDetail.source_quote_at || '--' }} · 筹码按上一完整交易日核验；分时为快照采样，非逐笔确认。</p>
            </div>
            <div v-if="selectedRow.reasons && selectedRow.reasons.length" class="reason-list">
              <span v-for="reason in selectedRow.reasons.slice(0, 3)" :key="reason">{{ reason }}</span>
            </div>
            <div
              v-if="selectedPlatformLevels.available"
              class="platform-levels"
              :class="platformToneClass(selectedPlatformLevels)"
            >
              <div class="platform-levels-head">
                <span>{{ platformTitle(selectedPlatformLevels) }}</span>
                <small>{{ selectedPlatformLevels.source }} · {{ selectedPlatformLevels.start_date || '--' }} ~ {{ selectedPlatformLevels.end_date || '--' }}</small>
              </div>
              <div class="platform-state-line">
                <strong>{{ selectedPlatformLevels.state }}</strong>
                <span>{{ platformDistanceText(selectedPlatformLevels) }}</span>
              </div>
              <div class="platform-track" aria-hidden="true">
                <span class="platform-track-fill"></span>
                <i class="platform-track-marker" :style="selectedPlatformMarkerStyle"></i>
              </div>
              <div class="platform-level-grid">
                <div class="platform-level defense-level">
                  <div class="platform-level-label">{{ platformDefenseLabel(selectedPlatformLevels) }}</div>
                  <strong>{{ priceText(selectedPlatformLevels.defense_price) }}</strong>
                  <small>防守 {{ signedPercent(selectedPlatformLevels.distance_to_defense_pct) }}</small>
                </div>
                <div class="platform-level current-level">
                  <div class="platform-level-label">当前</div>
                  <strong>{{ priceText(selectedRealtimePrice) }}</strong>
                  <small>位置 {{ platformPositionText(selectedPlatformLevels) }}</small>
                </div>
                <div class="platform-level attack-level">
                  <div class="platform-level-label">{{ platformAttackLabel(selectedPlatformLevels) }}</div>
                  <strong>{{ priceText(selectedPlatformLevels.attack_price) }}</strong>
                  <small>{{ platformDistanceText(selectedPlatformLevels) }}</small>
                </div>
              </div>
              <div v-if="selectedPlatformLevels.box_kind === 'n_breakout'" class="platform-structure-metrics">
                <span>启动L1 <strong>{{ priceText(selectedPlatformLevels.origin_price) }}</strong></span>
                <span>第一波 <strong>{{ signedPercent(selectedPlatformLevels.impulse_pct) }}</strong></span>
                <span>回踩 <strong>{{ Number(selectedPlatformLevels.retracement_pct || 0).toFixed(1) }}%</strong></span>
                <span>缩量比 <strong>{{ Number(selectedPlatformLevels.pullback_volume_ratio || 0).toFixed(2) }}</strong></span>
              </div>
              <div class="platform-rules">
                <span>{{ selectedPlatformLevels.attack_rule }}</span>
                <span>{{ selectedPlatformLevels.defense_rule }}</span>
                <span v-if="Number(selectedPlatformLevels.breakout_volume_ratio) > 0">突破量比 {{ Number(selectedPlatformLevels.breakout_volume_ratio).toFixed(2) }}</span>
              </div>
            </div>
            <div v-if="selectedRow.risks && selectedRow.risks.length" class="risk-line">风险：{{ selectedRow.risks.join('；') }}</div>
          </div>
          <div v-else class="placeholder-detail">
            <div class="placeholder-mark">↗</div>
            <h3>选择一个标的</h3>
            <p>点击上方列表查看完整信号、竞价和当前价格对比。</p>
          </div>
        </article>

        <article v-if="marketPanelOpen" class="insight-card pulse-card">
          <div class="card-topline">
            <span class="eyebrow">MARKET PULSE</span>
            <div class="detail-top-actions"><span class="pulse-time">{{ lastUpdatedText }}</span><button class="detail-close" type="button" aria-label="关闭盘面情报" @click="closeDetailPanel">×</button></div>
          </div>
          <div class="pulse-title">
            <h3>盘面脉搏</h3>
            <span class="pulse-badge">{{ positiveRatio }}% ↑</span>
          </div>
          <div class="bar-list">
            <div v-for="item in pulseRows" :key="item.label" class="bar-item">
              <div class="bar-label"><span>{{ item.label }}</span><strong>{{ item.count }}</strong></div>
              <div class="bar-track"><span :style="{ width: item.percent + '%' }" :class="item.tone"></span></div>
            </div>
          </div>
          <section class="auction-board-panel" aria-label="竞价强势板块">
            <div class="auction-board-head">
              <div><strong>竞价强势板块</strong><small>{{ auctionBoardStrength.trade_date || '无业务日期' }} · {{ auctionBoardStrength.stale ? '历史参考' : '当日数据' }}</small></div>
              <span :class="{ stale: auctionBoardStrength.stale }">{{ auctionBoardStrength.status || 'unavailable' }}</span>
            </div>
            <div v-if="auctionBoards.length" class="auction-board-list">
              <div v-for="board in auctionBoards.slice(0, 8)" :key="`${board.board_type}-${board.name}`">
                <span>{{ board.board_type === 'industry' ? '行业' : '概念' }} · {{ board.name }}</span>
                <strong>{{ Number(board.weighted_auction_pct || 0) >= 0 ? '+' : '' }}{{ Number(board.weighted_auction_pct || 0).toFixed(2) }}%</strong>
                <small>上涨{{ board.up_ratio_pct }}% · 强势{{ board.strong_count }} · 涨停{{ board.limit_up_count }} · 竞价额{{ supportMoneyText(board.auction_amount_yuan) }}</small>
              </div>
            </div>
            <small v-else class="fqgate-empty">{{ auctionBoardStrength.message || '暂无可用竞价板块数据' }}</small>
            <p>{{ auctionBoardStrength.l2_note || '竞价成交额不等于L2大单；真实大单待L2确认。' }}</p>
          </section>
          <section class="dashboard-alert-panel" aria-label="综合强势提醒">
            <div class="auction-board-head"><div><strong>综合强势提醒 · A级</strong><small>涨速达标 · 全天箱体未破 · 近期回踩收复 · 共振确认；资金作辅助评分</small></div><span>{{ highConfidenceAlerts.length }}</span></div>
            <button v-for="alert in highConfidenceAlerts" :key="alert.id" type="button" @click="openAlertStock(alert)">
              <span>{{ alert.name || alert.code }} · {{ alert.code }}</span><strong>{{ Number(alert.score || 0).toFixed(1) }}分</strong>
              <small>{{ (alert.reasons || []).join(' · ') }} · {{ alert.claim }}</small>
            </button>
            <small v-if="!highConfidenceAlerts.length" class="fqgate-empty">当前没有达到A级条件的提醒</small>
          </section>
          <section class="dashboard-alert-panel observation-panel" aria-label="动量修复观察">
            <div class="auction-board-head"><div><strong>动量 / 修复观察 · B级</strong><small>涨速达标、部分结构待确认 · 仅页面观察，静音、不推系统通知</small></div><span>{{ momentumObservations.length }}</span></div>
            <button v-for="alert in momentumObservations" :key="`observation-${alert.id}`" type="button" @click="openAlertStock(alert)">
              <span>{{ alert.name || alert.code }} · {{ alert.code }}</span><strong>观察</strong>
              <small>{{ (alert.reasons || []).join(' · ') }}</small>
              <small>升A级待补：{{ (alert.a_missing_labels || []).join(' · ') }}</small>
            </button>
            <small v-if="!momentumObservations.length" class="fqgate-empty">暂无满足持续资金条件的动量观察</small>
            <p v-if="alertDiagnostics.message" class="alert-evidence-note">{{ alertDiagnostics.message }}</p>
            <small v-if="alertDiagnostics.candidate_count !== undefined" class="fqgate-empty">候选 {{ alertDiagnostics.candidate_count }} · 涨速通过 {{ alertDiagnostics.speed_pass_count || 0 }} · 连续资金通过 {{ alertDiagnostics.continuous_flow_count || 0 }} · A {{ alertDiagnostics.a_count || 0 }} / B {{ alertDiagnostics.b_count || 0 }}</small>
            <p v-if="alertDiagnostics.storage_error || alertDiagnostics.record_error" class="alert-evidence-error">{{ alertDiagnostics.storage_error || alertDiagnostics.record_error }}</p>
          </section>
          <section class="fqgate-radar" aria-label="市场情报">
            <div class="fqgate-radar-head">
              <div>
                <span>MARKET INTELLIGENCE</span>
                <small>{{ fqgateStatusText }}</small>
                <small>{{ fqgateAlertStatusText }}</small>
                <small>{{ publicFundsStatusText }}</small>
                <small v-if="l2CollectionStatusText">{{ l2CollectionStatusText }}</small>
              </div>
              <strong>{{ fqgateOverviewTimeText }}</strong>
            </div>
            <div class="fqgate-diagnosis" :class="marketDiagnosis.tone">
              <div>
                <strong>{{ marketDiagnosis.title }}</strong>
                <span>周期分 {{ marketDiagnosis.score }}</span>
              </div>
              <p>{{ marketDiagnosis.summary }}</p>
              <small>{{ marketDiagnosis.action }}</small>
            </div>
            <div class="fqgate-overview-grid">
              <div v-for="item in fqgateOverviewRows" :key="item.key">
                <span>{{ item.label }}</span><strong>{{ item.value }}</strong>
              </div>
            </div>
            <div class="dat-intelligence">
              <div class="dat-intelligence-head">
                <strong>DAT 持久化情报</strong>
                <small>{{ datInsightsStatusText }}</small>
              </div>
              <div class="dat-module-strip">
                <span v-for="item in datModuleCards" :key="item.key" :class="`dat-${item.freshness}`">{{ item.label }} <strong>{{ item.count }}</strong></span>
              </div>
              <div v-if="datInsightRows.length" class="fqgate-alert-list" :class="{ 'stale-market-data': !datInsightsFresh }">
                <div v-for="item in datInsightRows.slice(0, 6)" :key="item.id" class="fqgate-alert-row dat-event-row">
                  <span>{{ item.occurred_at ? alertTime(item.occurred_at) : '时间未知' }}</span>
                  <strong>{{ item.name || item.code || item.kind }} · {{ item.title }} <i>{{ item.source }}</i></strong>
                </div>
              </div>
              <small v-else class="fqgate-empty">暂无已保存 DAT 雷达、异动或机会</small>
              <details class="dat-catalog">
                <summary>DAT API 目录 · {{ datCatalog.entries.length }}项 · 仅命名资源可查询</summary>
                <input v-model.trim="datCatalogSearch" type="search" placeholder="筛选接口名称、路径或来源">
                <div class="dat-catalog-list">
                  <span v-for="item in filteredDatCatalog.slice(0, 30)" :key="item.id"><i>{{ item.source }}</i><strong>{{ item.label }}</strong><code>{{ item.path }}</code></span>
                </div>
              </details>
            </div>
            <div v-if="fqgateAlerts.length" class="fqgate-alert-list">
              <div v-for="(alert, index) in fqgateAlerts.slice(0, 5)" :key="`${alert.received_at}-${index}`" class="fqgate-alert-row">
                <span>{{ alertTime(alert.received_at) }}</span>
                <strong>{{ alert.source || 'fqgate' }} · {{ alertText(alert.event) }}</strong>
              </div>
            </div>
            <small v-else class="fqgate-empty">{{ fqgateAlertEmptyText }}</small>
            <details v-if="Object.keys(fqgateOverview.errors || {}).length" class="fqgate-diagnostics">
              <summary>排行接口异常详情</summary>
              <p v-for="(message, key) in fqgateOverview.errors" :key="key">{{ key }}：{{ message }}</p>
            </details>
          </section>
          <p class="pulse-note">股票条目显示所属股池、筛选阶段与确认结果；全部模型按股票去重。</p>
        </article>
        </section>
      </section>

      <footer class="dashboard-footer">
        <span>POOLDESK / 本地项目股池观察台</span>
        <span>快照 {{ snapshotUpdatedText }} · 行情 {{ quoteUpdatedText }}</span>
      </footer>
    </main>
  </div>
</template>

<script>
const API_BASE = (process.env.VUE_APP_BASE_API || '').replace(/\/$/, '')
const STAGE_MODEL_ROW_LABELS = [
  '吸筹候选',
  '试盘突破',
  '洗盘候选',
  '出货风险',
  '操',
  '通道底部',
  '短周期跟踪',
  '长周期观察池',
  '优先关注',
  '未出通道',
  '靠近主成本区',
  '筹码持续收敛',
  '筹码重新发散',
  '收敛但价格偏离',
  '筹码峰下移',
  'T+5观察条件',
  '拉升跟踪',
  '风险回避'
]

export default {
  name: 'App',
  data () {
    return {
      dashboard: { pools: [] },
      thsTradePanelOpen: false,
      styleAlpha: { style: {}, breadth: {}, risk: {}, holdings: [], data_quality: {} },
      styleAlphaLoading: false,
      styleAlphaError: '',
      loaded: false,
      viewMode: 'trade',
      // 看板暂时只保留交易与模型研究；因子研究服务保留，方便后续恢复。
      factorResearchEnabled: false,
      activePoolId: 'all',
      searchText: '',
      selectedBoardFilter: '',
      selectedStageModelLabel: '',
      selectedRepairStage: '',
      positiveOnly: false,
      hideLimitUp: true,
      selectedRow: null,
      detailPanelOpen: false,
      marketPanelOpen: false,
      filtersExpanded: false,
      selectedOpportunityBoard: '',
      fqgateMarket: { status: 'idle', boards: [], memberships: {}, quotes: {}, error: '' },
      fqgateRequestId: 0,
      fqgateStatus: { status: 'idle', configured: false, stream_kinds: [] },
      fqgateOverview: { status: 'idle', feeds: {}, errors: {}, coverage: {}, updated_at: '' },
      fqgateAlerts: [],
      fqgateAlertSocket: null,
      fqgateAlertReconnectTimer: null,
      fqgateAlertState: 'idle',
      fqgateAlertError: '',
      datStatus: { status: 'idle', source: 'unknown' },
      datPoints: { status: 'idle', paid_available: false, daily_limit: 5 },
      datPointsLoading: false,
      l2PaidPending: false,
      l2PaidSamples: {},
      l2SnapshotTimer: null,
      l2SnapshotRequestId: 0,
      l2LegacyRoute: false,
      datInsights: { status: 'idle', modules: {}, events: [], opportunities: [] },
      datCatalog: { entries: [], resources: [] },
      datCatalogSearch: '',
      stageTimeline: { cycles: [] },
      stageTimelineLoading: false,
      stageTimelineRequestId: 0,
      sortKey: 'score',
      sortDirection: 'desc',
      isMobileViewport: false,
      visibleRowLimit: 60,
      refreshSeconds: 15,
      loading: false,
      dashboardRequestInFlight: false,
      error: '',
      refreshTimer: null,
      dashboardPollTimer: null,
      dashboardAbortController: null,
      l2Socket: null,
      l2ReconnectTimer: null,
      l2Watch: {
        state: 'idle',
        factor: {},
        quote: {},
        streams: {},
        events: [],
        error: ''
      },
      factorCatalog: [],
      factorRows: [],
      factorControls: {},
      factorLoading: false,
      factorBacktestLoading: false,
      factorResearchError: '',
      factorBacktestError: '',
      factorBacktest: { status: 'idle', metrics: {}, warnings: [] },
      factorMinScore: 0,
      factorPositiveOnly: false,
      factorBacktestStart: '',
      factorBacktestEnd: '',
      factorHoldingDays: 3,
      prominentAlert: null,
      newAlertBatchCount: 0,
      alertBannerTimer: null,
      alertSoundEnabled: false,
      notificationPermission: 'default',
      originalDocumentTitle: ''
    }
  },
  computed: {
    styleAlphaHoldings () {
      return Array.isArray(this.styleAlpha.holdings) ? this.styleAlpha.holdings : []
    },
    styleAlphaIntraday () {
      return this.styleAlpha.intraday || { status: 'unavailable', coverage: {}, alerts: [], message: '等待盘中估算数据' }
    },
    styleAlphaIntradayMetrics () {
      const intraday = this.styleAlphaIntraday
      const style = intraday.style || {}
      const risk = intraday.risk || {}
      const breadth = intraday.breadth || {}
      const percent = value => value == null ? '--' : `${value >= 0 ? '+' : ''}${(value * 100).toFixed(1)}%`
      return `20日相对 ${percent(style.r20)} · 60日相对 ${percent(style.r60)} · 创业板波动 ${percent(risk.vol20)} · 20日回撤 ${percent(risk.mdd20)} · 市场宽度 ${breadth.score == null ? '--' : `${breadth.score}/${breadth.max}`}`
    },
    styleAlphaIntradayHoldingsCoverage () {
      const coverage = this.styleAlphaIntraday.coverage || {}
      return `${coverage.holdings_available || 0}/${coverage.holdings_total || 0}`
    },
    styleAlphaReadyHoldings () {
      return this.styleAlphaHoldings.filter(item => item.status === 'ready').length
    },
    styleAlphaIssues () {
      const quality = this.styleAlpha.data_quality || {}
      return [...(quality.missing || []), ...(quality.short_history || []), ...(quality.stale || []), ...(this.styleAlphaError ? [this.styleAlphaError] : [])]
    },
    styleAlphaStateText () {
      const labels = { CONFIRM: '成长确认', OBSERVE: '观察', DEFEND: '防守', DATA_INCOMPLETE: '数据待补', DATA_UNAVAILABLE: '数据不可用' }
      return labels[this.styleAlpha.state] || (this.styleAlphaLoading ? '读取中' : '等待数据')
    },
    styleAlphaMomentumText () {
      const style = this.styleAlpha.style || {}
      if (style.r20 == null) return '创业板 / 红利序列尚未齐备'
      return `20日 ${this.styleAlphaPercent(style.r20)} · 60日 ${style.r60 == null ? '--' : this.styleAlphaPercent(style.r60)}`
    },
    styleAlphaHoldingText () {
      if (!this.styleAlphaHoldings.length) return '等待导入真实持仓清单'
      const weak = this.styleAlphaHoldings.filter(item => item.status === 'ready' && item.above_alpha_ma20 === false).length
      return weak ? `${weak} 只跌破 Alpha 均线` : '暂无 Alpha 弱化持仓'
    },
    styleAlphaRiskText () {
      const risk = this.styleAlpha.risk || {}
      if (risk.vol20 == null) return '待计算'
      return risk.vol20 > 0.3 || risk.atr_z > 1 || risk.mdd20 < -0.1 ? '警戒' : '正常'
    },
    styleAlphaRiskDetail () {
      const risk = this.styleAlpha.risk || {}
      return `波动 ${risk.vol20 == null ? '--' : this.styleAlphaPercent(risk.vol20)} · 20日回撤 ${risk.mdd20 == null ? '--' : this.styleAlphaPercent(risk.mdd20)}`
    },
    styleAlphaQualityText () {
      const quality = this.styleAlpha.data_quality || {}
      return quality.status === 'ready' ? '数据齐备 · 日线只读' : `${this.styleAlphaIssues.length} 项待补 · 日线只读`
    },
    sidePanelOpen () {
      return this.detailPanelOpen || this.marketPanelOpen
    },
    rawPools () {
      return Array.isArray(this.dashboard.pools) ? this.dashboard.pools : []
    },
    workPools () {
      return Array.isArray(this.dashboard.work_pools) ? this.dashboard.work_pools : []
    },
    tradePools () {
      return this.workPools
    },
    factorPool () {
      return {
        id: 'factor_current',
        label: '因子组合结果',
        short_label: '组合结果',
        description: '按当前因子组合评分排序，不改变交易池',
        total_count: this.factorRows.length,
        snapshot_at: this.dashboard.updated_at || '',
        rows: this.factorRows
      }
    },
    pools () {
      if (this.viewMode === 'trade') return this.tradePools
      if (this.viewMode === 'factor') return [this.factorPool]
      return this.rawPools
    },
    visiblePools () {
      const statusOnlyPools = new Set(['stage_model', 'bullish_oversold', 'yaogu_sniffer', 'ma_cross_channel'])
      return this.pools.filter(pool => this.poolDisplayCount(pool) > 0 || statusOnlyPools.has(pool.id))
    },
    strategyCalibration () {
      return this.dashboard.strategy_calibration && typeof this.dashboard.strategy_calibration === 'object'
        ? this.dashboard.strategy_calibration
        : {}
    },
    mainlineBoards () {
      return this.opportunityBoards.filter(board => board.state === '资金扩散确认' || board.direction === 'inflow').slice(0, 3)
    },
    boardFundsObservations () {
      const original = ((this.opportunityRadar.board_flow || {}).observation_rows || []).slice(0, 80)
      const snapshots = new Map()
      for (const pool of [...(this.dashboard.pools || []), ...(this.dashboard.work_pools || [])]) {
        for (const row of pool.rows || []) {
          const flow = this.kaipanlaSnapshot(row)
          for (const board of (flow && flow.board_snapshots) || []) {
            const previous = snapshots.get(board.code)
            if (!previous || board.captured_at > previous.captured_at) snapshots.set(board.code, board)
          }
        }
      }
      return [...original, ...Array.from(snapshots.values()).slice(0, 40).map(board => ({ ...board,
        board_type: 'concept', confirmation_eligible: false, direction: board.main_net_inflow_yuan > 0 ? 'inflow' : 'outflow',
        stale: this.kaipanlaSnapshotStale(board) }))]
    },
    mainlineBoardText () {
      const names = this.mainlineBoards.map(board => board.board_name).filter(Boolean)
      return names.length ? names.join(' · ') : '资金扩散尚未确认'
    },
    cyclePositionText () {
      const policy = this.currentMarketCycle.risk_policy || {}
      if (!Object.keys(policy).length) return '仓位等待周期确认'
      return `${policy.position_recommendation || '观察'} · 上限${Number(policy.max_position_pct || 0).toFixed(0)}%`
    },
    coreActionCount () {
      const pool = this.workPools.find(item => item.id === 'core_action')
      return Number((pool && pool.total_count) || 0)
    },
    riskReviewCount () {
      const pool = this.workPools.find(item => item.id === 'risk_review')
      return Number((pool && pool.total_count) || 0)
    },
    calibrationSamples () {
      return Number(this.strategyCalibration.samples || 0)
    },
    calibrationWinRate () {
      return Number((this.strategyCalibration.t3 || {}).win_rate_pct || 0).toFixed(1)
    },
    calibrationAverageReturn () {
      return Number((this.strategyCalibration.t3 || {}).average_return_pct || 0)
    },
    opportunityRadar () {
      return this.dashboard.opportunity_radar && typeof this.dashboard.opportunity_radar === 'object'
        ? this.dashboard.opportunity_radar
        : {}
    },
    opportunityMarket () {
      return this.opportunityRadar.market && typeof this.opportunityRadar.market === 'object'
        ? this.opportunityRadar.market
        : {}
    },
    opportunityCycle () {
      return this.opportunityMarket.cycle && typeof this.opportunityMarket.cycle === 'object'
        ? this.opportunityMarket.cycle
        : {}
    },
    currentMarketCycle () {
      return this.fqgateOverview.data_fresh && this.fqgateOverview.market_cycle && typeof this.fqgateOverview.market_cycle === 'object'
        ? this.fqgateOverview.market_cycle
        : this.opportunityCycle
    },
    opportunitySummary () {
      return this.opportunityRadar.summary && typeof this.opportunityRadar.summary === 'object'
        ? this.opportunityRadar.summary
        : {}
    },
    opportunityBoards () {
      return Array.isArray(this.opportunityRadar.boards) ? this.opportunityRadar.boards : []
    },
    auctionBoardStrength () {
      return this.dashboard.auction_board_strength && typeof this.dashboard.auction_board_strength === 'object'
        ? this.dashboard.auction_board_strength
        : { status: 'unavailable', stale: true, boards: [] }
    },
    auctionBoards () {
      return Array.isArray(this.auctionBoardStrength.boards) ? this.auctionBoardStrength.boards : []
    },
    highConfidenceAlerts () {
      return Array.isArray(this.dashboard.high_confidence_alerts)
        ? this.dashboard.high_confidence_alerts.filter(this.isNotifyingAlert) : []
    },
    momentumObservations () {
      return Array.isArray(this.dashboard.momentum_observations) ? this.dashboard.momentum_observations : []
    },
    alertDiagnostics () {
      return this.dashboard.alert_diagnostics || {}
    },
    opportunityStocks () {
      const stocks = Array.isArray(this.opportunityRadar.stocks) ? this.opportunityRadar.stocks : []
      if (!this.selectedOpportunityBoard) return stocks
      return stocks.filter(stock => stock.board_name === this.selectedOpportunityBoard)
    },
    opportunityWarnings () {
      const warnings = Array.isArray(this.opportunityRadar.warnings) ? [...this.opportunityRadar.warnings] : []
      return warnings
    },
    opportunityEvaluation () {
      return this.opportunityRadar.evaluation && typeof this.opportunityRadar.evaluation === 'object'
        ? this.opportunityRadar.evaluation
        : {}
    },
    factorGroups () {
      const groups = {}
      this.factorCatalog.forEach(item => {
        if (!groups[item.group]) groups[item.group] = []
        groups[item.group].push(item)
      })
      return Object.keys(groups).map(name => ({ name, items: groups[name] }))
    },
    factorSelectedCount () {
      return Object.values(this.factorControls).filter(item => item && item.enabled && Number(item.weight) > 0).length
    },
    factorCurrentCount () {
      return this.factorRows.length
    },
    activePool () {
      return this.pools.find(pool => pool.id === this.activePoolId) || null
    },
    stageModelFilterOptions () {
      if (this.viewMode !== 'research' || this.activePoolId !== 'stage_model') return []
      return STAGE_MODEL_ROW_LABELS.map(label => ({
        label,
        count: this.rows.filter(row => this.matchesResearchLabel(row, label)).length
      })).filter(item => item.count > 0)
    },
    repairStageFilterOptions () {
      if (this.viewMode !== 'research' || this.activePoolId !== 'limit_pullback_repair') return []
      return ['强势结构', '回踩观察', '盘中确认'].map(label => ({
        label,
        count: new Set(this.rows.filter(row => this.repairStageLabel(row) === label).map(row => row.code)).size
      })).filter(item => item.count > 0)
    },
    activeConfirmationNotice () {
      if (this.viewMode !== 'research') return ''
      if (!this.activePool || !['bullish_oversold', 'first_board_discovery'].includes(this.activePool.id)) return ''
      const status = this.activePool.confirmation_status || {}
      const readiness = status.auction_readiness || {}
      const diagnostics = status.diagnostics || {}
      if (readiness.ready === false) return readiness.error || diagnostics.message || '竞价数据尚未就绪，任务仍在重试。'
      const evaluated = Number(diagnostics.evaluated || diagnostics.eligible_count || 0)
      const confirmed = Number(diagnostics.confirmed_count || 0)
      if (evaluated > 0 && confirmed === 0) {
        const labels = {
          change_pct: '涨跌幅',
          above_open: '站上开盘价',
          intraday_position: '日内位置',
          market_breadth: '市场宽度',
          open_gap: '开盘缺口',
          holds_causal_lower: '守住因果下轨',
          auction_gap: '竞价涨幅',
          auction_amount: '竞价额',
          amount_to_previous: '竞价额/昨额',
          amount_to_float: '竞价额/流通市值',
          above_defense: '防守位',
          auction_score: '综合分'
        }
        const failed = diagnostics.failed_checks || diagnostics.rejected_by_gate || {}
        const summary = Object.keys(failed)
          .sort((left, right) => Number(failed[right]) - Number(failed[left]))
          .slice(0, 4)
          .map(key => `${labels[key] || key}${failed[key]}只`)
          .join('、')
        return `已评估${evaluated}只，本轮无承接确认${summary ? `；主要未通过：${summary}` : ''}。`
      }
      if (evaluated === 0) {
        return this.activePool.id === 'bullish_oversold'
          ? '尚未生成今日盘中承接评估；请检查全市场行情循环和盘中跟踪快照。'
          : '尚未生成今日竞价确认评估；系统会在竞价数据就绪后持续重试至11:30。'
      }
      return ''
    },
    rows () {
      if (this.viewMode === 'research') {
        return this.dedupeResearchRows(this.activePoolId === 'all' ? this.pools : (this.activePool ? [this.activePool] : []))
      }
      let poolRows
      if (this.activePoolId === 'all') {
        poolRows = this.pools.reduce((all, pool) => all.concat(pool.rows || []), [])
      } else {
        poolRows = this.activePool ? (this.activePool.rows || []) : []
      }

      if (this.activePoolId !== 'all') return this.dedupePoolRows(poolRows)

      const grouped = {}
      poolRows.forEach(row => {
        const key = row.pool_id || 'unknown'
        if (!grouped[key]) grouped[key] = []
        grouped[key].push(row)
      })
      return Object.keys(grouped).reduce(
        (all, poolId) => all.concat(this.dedupePoolRows(grouped[poolId])),
        []
      )
    },
    activePoolRowCount () {
      if (this.activePoolId === 'all') {
        return this.pools.reduce((total, pool) => total + this.poolDisplayCount(pool), 0)
      }
      return this.activePool ? this.poolDisplayCount(this.activePool) : 0
    },
    activePoolRecordCount () {
      return this.pools.reduce((total, pool) => total + Number(pool.total_count || 0), 0)
    },
    activeTradeSourceCount () {
      if (this.viewMode !== 'trade') return this.activePoolUniqueCount
      if (this.activePoolId === 'all') {
        return this.pools.reduce(
          (total, pool) => total + Number(pool.source_total_count || pool.total_count || 0),
          0
        )
      }
      return Number(
        (this.activePool && (this.activePool.source_total_count || this.activePool.total_count)) || 0
      )
    },
    activeTradeFilteredCount () {
      return Math.max(0, this.activeTradeSourceCount - this.activePoolUniqueCount)
    },
    activePoolUniqueCount () {
      return new Set(this.rows.map(row => row.code).filter(Boolean)).size
    },
    activeViewRows () {
      return this.rows
    },
    activeViewRecordCount () {
      return this.activeViewRows.length
    },
    activeViewUniqueCount () {
      return new Set(this.activeViewRows.map(row => row.code).filter(Boolean)).size
    },
    boardFilterOptions () {
      const groups = {
        industry: new Map(),
        concept: new Map(),
        strategy: new Map()
      }
      this.activeViewRows.forEach(row => {
        const code = String(row.code || '')
        this.rowBoardEntries(row).forEach(entry => {
          if (!groups[entry.type].has(entry.name)) groups[entry.type].set(entry.name, new Set())
          groups[entry.type].get(entry.name).add(code || `${row.name || ''}-${entry.name}`)
        })
      })
      const options = type => [...groups[type].entries()]
        .map(([label, codes]) => ({
          label,
          count: codes.size,
          value: `${type}::${label}`
        }))
        .sort((left, right) => right.count - left.count || left.label.localeCompare(right.label, 'zh-CN'))
      return {
        industries: options('industry'),
        concepts: options('concept'),
        strategies: options('strategy')
      }
    },
    filteredRows () {
      const query = this.searchText.toLowerCase()
      const filtered = [...this.rows]
        .filter(row => {
          const matchesSearch = !query || this.rowSearchText(row).includes(query)
          const matchesBoard = !this.selectedBoardFilter || this.rowMatchesBoard(row, this.selectedBoardFilter)
          const matchesPositive = !this.positiveOnly || Number(row.change_pct) > 0
          const matchesLimit = !this.hideLimitUp || row.is_at_limit_up !== true
          const matchesStage = this.viewMode !== 'research' || this.activePoolId !== 'stage_model' || !this.selectedStageModelLabel || this.matchesResearchLabel(row, this.selectedStageModelLabel)
          const matchesRepair = this.viewMode !== 'research' || this.activePoolId !== 'limit_pullback_repair' || !this.selectedRepairStage || this.repairStageLabel(row) === this.selectedRepairStage
          return matchesSearch && matchesBoard && matchesPositive && matchesLimit && matchesStage && matchesRepair
        })
      return filtered
        .sort((a, b) => {
          const direction = this.sortDirection === 'asc' ? 1 : -1
          if (this.viewMode === 'trade' && this.sortKey === 'score') {
            const gradeRank = { A: 4, B: 3, C: 2, X: 1 }
            const stateRank = { core_action: 3, conditional_watch: 2, risk_review: 1 }
            const gradeDiff = (Number(gradeRank[a.action_grade] || 0) - Number(gradeRank[b.action_grade] || 0)) * direction
            if (gradeDiff) return gradeDiff
            const stateDiff = (Number(stateRank[a.decision_state] || 0) - Number(stateRank[b.decision_state] || 0)) * direction
            if (stateDiff) return stateDiff
          }
          const aValue = this.sortMetricValue(a)
          const bValue = this.sortMetricValue(b)
          const aValid = Number.isFinite(aValue)
          const bValid = Number.isFinite(bValue)
          if (!aValid || !bValid) {
            if (aValid !== bValid) return aValid ? -1 : 1
          } else {
            const primaryDiff = (aValue - bValue) * direction
            if (primaryDiff) return primaryDiff
          }

          const changeDiff = Number(b.change_pct || 0) - Number(a.change_pct || 0)
          const scoreDiff = Number(b.score || 0) - Number(a.score || 0)
          return changeDiff || scoreDiff || String(a.code).localeCompare(String(b.code))
        })
    },
    filteredUniqueCount () {
      return new Set(this.filteredRows.map(row => row.code).filter(Boolean)).size
    },
    renderedRows () {
      return this.isMobileViewport
        ? this.filteredRows.slice(0, this.visibleRowLimit)
        : this.filteredRows
    },
    hasMoreRenderedRows () {
      return this.isMobileViewport && this.renderedRows.length < this.filteredRows.length
    },
    activePoolLabel () {
      if (this.activePool) return this.activePool.label
      if (this.viewMode === 'factor') return '因子组合结果'
      return this.viewMode === 'trade' ? '全部工作池' : '全部项目股池'
    },
    activePoolDescription () {
      if (this.viewMode === 'trade') {
        if (this.activePoolId === 'all') return '按决策优先级查看全部标的'
        return (this.activePool && this.activePool.description) || '统一决策工作池'
      }
      if (this.viewMode === 'factor') return '当前组合评分、原子因子标签与独立回测'
      if (this.activePoolId === 'all') return '全部模型信号，按当前涨跌幅排列'
      if (this.activePoolId === 'stage_model') {
        const rule = (this.activePool && this.activePool.method && this.activePool.method.channel_rule) || {}
        return `全部量价阶段展示；通常须在通达信通道下部${rule.max_position_pct == null ? 20 : rule.max_position_pct}%；“操”独立展示，不受底部过滤，标注是否收回下轨`
      }
      if (this.activePoolId === 'bullish_oversold') return '多头结构中的超跌修复与盘中承接确认；通道位置供参考'
      if (this.activePoolId === 'first_board_discovery') return '低位平台初筛、竞价与盘中承接确认；通道位置供参考，不作为入池条件'
      if (this.activePoolId === 'ma_cross_channel') {
        const rules = (this.activePool && this.activePool.method && this.activePool.method.rules) || {}
        const model = rules.tdx_channel_model === 'tdx_formula_endpoint' ? '通达信公式当日端点' : rules.tdx_channel_model === 'ma10_endpoint' ? 'MA10近似通道' : '旧版通道（待重建）'
        return `近期金叉或当日回踩金叉${rules.limit_pullback_enabled ? '或老金叉涨停回调守低' : ''}；${model}向上，通道位置供参考`
      }
      if (this.activePoolId === 'bear_overnight') return '宽筛候选 · 全天强势/午后修复 · 每日最多一个研究焦点'
      if (this.activePoolId === 'macd_bear_rebound') return '零轴下死叉 · 昨日绿柱峰值 · 峰后首次缩短且放量收阳'
      if (this.activePoolId === 'limit_pullback_repair') return '强势结构 → 回踩观察 → 盘中确认 · 结构不是买入信号 · 同题材优选高板'
      return `${this.activePool ? this.activePool.label : '股池'} · 当前快照`
    },
    overnightDiagnostics () {
      return (this.activePool && this.activePool.method && this.activePool.method.diagnostics) || {}
    },
    repairDetail () {
      return (this.selectedRow || {}).limit_pullback_repair_detail || {}
    },
    macdReboundDiagnostics () {
      return (this.activePool && this.activePool.method && this.activePool.method.diagnostics) || {}
    },
    macdReboundSettings () {
      return (this.activePool && this.activePool.method && this.activePool.method.screening_settings) || {}
    },
    overnightSettings () {
      const settings = this.activePool && this.activePool.method && this.activePool.method.screening_settings
      return { min_above_vwap_time_pct: 85, afternoon_min_above_vwap_time_pct: 90, min_sample_coverage_pct: 90, funds_total_windows: 4, max_below_vwap_minutes: 10, max_below_vwap_depth_pct: 0.5, entry_lookback_minutes: 10, ...(settings || {}) }
    },
    overnightFocus () {
      return (this.activePool && this.activePool.method && this.activePool.method.focus) || {}
    },
    overnightImplementation () {
      const notes = this.activePool && this.activePool.method && this.activePool.method.implementation
      return Array.isArray(notes) ? notes : []
    },
    isStageModelSelected () {
      return Boolean(this.selectedRow && this.selectedRow.pool_id === 'stage_model')
    },
    currentStagePhase () {
      const cycles = this.stageTimeline && Array.isArray(this.stageTimeline.cycles)
        ? this.stageTimeline.cycles
        : []
      const stage = this.selectedRow && String(this.selectedRow.stage || '')
      const phases = cycles.reduce((all, cycle) => all.concat(cycle.phases || []), [])
      const aliases = {
        '建仓吸筹': ['建仓吸筹', '整理/吸筹'],
        '试盘突破': ['试盘突破', '整理/试盘'],
        '洗盘企稳': ['洗盘企稳', '洗盘后再拉升'],
        '拉升跟踪': ['拉升跟踪', '拉升']
      }
      const acceptedStages = aliases[stage] || [stage]
      const exact = [...phases].reverse().find(phase => acceptedStages.includes(phase.stage))
      return exact || [...phases].reverse()[0] || {}
    },
    isBullishOversoldSelected () {
      return Boolean(this.selectedRow && this.selectedRow.pool_id === 'bullish_oversold')
    },
    isFirstBoardSelected () {
      return Boolean(this.selectedRow && this.selectedRow.pool_id === 'first_board_discovery')
    },
    selectedL2Factor () {
      if (this.l2Watch.factor && Object.keys(this.l2Watch.factor).length) return this.l2Watch.factor
      return (this.selectedRow && this.selectedRow.l2_factor) || {}
    },
    selectedL2SourceLabel () {
      const source = String((this.selectedL2Factor || {}).source || (this.selectedRow || {}).l2_source || 'unknown')
      const extra = (this.selectedL2Factor.supplementary_sources || []).map(this.l2SourceName).join('、')
      return `${this.l2SourceName(source)}${extra ? ` + ${extra}` : ''}`
    },
    datPointsTitle () {
      const points = this.datPoints
      if (points.status !== 'ok') return points.error || '点击刷新DAT积分余额'
      return `赠送 ${points.gift_points} · 付费 ${points.paid_points} · 今日观察扣减 ${Number(points.today_observed_points || 0).toFixed(2)} · 更新 ${points.updated_at}${points.paid_reason ? ` · ${points.paid_reason}` : ''}`
    },
    paidL2AvailabilityText () {
      if (this.l2PaidPending) return `${String(this.l2PaidPending).toUpperCase()} 正在采样，请等待本次结果`
      if (this.datPointsLoading) return '正在检查积分和采样额度…'
      const points = this.datPoints
      if (!points.paid_available) return `暂不可采样：${points.error || points.paid_reason || '积分状态尚未确认，请重新检查'}`
      const reservation = Number(points.estimated_reservation_points)
      return `可手动采样 · 余额 ${Number(points.balance_points).toFixed(2)} 积分${Number.isFinite(reservation) ? ` · 每次预留约 ${reservation.toFixed(4)} 积分` : ''}`
    },
    supportSourceSummary () {
      const labels = { book: '盘口', active: '主动买', queue: '队列', cancel: '撤单', capital_flow: '资金', big_order_flow: '大单' }
      return Object.entries(this.selectedL2Factor.metric_sources || {}).map(([key, item]) => `${labels[key] || key}：${this.l2SourceName(item.source)}`).join(' · ') || '保留数据来源与快照时间'
    },
    selectedL2Version () {
      const factor = this.selectedL2Factor || {}
      return String(factor.factor_version || (this.selectedRow || {}).l2_factor_version || '版本未知')
    },
    datInsightRows () {
      const opportunities = Array.isArray(this.datInsights.opportunities) ? this.datInsights.opportunities : []
      const events = Array.isArray(this.datInsights.events) ? this.datInsights.events : []
      return [...opportunities.map(item => ({ ...item, title: item.title || (item.reasons || []).join('；') })), ...events]
        .filter((item, index, rows) => rows.findIndex(row => row.id === item.id) === index)
    },
    datInsightsFresh () {
      const stamp = this.quoteTimestamp(this.datInsights.captured_at)
      return Number.isFinite(stamp) && Date.now() - stamp <= 3 * 60 * 1000
    },
    datModuleCards () {
      const labels = { radar: '雷达', anomalies: '异动', scramble: '抢筹', sectors: '板块', leaders: '龙头', sentiment: '情绪', distribution: '分布', funds: '资金监控' }
      return Object.keys(labels).map(key => {
        const module = (this.datInsights.modules || {})[key] || {}
        const payload = module.payload || {}
        const data = payload.data || payload.result || payload
        const payloadRows = Array.isArray(data)
          ? data
          : ['rows', 'records', 'list', 'infos', 'upList', 'downList'].flatMap(name => Array.isArray(data[name]) ? data[name] : [])
        const rows = Array.isArray(module.rows) && module.rows.length ? module.rows : payloadRows
        const freshness = this.datInsightsFresh ? (module.freshness || 'unknown') : 'stale'
        return { key, label: labels[key], count: rows.length || (module.status === 'ok' ? (this.datInsightsFresh ? '可用' : '旧') : '--'), freshness }
      })
    },
    datInsightsStatusText () {
      if (this.datInsights.status === 'unavailable') return '主任务尚未生成 · 明确不可用'
      const stale = this.datModuleCards.filter(item => item.freshness === 'stale').length
      const state = this.datInsightsFresh ? '实时' : '历史快照，禁止作盘中确认'
      return `${this.datInsights.source || 'unknown'} · ${this.datInsights.captured_at || '无快照时间'} · ${state}${stale ? ` · ${stale}模块过期` : ''}`
    },
    filteredDatCatalog () {
      const search = this.datCatalogSearch.toLowerCase()
      if (!search) return this.datCatalog.entries
      return this.datCatalog.entries.filter(item => [item.label, item.path, item.source, item.category, item.operation].join(' ').toLowerCase().includes(search))
    },
    selectedSupport () {
      const row = this.selectedRow || {}
      const factor = this.selectedL2Factor || {}
      const windowData = factor.support_window && typeof factor.support_window === 'object'
        ? factor.support_window
        : {}
      const score = Number(factor.support_score !== undefined ? factor.support_score : (factor.score !== undefined ? factor.score : row.support_score || 0))
      const rollingScore = Number(windowData.support_decay_score !== undefined
        ? windowData.support_decay_score
        : (row.support_rolling_score || 0))
      const coverage = Number(factor.coverage !== undefined ? factor.coverage : (row.support_coverage || 0))
      const bookImbalance = factor.book_imbalance !== undefined ? factor.book_imbalance : row.support_book_imbalance
      const activeBuyRatio = factor.active_buy_ratio !== undefined ? factor.active_buy_ratio : row.support_active_buy_ratio
      const hasMetrics = [bookImbalance, activeBuyRatio, factor.bid_amount, factor.ask_amount,
        factor.queue_imbalance, factor.cancellation_imbalance, (factor.capital_flow || {}).net_inflow_yuan,
        (factor.big_order_flow || {}).net_inflow_yuan].some(this.hasMetric)
      const status = String(factor.stage_status || row.l2_stage_status || factor.status || row.l2_status || 'missing')
      const alignment = String(factor.stage_alignment || row.l2_stage_alignment || '')
      const source = String(factor.source || row.l2_source || 'unknown')
      const readiness = factor.readiness && typeof factor.readiness === 'object'
        ? factor.readiness
        : (row.l2_readiness && typeof row.l2_readiness === 'object' ? row.l2_readiness : {})
      const researchOnly = (
        readiness.actionable !== true ||
        factor.partial === true ||
        factor.full_l2 !== true ||
        factor.score_calibrated !== true
      )
      const divergent = score >= 12 && bookImbalance !== null && bookImbalance !== undefined && Number(bookImbalance) <= -0.2
      const providerFresh = readiness.fresh === true || (
        readiness.fresh === undefined && factor.data_freshness === 'fresh' && factor.status !== 'stale'
      )
      const review = factor.review_snapshot === true || factor.data_freshness === 'historical'
      const live = this.l2Watch.state === 'live' && providerFresh && !review
      const fresh = providerFresh && !review
      let confidence = row.support_confidence_text || '等待数据'
      let signal = row.support_signal_text || row.support_text || '承接待补'
      let tone = row.support_confidence || 'missing'
      if (!hasMetrics) {
        confidence = '等待数据'
        signal = '承接数据待补'
        tone = 'missing'
      } else if (review) {
        confidence = '盘后复盘'
        signal = '最后有效快照 · 研究观察'
        tone = 'medium'
      } else if (researchOnly) {
        confidence = fresh ? '部分L2 · 未校准' : 'L2时效待确认'
        signal = fresh ? `${factor.support_status_text || '部分L2研究观察'} · 未校准` : '部分L2时效待确认'
        tone = fresh ? 'medium' : 'stale'
      } else if (!fresh) {
        confidence = row.support_updated_at ? '数据过期' : '等待数据'
        signal = row.support_updated_at ? '承接过期' : '承接待补'
        tone = row.support_updated_at ? 'stale' : 'missing'
      } else if (coverage < 2) {
        confidence = '低覆盖'
        signal = '承接低覆盖'
        tone = 'low'
      } else if (alignment === 'bullish_reversal') {
        confidence = '转强修复'
        signal = 'L2反证风险 · 转强修复'
        tone = 'recovery'
      } else if (['bearish_conflict', 'bearish_risk_confirmed'].includes(alignment)) {
        confidence = '卖压确认'
        signal = alignment === 'bearish_risk_confirmed' ? '风险卖压确认' : '卖压冲突'
        tone = 'conflict'
      } else if (status === 'neutral') {
        confidence = '中性观察'
        signal = '承接中性'
        tone = 'medium'
      } else if (divergent) {
        confidence = '盘口分歧'
        signal = '承接偏强 · 盘口分歧'
        tone = 'medium'
      } else if (status === 'confirm') {
        confidence = '高置信'
        signal = `承接确认 ${Math.round(rollingScore || score)}`
        tone = 'high'
      }
      return {
        hasMetrics,
        signal,
        confidence,
        tone,
        score,
        rollingScore,
        coverage,
        bookImbalance,
        activeBuyRatio,
        queueImbalance: factor.queue_imbalance,
        cancelImbalance: factor.cancellation_imbalance,
        netFlow: (factor.capital_flow || {}).net_inflow_yuan,
        freshness: review ? '历史快照' : (live ? '实时' : (fresh ? '今日有效' : '非实时')),
        updatedAt: String(factor.updated_at || windowData.updated_at || windowData.last_at || row.support_updated_at || ''),
        source,
        readiness
      }
    },
    selectedPaidSamples () {
      return Object.values(this.l2PaidSamples[(this.selectedRow || {}).code] || {})
    },
    selectedRealtimePrice () {
      return Number((this.l2Watch.quote || {}).price || (this.selectedRow && this.selectedRow.price) || 0)
    },
    selectedRealtimeChange () {
      return this.liveChangePct(this.l2Watch.quote, this.selectedRow)
    },
    selectedPlatformLevels () {
      const fallback = {
        available: false,
        defense_price: 0,
        attack_price: 0,
        defense_rule: '',
        attack_rule: '',
        state: '待计算',
        source: '',
        structure_label: '',
        box_kind: '',
        start_date: '',
        end_date: '',
        distance_to_defense_pct: 0,
        distance_to_attack_pct: 0
      }
      const source = (this.selectedRow && this.selectedRow.platform_levels) || fallback
      const levels = { ...fallback, ...source }
      const price = Number(this.selectedRealtimePrice || 0)
      const defense = Number(levels.defense_price || 0)
      const attack = Number(levels.attack_price || 0)
      if (!levels.available || price <= 0 || defense <= 0 || attack <= defense) return levels
      levels.distance_to_defense_pct = (price / defense - 1) * 100
      levels.distance_to_attack_pct = (price / attack - 1) * 100
      levels.position_pct = (price - defense) * 100 / (attack - defense)
      return this.platformLevelsAtPrice(levels, price)
    },
    selectedPlatformMarkerStyle () {
      const position = Number(this.selectedPlatformLevels.position_pct || 0)
      const clamped = Math.max(2, Math.min(98, Number.isFinite(position) ? position : 0))
      return { left: `${clamped}%` }
    },
    selectedDailyPositions () {
      return (this.selectedRow && this.selectedRow.daily_positions) || {
        available: false,
        trade_date: '',
        ma5: 0,
        ma10: 0,
        ma13: 0,
        ma17: 0,
        ma55: 0,
        intraday_high: 0,
        intraday_low: 0,
        intraday_source: 'unavailable'
      }
    },
    dailyMaRows () {
      return [
        { key: 'ma5', label: '5日线' },
        { key: 'ma10', label: '10日线' },
        { key: 'ma13', label: '13日线' },
        { key: 'ma17', label: '17日线' },
        { key: 'ma55', label: '55日线' }
      ]
    },
    selectedMaCrossSignals () {
      if (this.viewMode === 'research') {
        const signals = this.selectedRow && this.selectedRow.ma_cross_signals
        return signals ? Object.values(signals).filter(Boolean) : []
      }
      return this.activeMaCrossSignals(this.selectedRow)
    },
    maReclaimRuleText () {
      const signal = this.selectedMaCrossSignals.find(item => ['daily_reclaim', 'intraday_reclaim'].includes(item.confirmation_type))
      return `回踩低点容差 ${signal ? signal.reclaim_lower_multiplier : 0.985}～${signal ? signal.reclaim_upper_multiplier : 1.02} 倍`
    },
    uniqueTrackedCount () {
      return new Set(this.rows.map(row => row.code).filter(Boolean)).size
    },
    visiblePoolCount () {
      return this.uniqueTrackedCount
    },
    sortLabel () {
      const labels = {
        price: '现价',
        change_pct: '涨跌幅',
        speed_1m_pct: '1分钟涨速',
        score: '评分',
        float_net_inflow_sort_pct: '流通盘净流入',
        main_participation_sort_pct: '主力介入度',
        main_net_intensity_pct: '主力净流强度',
        main_occupancy_sort_pct: '主力占盘比'
      }
      const direction = this.sortDirection === 'asc' ? '升序' : '降序'
      const label = this.viewMode === 'trade' && this.sortKey === 'score'
        ? '资金/L2/金叉共振'
        : (labels[this.sortKey] || '涨跌幅')
      return `${label}${direction}`
    },
    positiveCount () {
      return this.rows.filter(row => Number(row.change_pct) > 0).length
    },
    positiveRatio () {
      return this.rows.length ? Math.round(this.positiveCount * 1000 / this.rows.length) / 10 : 0
    },
    liveQuoteCount () {
      return this.rows.filter(row => row.quote_status === 'live').length
    },
    liveCoverage () {
      return this.rows.length ? Math.round(this.liveQuoteCount * 1000 / this.rows.length) / 10 : 0
    },
    isMarketOpen () {
      return Boolean(this.dashboard.market_status && this.dashboard.market_status.is_open)
    },
    marketStatus () {
      return (this.dashboard.market_status && this.dashboard.market_status.label) || '等待行情'
    },
    marketDate () {
      const value = (this.dashboard.market_status && this.dashboard.market_status.server_time) || this.dashboard.updated_at
      return value ? value.slice(0, 10).replace(/-/g, '.') : '--.--.--'
    },
    quoteStatusText () {
      const source = this.dashboard.source_status || {}
      if (source.fqgate_quote_count > 0) return 'FQGate实时行情已接入'
      const status = source.quotes
      return status === 'live' ? '实时行情已接入' : ['degraded', 'stale'].includes(status) ? '行情降级，使用快照' : '等待行情接口'
    },
    dashboardCacheNotice () {
      const status = this.dashboard.source_status || {}
      if (status.dashboard_cache === 'stale_refreshing') return '后台刷新中 · 当前显示上一版快照'
      if (status.dashboard_cache === 'refresh_failed') return '后台刷新失败 · 当前显示上一版快照'
      return ''
    },
    quoteUpdatedText () {
      const source = this.dashboard.source_status || {}
      const value = source.fqgate_quote_count > 0 ? source.fqgate_quote_updated_at : source.quote_updated_at
      return value ? `行情时间 ${value}` : `尚未获得实时行情 · 快照 ${this.dashboard.updated_at || '--'}`
    },
    publicFundsStatusText () {
      const sources = this.dashboard.source_status || {}
      const status = sources.public_funds || {}
      const labels = { ok: '正常', partial: '部分可用', stale: '已过期', warming: '预热中', error: '采集失败', unavailable: '不可用', waiting_external: '等待手工采集结束' }
      const state = labels[status.status] || '待采集'
      const kpl = sources.kaipanla_funds || {}
      let extra = ''
      if (kpl.status && kpl.status !== 'disabled') {
        const progress = kpl.coverage === 'all_a_share_rolling'
          ? `全A股 · 今日覆盖 ${kpl.covered_today || 0}/${kpl.total_stocks || 0} · 本轮已处理 ${kpl.pass_processed || 0} · 剩余 ${kpl.pass_remaining || 0} · 失败 ${kpl.pass_failed || 0} · 最近整轮结束 ${kpl.last_pass_completed_at || '尚未完成'}`
          : '候选采集'
        extra = ` · 开盘啦 ${labels[kpl.status] || kpl.status} · ${progress} · 本批个股${kpl.stock_count || 0}/板块${kpl.board_count || 0} · 最近个股采集 ${kpl.latest_stock_at || kpl.captured_at || '--'}（快照观察）`
        if (kpl.error) extra += ` · ${kpl.error}`
      }
      return `公开资金 ${state} · 更新 ${status.captured_at || '--'} · 候选分钟覆盖 ${status.minute_coverage || 0}/${status.minute_required || 0}（覆盖范围为持仓及候选）${extra}`
    },
    l2CollectionStatusText () {
      const budget = ((this.fqgateStatus || {}).stream_manager || {}).l2_collection
      if (!budget) return ''
      const labels = { idle: '待机', observing: '观察中', cooldown: '冷却中', capacity: '等待空位', circuit_open: '失败退避', disabled: '已关闭', already_active: '复用观察' }
      return `L2 ${labels[budget.state] || budget.state} · 活跃 ${(budget.active_codes || []).length}/${budget.max_active_codes || 5} · 冷却 ${budget.cooling_keys || 0} 项`
    },
    fqgateStatusText () {
      if (this.fqgateOverview.status === 'ok') return '行情排行在线'
      if (this.fqgateOverview.status === 'degraded' && this.fqgateOverview.data_fresh) return '排行部分实时 · 其余历史快照'
      if (this.fqgateOverview.status === 'degraded') return '无实时排行 · 当前仅保留历史快照'
      if (this.fqgateStatus.status === 'degraded') return 'FQGate会话降级'
      if (this.fqgateStatus.status === 'missing') return 'FQGate未配置'
      return this.fqgateOverview.status === 'error' ? '市场雷达暂不可用' : '等待市场数据'
    },
    fqgateAlertStatusText () {
      const states = { idle: '短线异动未连接', connecting: '短线异动连接中', subscribing: '短线异动订阅确认中',
        connected: '短线异动已订阅', waiting_data: '短线异动已连接 · 当前暂无事件', degraded: '短线异动异常 · 自动重试中', offline: '短线异动断线 · 正在重连' }
      return this.fqgateAlertError ? `短线异动：${this.fqgateAlertError}` : states[this.fqgateAlertState] || '短线异动等待数据'
    },
    fqgateAlertEmptyText () {
      if (this.fqgateAlertState === 'connecting') return '正在连接短线异动流'
      if (this.fqgateAlertState === 'offline' || this.fqgateAlertState === 'degraded') return '短线异动流不可用，未显示旧事件'
      return '暂无当次连接收到的短线异动事件'
    },
    fqgateOverviewTimeText () {
      const value = this.fqgateOverview.data_updated_at
      if (!value) return '--:--:--'
      return `${this.fqgateOverview.data_fresh ? '实时' : '旧'} ${String(value).slice(11, 19)}`
    },
    fqgateLimitStatistics () {
      const feed = (this.fqgateOverview.feeds || {}).limit_up_statistics || {}
      const records = Array.isArray(feed.records) ? feed.records : []
      const find = (direction, period) => records.find(item => item && item.direction === direction && item.period === period) || {}
      return {
        currentUp: find('limit_up', 'current'),
        previousUp: find('limit_up', 'previous'),
        currentDown: find('limit_down', 'current'),
        previousDown: find('limit_down', 'previous')
      }
    },
    marketDiagnosis () {
      if (!this.fqgateOverview.data_fresh) {
        const age = Number(this.fqgateOverview.data_age_seconds)
        const ageText = Number.isFinite(age) ? `${Math.floor(age / 3600)}小时${Math.floor((age % 3600) / 60)}分前` : '时间未知'
        return { title: '实时盘面待恢复', score: '--', summary: `排行数据为${ageText}的历史快照`,
          action: '旧涨跌家数、封板率与周期判断已停用，不参与盘中决策', tone: 'diagnosis-risk' }
      }
      const cycle = this.currentMarketCycle || {}
      const policy = cycle.risk_policy || {}
      const metrics = cycle.metrics || {}
      const stats = this.fqgateLimitStatistics
      const currentUp = Number(stats.currentUp.sealed_count || 0)
      const previousUp = Number(stats.previousUp.sealed_count || 0)
      const currentDown = Number(stats.currentDown.sealed_count || 0)
      const previousDown = Number(stats.previousDown.sealed_count || 0)
      const touched = Number(stats.currentUp.touched_count || 0)
      const sealRate = Number(stats.currentUp.seal_rate || 0) * 100
      const upCount = Number(metrics.up_count || 0)
      const downCount = Number(metrics.down_count || 0)
      const breadth = upCount + downCount > 0 ? upCount * 100 / (upCount + downCount) : 0
      const improving = currentUp > previousUp && currentDown < previousDown
      const weakening = currentUp < previousUp && currentDown > previousDown
      const blocked = policy.risk_gate === 'blocked'
      let interpretation = '等待涨跌分布与涨停统计形成一致信号'
      if (improving && blocked) interpretation = '涨停扩张、跌停收敛，但周期风控仍阻断，先按退潮修复而非主升确认'
      else if (improving) interpretation = '涨停扩张、跌停收敛，短线情绪正在修复'
      else if (weakening) interpretation = '涨停收缩、跌停扩散，短线情绪继续走弱'
      else if (breadth >= 60) interpretation = '上涨广度占优，但涨停与跌停尚未形成明确共振'
      const facts = []
      if (upCount || downCount) facts.push(`上涨${upCount}家、下跌${downCount}家，红盘率${breadth.toFixed(1)}%`)
      if (currentUp || touched) facts.push(`封板${currentUp}/${touched}家，封板率${sealRate.toFixed(1)}%`)
      if (previousUp || previousDown) facts.push(`较昨日涨停${this.signedInteger(currentUp - previousUp)}、跌停${this.signedInteger(currentDown - previousDown)}`)
      return {
        title: `${cycle.state || '周期待确认'} · ${policy.position_recommendation || '观察'}`,
        score: Number(cycle.score || 0).toFixed(1),
        summary: facts.length ? facts.join('；') : '等待盘面统计',
        action: `${interpretation}${policy.invalid_condition ? `；${policy.invalid_condition}` : ''}`,
        tone: blocked || weakening ? 'diagnosis-risk' : improving ? 'diagnosis-positive' : 'diagnosis-neutral'
      }
    },
    fqgateOverviewRows () {
      if (!this.fqgateOverview.data_fresh) {
        return [
          { key: 'breadth', label: '上涨 / 下跌', value: '--' },
          { key: 'limit', label: '封板 / 触板', value: '--' },
          { key: 'change', label: '较昨日 涨停 / 跌停', value: '--' }
        ]
      }
      const cycle = this.currentMarketCycle || {}
      const metrics = cycle.metrics || {}
      const stats = this.fqgateLimitStatistics
      const currentUp = Number(stats.currentUp.sealed_count || 0)
      const previousUp = Number(stats.previousUp.sealed_count || 0)
      const currentDown = Number(stats.currentDown.sealed_count || 0)
      const previousDown = Number(stats.previousDown.sealed_count || 0)
      const touched = Number(stats.currentUp.touched_count || 0)
      const sealRate = Number(stats.currentUp.seal_rate || 0) * 100
      return [
        { key: 'breadth', label: '上涨 / 下跌', value: metrics.up_count || metrics.down_count ? `${Number(metrics.up_count || 0)} / ${Number(metrics.down_count || 0)}` : '--' },
        { key: 'limit', label: '封板 / 触板', value: currentUp || touched ? `${currentUp}/${touched} · ${sealRate.toFixed(0)}%` : '--' },
        { key: 'change', label: '较昨日 涨停 / 跌停', value: previousUp || previousDown ? `${this.signedInteger(currentUp - previousUp)} / ${this.signedInteger(currentDown - previousDown)}` : '--' }
      ]
    },
    lastUpdatedText () {
      return this.dashboard.updated_at ? this.dashboard.updated_at.slice(11, 19) : '--:--:--'
    },
    snapshotUpdatedText () {
      const values = this.pools.map(pool => pool.snapshot_at).filter(Boolean).sort()
      return values.length ? values[values.length - 1] : '暂无'
    },
    pulseRows () {
      const groups = [
        { label: '上涨标的', count: this.rows.filter(row => Number(row.change_pct) > 0).length, tone: 'bar-mint' },
        { label: '实时行情', count: this.liveQuoteCount, tone: 'bar-blue' },
        { label: '阶段确认', count: this.rows.filter(row => row.status === '已确认' || row.status === '承接确认').length, tone: 'bar-yellow' },
        { label: '风险提示', count: this.rows.filter(row => row.stage === '出货风险' || (row.risks || []).length).length, tone: 'bar-coral' }
      ]
      const max = Math.max(...groups.map(item => item.count), 1)
      return groups.map(item => ({ ...item, percent: Math.round(item.count * 100 / max) }))
    }
  },
  watch: {
    dashboard () {
      if (this.activePoolId !== 'all' && !this.visiblePools.some(pool => pool.id === this.activePoolId)) {
        this.activePoolId = 'all'
      }
    },
    sidePanelOpen (open) {
      document.body.classList.toggle('panel-open', open)
      if (open) {
        this._panelReturnFocus = document.activeElement
        this.$nextTick(() => {
          if (this.$refs.insightPanel) this.$refs.insightPanel.focus()
        })
      } else if (this._panelReturnFocus && this._panelReturnFocus.isConnected) {
        this._panelReturnFocus.focus({ preventScroll: true })
      }
    },
    viewMode () {
      this.activePoolId = 'all'
      this.selectedBoardFilter = ''
      this.selectedStageModelLabel = ''
      this.selectedRepairStage = ''
      this.selectedRow = null
      this.detailPanelOpen = false
      this.stopL2Watch()
      if (!this.factorResearchEnabled && this.viewMode === 'factor') {
        this.viewMode = 'trade'
        return
      }
    },
    activePoolId () {
      this.selectedBoardFilter = ''
      this.selectedStageModelLabel = ''
      this.selectedRepairStage = ''
      this.resetVisibleRows()
      this.selectedRow = null
      this.detailPanelOpen = false
      this.stopL2Watch()
    },
    searchText () {
      this.resetVisibleRows()
    },
    selectedBoardFilter () {
      this.resetVisibleRows()
    },
    selectedStageModelLabel () {
      this.resetVisibleRows()
    },
    selectedRepairStage () {
      this.resetVisibleRows()
    },
    repairStageFilterOptions (options) {
      if (this.selectedRepairStage && !options.some(item => item.label === this.selectedRepairStage)) this.selectedRepairStage = ''
    }
  },
  mounted () {
    this.loadAlertPreferences()
    this.updateViewportMode()
    this.fetchDashboard()
    this.fetchStyleAlpha()
    this.fetchFQGateStatus()
    this.fetchDatIntelligence()
    this.restartTimer()
    window.addEventListener('resize', this.updateViewportMode)
    window.addEventListener('keydown', this.handleGlobalKeydown)
  },
  beforeDestroy () {
    document.body.classList.remove('panel-open')
    if (this.dashboardPollTimer) window.clearTimeout(this.dashboardPollTimer)
    if (this.dashboardAbortController) this.dashboardAbortController.abort()
    if (this.alertBannerTimer) window.clearTimeout(this.alertBannerTimer)
    this.restoreDocumentTitle()
    this.stopTimer()
    this.stopL2Watch()
    this.stopFQGateAlerts()
    window.removeEventListener('resize', this.updateViewportMode)
    window.removeEventListener('keydown', this.handleGlobalKeydown)
  },
  methods: {
    bottomRepairText (row) {
      const repair = row.bottom_repair || {}
      if (!repair.available || !['observing', 'confirmed', 'retested', 'failed'].includes(repair.stage)) return ''
      const live = repair.live_position || {}
      const distance = live.risk_distance_pct !== null && live.risk_distance_pct !== undefined ? live.risk_distance_pct : repair.risk_distance_pct
      const risk = distance !== null && distance !== undefined && Number.isFinite(Number(distance))
        ? ` · ${live.captured_at ? '现价' : '日线'}风险距离${Number(distance).toFixed(1)}%` : ''
      return `${repair.stage_label} ${repair.evidence_count}/4${repair.freshness === 'stale' ? ' · 历史' : risk}`
    },
    alertStorageGet (key) {
      try { return window.localStorage ? window.localStorage.getItem(key) : null } catch (error) { return null }
    },
    alertStorageSet (key, value) {
      try {
        if (window.localStorage) window.localStorage.setItem(key, value)
      } catch (error) {
        return false
      }
      return true
    },
    loadAlertPreferences () {
      this.alertSoundEnabled = this.alertStorageGet('pooldesk.alert.sound') === 'on'
      this.notificationPermission = typeof window.Notification === 'undefined' ? 'unsupported' : window.Notification.permission
      this.originalDocumentTitle = document.title
    },
    alertDedupKey (alert) {
      if (alert && alert.id) return String(alert.id)
      const minute = String((alert && alert.updated_at) || '').slice(0, 16)
      return `${String((alert && alert.code) || '')}:${minute}`
    },
    readSeenAlerts () {
      try {
        const parsed = JSON.parse(this.alertStorageGet('pooldesk.alert.seen.v1') || '{}')
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
      } catch (error) { return {} }
    },
    rememberAlertKeys (alerts) {
      const now = Date.now()
      const cutoff = now - 2 * 24 * 60 * 60 * 1000
      const seen = Object.fromEntries(Object.entries(this.readSeenAlerts()).filter(([, stamp]) => Number(stamp) >= cutoff))
      alerts.forEach(alert => { seen[this.alertDedupKey(alert)] = now })
      const compact = Object.fromEntries(Object.entries(seen).sort((left, right) => Number(right[1]) - Number(left[1])).slice(0, 500))
      this.alertStorageSet('pooldesk.alert.seen.v1', JSON.stringify(compact))
    },
    isNotifyingAlert (alert) {
      return !!(alert && alert.code && alert.tier !== 'B' && alert.notification_allowed !== false)
    },
    processHighConfidenceAlerts (alerts) {
      const rows = Array.isArray(alerts) ? alerts.filter(this.isNotifyingAlert) : []
      const seen = this.readSeenAlerts()
      const fresh = rows.filter(item => !seen[this.alertDedupKey(item)])
      if (!fresh.length) return
      this.rememberAlertKeys(fresh)
      this.prominentAlert = fresh[0]
      this.newAlertBatchCount = fresh.length
      if (this.alertBannerTimer) window.clearTimeout(this.alertBannerTimer)
      this.alertBannerTimer = window.setTimeout(() => this.dismissProminentAlert(), 20000)
      if (!this.originalDocumentTitle) this.originalDocumentTitle = document.title
      document.title = `🚨 ${fresh.length}条强势提醒 · ${fresh[0].name || fresh[0].code}`
      this.sendDesktopAlerts(fresh)
      this.playAlertSound()
    },
    async enableDesktopNotifications () {
      if (typeof window.Notification === 'undefined') {
        this.notificationPermission = 'unsupported'
        return
      }
      if (window.Notification.permission === 'granted') {
        this.notificationPermission = 'granted'
        return
      }
      try { this.notificationPermission = await window.Notification.requestPermission() } catch (error) { this.notificationPermission = 'denied' }
    },
    sendDesktopAlerts (alerts) {
      if (typeof window.Notification === 'undefined' || window.Notification.permission !== 'granted') return
      alerts.filter(this.isNotifyingAlert).slice(0, 3).forEach(alert => {
        const notice = new window.Notification(`强势机会 · ${alert.name || alert.code} ${alert.code}`, {
          body: `${(alert.reasons || []).join(' · ')} · 综合${Number(alert.score || 0).toFixed(1)}分`,
          tag: `pooldesk-${this.alertDedupKey(alert)}`,
          renotify: false,
          requireInteraction: true
        })
        notice.onclick = () => {
          window.focus()
          this.openAlertStock(alert)
          if (notice.close) notice.close()
        }
      })
    },
    toggleAlertSound () {
      this.alertSoundEnabled = !this.alertSoundEnabled
      this.alertStorageSet('pooldesk.alert.sound', this.alertSoundEnabled ? 'on' : 'off')
      if (this.alertSoundEnabled) this.playAlertSound(true)
    },
    playAlertSound (preview = false) {
      if (!this.alertSoundEnabled && !preview) return
      const AudioContext = window.AudioContext || window.webkitAudioContext
      if (!AudioContext) return
      try {
        const context = new AudioContext()
        const gain = context.createGain()
        gain.gain.setValueAtTime(0.0001, context.currentTime)
        gain.gain.exponentialRampToValueAtTime(0.18, context.currentTime + 0.02)
        gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.75)
        gain.connect(context.destination)
        ;[0, 0.2, 0.4].forEach((delay, index) => {
          const oscillator = context.createOscillator()
          oscillator.type = 'sine'
          oscillator.frequency.value = index === 1 ? 880 : 660
          oscillator.connect(gain)
          oscillator.start(context.currentTime + delay)
          oscillator.stop(context.currentTime + delay + 0.16)
        })
        window.setTimeout(() => { if (context.close) context.close() }, 1000)
      } catch (error) {
        return false
      }
      return true
    },
    openProminentAlert () {
      const alert = this.prominentAlert
      this.dismissProminentAlert()
      if (alert) this.openAlertStock(alert)
    },
    dismissProminentAlert () {
      this.prominentAlert = null
      this.newAlertBatchCount = 0
      if (this.alertBannerTimer) window.clearTimeout(this.alertBannerTimer)
      this.alertBannerTimer = null
      this.restoreDocumentTitle()
    },
    restoreDocumentTitle () {
      if (this.originalDocumentTitle) document.title = this.originalDocumentTitle
    },
    openStockDailyChart (row) {
      if (this.$refs.stockDailyChart) this.$refs.stockDailyChart.openStock(row)
    },
    overnightMetric (detail, key) {
      const value = detail && detail[key]
      if (value === null || value === undefined || value === '') return '--'
      const number = Number(value)
      return Number.isFinite(number) ? number.toFixed(1) : '--'
    },
    overnightFlowText (value) {
      if (value === null || value === undefined || !Number.isFinite(Number(value))) return '--'
      return `${Number(value) > 0 ? '+' : ''}${(Number(value) / 10000).toFixed(1)} 万元`
    },
    sortMetricValue (row) {
      if (this.sortKey === 'speed_1m_pct') return this.speedValue(row)
      const raw = this.viewMode === 'research' && this.sortKey === 'score'
        ? this.poolDisplayScore(row)
        : (this.viewMode === 'trade' && this.sortKey === 'score'
            ? (row.trade_selection_score !== undefined ? row.trade_selection_score : row.decision_score)
            : row[this.sortKey])
      if (raw === null || raw === undefined || raw === '') return Number.NaN
      const value = Number(raw)
      return Number.isFinite(value) ? value : Number.NaN
    },
    quoteTimestamp (value) {
      const stamp = String(value || '').trim().replace(' ', 'T')
      return stamp ? Date.parse(/(?:Z|[+-]\d{2}:\d{2})$/.test(stamp) ? stamp : stamp + '+08:00') : Number.NaN
    },
    speedValue (row) {
      if (!row || row.speed_1m_status !== 'ready' || row.speed_1m_pct === null || row.speed_1m_pct === undefined || row.speed_1m_pct === '') return Number.NaN
      const stamp = this.quoteTimestamp(row.speed_1m_updated_at)
      const now = Date.now()
      const session = time => {
        const date = new Date(time + 8 * 3600000)
        const minutes = date.getUTCHours() * 60 + date.getUTCMinutes() + date.getUTCSeconds() / 60
        if ([0, 6].includes(date.getUTCDay())) return ''
        const part = minutes >= 570 && minutes <= 690 ? 'am' : (minutes >= 780 && minutes <= 900 ? 'pm' : '')
        return part ? `${date.toISOString().slice(0, 10)}/${part}` : ''
      }
      const value = Number(row.speed_1m_pct)
      if (!this.validLivePrice(row.price) || !this.validLivePrice(row.speed_1m_price) || Math.abs(Number(row.price) - Number(row.speed_1m_price)) > 0.000001) return Number.NaN
      if (!Number.isFinite(stamp) || now - stamp > 90000 || stamp - now > 5000 || !session(now) || session(now) !== session(stamp)) return Number.NaN
      return Number.isFinite(value) ? value : Number.NaN
    },
    speedText (row) {
      const value = this.speedValue(row)
      return Number.isFinite(value) ? this.signedPercent(value) : '--'
    },
    speedHint (row) {
      if (Number.isFinite(this.speedValue(row))) return `近${row.speed_1m_window_seconds}秒涨幅；基准 ${row.speed_1m_base_at}，更新 ${row.speed_1m_updated_at}`
      if (row.speed_1m_status === 'capacity') return '当前行情数量超过涨速采样上限，暂未采样'
      return row.speed_1m_status === 'warming' ? '采样中：需积累至少60秒同源行情；请开启自动刷新' : '暂无有效1分钟涨速：行情缺失、过期、休市或交易日历未就绪'
    },
    capitalFlowBadge (row) {
      const badge = row && row.capital_flow_badge
      const flow = row && row.stock_capital_flow
      if (flow && flow.available && flow.snapshot) {
        return { label: this.kaipanlaSnapshotStale(flow) ? '资金快照过期' : this.kaipanlaChangeCurrent(flow) ? '资金变动·观察' : '资金快照', tone: 'neutral' }
      }
      if ((!badge || badge.label === '资金待补') && row && row.funds_observation && row.funds_observation.recent) {
        return { label: '净额待补', tone: 'pending' }
      }
      return badge && badge.label ? badge : { label: '资金待补', tone: 'pending' }
    },
    kaipanlaSnapshot (row) {
      const flow = (row && row.stock_capital_flow) || {}
      return flow.source === 'kaipanla_main' && flow.snapshot ? flow : flow.kaipanla_snapshot || null
    },
    kaipanlaSnapshotStale (flow) {
      if (!flow || flow.stale) return true
      const received = this.quoteTimestamp(flow.captured_at)
      const current = new Date(Date.now() + 8 * 3600000).toISOString()
      const age = Date.now() - received
      const during = current.slice(11, 16) >= '09:15' && current.slice(11, 16) <= '15:10'
      return !Number.isFinite(received) || flow.trade_date !== current.slice(0, 10) || age < -5000 || (during && age > 180000)
    },
    kaipanlaChangeCurrent (flow) {
      const age = Date.now() - this.quoteTimestamp(flow.last_changed_at)
      return flow.time_quality === 'observed_change' && Number.isFinite(age) && age >= 0 && age <= 180000 && !this.kaipanlaSnapshotStale(flow)
    },
    kaipanlaFundsHint (row) {
      const flow = this.kaipanlaSnapshot(row)
      if (!flow) return ''
      const boards = (flow.board_snapshots || []).map(board => `${board.name} ${Number(board.main_net_inflow_yuan / 10000).toFixed(0)}万${board.stale ? '（过期）' : ''}`).join('、')
      return `开盘啦主力净额快照 ${(Number(flow.main_net_inflow_yuan) / 10000).toFixed(0)}万元 · ${flow.trade_date || '--'} · 采集 ${flow.captured_at || '--'}；${this.kaipanlaChangeCurrent(flow) ? `已观察到数值变化 ${flow.last_changed_at || ''}；` : ''}未提供可验证资金业务时间，观察用，不代替连续资金确认${boards ? '；关联板块：' + boards : '；板块采集中'}`
    },
    fundsObservationLabel (row) {
      const observation = row && row.funds_observation
      if (!observation) return ''
      const stamp = this.quoteTimestamp(observation.direction_captured_at || observation.captured_at)
      const age = Date.now() - stamp
      if (!observation.recent || !Number.isFinite(stamp) || age < -60000 || age > 180000) return 'FQ观察过期'
      const direction = (observation.direction || {}).main
      return { net_inflow: 'FQ方向流入·观察', net_outflow: 'FQ方向流出·观察', balanced: 'FQ方向平衡·观察' }[direction] || 'FQ大单样本·观察'
    },
    fundsObservationHint (row) {
      const observation = (row && row.funds_observation) || {}
      const orders = observation.large_orders || {}
      const extra = orders.returned_count === undefined ? '' : `；最近返回大单样本${orders.returned_count}条，非完整区间统计`
      return `FQGate方向/事件观察，采集于${observation.captured_at || '--'}；未提供可验证业务时间，不代替累计资金净额或单独触发交易；净额待补仍可走倍量/箱体分支，须通过A级提醒、板块确认和风控${extra}`
    },
    capitalMetricText (row, metric) {
      const flow = row && row.stock_capital_flow
      if (flow && flow.snapshot) return this.kaipanlaSnapshotStale(flow) ? '快照过期' : '快照观察'
      if (!row || row.capital_metric_fresh !== true || !this.capitalFlowCurrent(flow)) return flow && (flow.stale || flow.fresh) ? '数据过期' : '等待数据'
      if (['participation', 'occupancy'].includes(metric) && flow.gross_flow && !this.capitalFlowCurrent(flow.gross_flow)) return '数据过期'
      const values = {
        float: row.float_net_inflow_pct,
        participation: row.main_participation_pct,
        intensity: row.main_net_intensity_pct,
        occupancy: row.main_occupancy_pct
      }
      if (values[metric] === null || values[metric] === undefined || values[metric] === '') return '--'
      const value = Number(values[metric])
      return Number.isFinite(value) ? `${value >= 0 ? '+' : ''}${value.toFixed(metric === 'participation' ? 1 : 2)}%` : '--'
    },
    selectViewMode (mode) {
      if (!['trade', 'research', 'etf'].includes(mode)) return
      this.viewMode = mode
      this.sortKey = mode === 'trade' ? 'score' : 'change_pct'
      this.sortDirection = 'desc'
      this.resetVisibleRows()
    },
    refreshActiveView () {
      if (this.viewMode === 'etf') {
        if (this.$refs.etfPanel) this.$refs.etfPanel.refresh()
        return
      }
      this.fetchDashboard(true)
      this.fetchStyleAlpha()
    },
    styleAlphaPercent (value) {
      const number = Number(value)
      return Number.isFinite(number) ? `${number >= 0 ? '+' : ''}${(number * 100).toFixed(1)}%` : '--'
    },
    async fetchStyleAlpha () {
      if (this.styleAlphaLoading) return
      this.styleAlphaLoading = true
      this.styleAlphaError = ''
      try {
        let response = null
        for (let attempt = 0; attempt < 2; attempt += 1) {
          response = await fetch(`${API_BASE}/api/v1/dashboard/style-alpha?_ts=${Date.now()}`, {
            cache: 'no-store',
            headers: { Accept: 'application/json', 'Cache-Control': 'no-cache' }
          })
          if (response.ok) break
          if (![502, 503, 504].includes(response.status) || attempt > 0) {
            throw new Error(`风格–Alpha 接口返回 ${response.status}`)
          }
          await new Promise(resolve => window.setTimeout(resolve, 800))
        }
        if (!response || !response.ok) throw new Error(`风格–Alpha 接口返回 ${response ? response.status : '网络异常'}`)
        this.styleAlpha = await response.json()
      } catch (error) {
        this.styleAlphaError = error.message || '风格–Alpha 接口不可用'
      } finally {
        this.styleAlphaLoading = false
      }
    },
    updateViewportMode () {
      const mobile = window.matchMedia('(max-width: 600px)').matches
      if (mobile && !this.isMobileViewport) this.resetVisibleRows()
      this.isMobileViewport = mobile
    },
    resetVisibleRows () {
      this.visibleRowLimit = 60
    },
    loadMoreRows () {
      this.visibleRowLimit += 60
    },
    togglePositiveOnly () {
      this.positiveOnly = !this.positiveOnly
      this.resetVisibleRows()
    },
    normalizedConcepts (value) {
      if (Array.isArray(value)) return value.map(item => String(item || '').trim()).filter(Boolean)
      if (!value) return []
      if (typeof value === 'string') {
        const text = value.trim()
        if (!text) return []
        try {
          const parsed = JSON.parse(text)
          if (Array.isArray(parsed)) return parsed.map(item => String(item || '').trim()).filter(Boolean)
        } catch (error) {
          // Some historical pool files store concepts as comma-separated text.
        }
        return text.split(/[,，、|/]/).map(item => item.trim()).filter(Boolean)
      }
      return []
    },
    rowBoardEntries (row) {
      const entries = []
      const seen = new Set()
      const append = (type, value) => {
        const name = String(value || '').trim()
        const key = `${type}::${name}`
        if (!name || seen.has(key)) return
        seen.add(key)
        entries.push({ type, name })
      }
      const researchBoards = row.research_boards || []
      researchBoards.forEach(entry => append(entry.type, entry.name))
      const industry = String(row.industry || '').trim()
      append('industry', industry)
      const concepts = this.normalizedConcepts(row.concepts)
      concepts.forEach(name => append('concept', name))

      const sector = typeof row.sector === 'string' ? row.sector.trim() : ''
      if (sector.startsWith('概念:') || sector.startsWith('概念：')) {
        append('concept', sector.slice(3).trim())
      } else if (sector && sector !== industry && !concepts.includes(sector)) {
        append('strategy', sector)
      }
      const boardName = String(row.board_name || '').trim()
      if (boardName && boardName !== industry && !concepts.includes(boardName)) append('strategy', boardName)
      return entries
    },
    rowMatchesBoard (row, selected) {
      const separator = selected.indexOf('::')
      if (separator < 0) return false
      const type = selected.slice(0, separator)
      const name = selected.slice(separator + 2)
      return this.rowBoardEntries(row).some(entry => entry.type === type && entry.name === name)
    },
    rowSearchText (row) {
      const boards = this.rowBoardEntries(row).map(entry => entry.name).join(' ')
      return `${row.code || ''} ${row.name || ''} ${row.pool_label || ''} ${this.researchPoolText(row)} ${this.researchStageText(row)} ${boards}`.toLowerCase()
    },
    applyMobileSort () {
      this.sortDirection = 'desc'
      this.resetVisibleRows()
    },
    factorCombinationPayload () {
      const factors = Object.keys(this.factorControls)
        .filter(id => this.factorControls[id] && this.factorControls[id].enabled)
        .map(id => ({ id, weight: Number(this.factorControls[id].weight || 0) }))
        .filter(item => item.weight > 0)
      return {
        factors,
        limit: 2000,
        top_n: 500,
        filters: {
          min_score: Number(this.factorMinScore || 0),
          positive_change: Boolean(this.factorPositiveOnly)
        }
      }
    },
    applyFactorCatalog (payload) {
      const catalog = payload && Array.isArray(payload.factors) ? payload.factors : []
      this.factorCatalog = catalog
      const defaults = payload && payload.default_combination && Array.isArray(payload.default_combination.factors)
        ? payload.default_combination.factors
        : []
      const defaultMap = defaults.reduce((result, item) => {
        result[item.id] = item.weight
        return result
      }, {})
      const controls = {}
      catalog.forEach(item => {
        controls[item.id] = this.factorControls[item.id] || {
          enabled: Object.prototype.hasOwnProperty.call(defaultMap, item.id),
          weight: Number(defaultMap[item.id] || item.default_weight || 0)
        }
      })
      this.factorControls = controls
    },
    async fetchFactorResearch () {
      if (!this.factorResearchEnabled) return
      if (this.factorLoading) return
      this.factorLoading = true
      this.factorResearchError = ''
      try {
        const hasCombination = Object.keys(this.factorControls).length > 0
        const [catalogResult, currentResult] = await Promise.allSettled([
          this.requestFactorApi('/catalog', '因子目录'),
          hasCombination
            ? this.requestFactorApi('/combination/picks', '因子快照', {
              method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(this.factorCombinationPayload())
            })
            : this.requestFactorApi('/current?limit=2000', '因子快照')
        ])
        if (catalogResult.status === 'fulfilled') this.applyFactorCatalog(catalogResult.value)
        if (currentResult.status === 'fulfilled') {
          this.factorRows = Array.isArray(currentResult.value.rows) ? currentResult.value.rows : []
        }
        this.factorResearchError = [catalogResult, currentResult]
          .filter(result => result.status === 'rejected')
          .map(result => result.reason.message || '因子研究接口不可用').join('；')
      } catch (error) {
        this.factorResearchError = error.message || '因子研究接口不可用'
      } finally {
        this.factorLoading = false
      }
    },
    async requestFactorApi (path, label, options = {}, timeoutMs = 20000) {
      const controller = new AbortController()
      const timeout = window.setTimeout(() => controller.abort(), timeoutMs)
      try {
        const response = await fetch(`${API_BASE}/api/v1/factors${path}`, {
          ...options, headers: { Accept: 'application/json', ...options.headers }, signal: controller.signal
        })
        if (response.status === 404) throw new Error(`${label}接口未就绪（404），请更新线上因子服务与站点转发配置`)
        if (!response.ok) {
          const payload = await response.json().catch(() => ({}))
          throw new Error(typeof payload.detail === 'string' ? payload.detail : `${label}接口返回 ${response.status}`)
        }
        if (!(response.headers.get('Content-Type') || '').includes('application/json')) {
          throw new Error(`${label}返回了非 JSON 内容，请检查站点转发配置`)
        }
        return await response.json()
      } catch (error) {
        if (error.name === 'AbortError') throw new Error(`${label}请求超时，已保留本次数据，请稍后重试`)
        throw error
      } finally {
        window.clearTimeout(timeout)
      }
    },
    async runFactorCombination () {
      if (!this.factorResearchEnabled) return
      this.factorLoading = true
      this.factorResearchError = ''
      try {
        const payload = await this.requestFactorApi('/combination/picks', '组合选股', {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify(this.factorCombinationPayload())
        })
        this.factorRows = Array.isArray(payload.rows) ? payload.rows : []
      } catch (error) {
        this.factorResearchError = error.message || '组合选股失败'
      } finally {
        this.factorLoading = false
      }
    },
    async runFactorBacktest () {
      if (!this.factorResearchEnabled) return
      this.factorBacktestLoading = true
      this.factorBacktestError = ''
      try {
        const payload = this.factorCombinationPayload()
        payload.start_date = this.factorBacktestStart
        payload.end_date = this.factorBacktestEnd
        payload.holding_days = Math.max(1, Math.min(Number(this.factorHoldingDays || 3), 20))
        payload.top_n = 20
        this.factorBacktest = await this.requestFactorApi('/combination/backtest', '因子回测', {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        }, 120000)
      } catch (error) {
        this.factorBacktestError = error.message || '因子回测失败'
      } finally {
        this.factorBacktestLoading = false
      }
    },
    toggleSortDirection () {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
      this.resetVisibleRows()
    },
    async fetchDashboard (forceRefresh = false) {
      if (this.dashboardRequestInFlight) return
      this.dashboardRequestInFlight = true
      this.loading = true
      this.error = ''
      if (this.dashboardPollTimer) window.clearTimeout(this.dashboardPollTimer)
      const controller = new AbortController()
      this.dashboardAbortController = controller
      const timeout = window.setTimeout(() => controller.abort(), 12000)
      try {
        // A timestamp query parameter prevents browser/Nginx cache from
        // serving the same JSON forever. The backend still controls its own
        // short-lived snapshot and refresh lock.
        const query = new URLSearchParams({
          _ts: String(Date.now()),
          ...(forceRefresh ? { refresh: 'true' } : {})
        }).toString()
        const response = await fetch(`${API_BASE}/api/v1/pools/dashboard?${query}`, {
          cache: 'no-store',
          headers: { Accept: 'application/json', 'Cache-Control': 'no-cache' }, signal: controller.signal
        })
        if (!response.ok) throw new Error(`接口返回 ${response.status}`)
        const payload = await response.json()
        this.dashboard = payload
        const cacheState = (payload.source_status || {}).dashboard_cache
        if (!cacheState || cacheState === 'fresh') this.processHighConfidenceAlerts(payload.high_confidence_alerts)
        // The pool snapshot can legitimately keep the same updated_at while
        // live market data is changing. Refresh FQGate independently on every
        // dashboard poll; tying it to `changed` leaves the market panel frozen
        // on the last snapshot (often from the previous session).
        this.fetchFQGateSnapshot(payload)
        this.fetchFQGateOverview(payload)
        if (payload.source_status && payload.source_status.dashboard_cache === 'stale_refreshing') {
          this.dashboardPollTimer = window.setTimeout(() => this.fetchDashboard(), 2500)
        }
        if (
          this.selectedOpportunityBoard &&
          !this.opportunityBoards.some(board => board.board_name === this.selectedOpportunityBoard)
        ) {
          this.selectedOpportunityBoard = ''
        }
        this.loaded = true
        if (this.selectedRow) {
          const refreshed = this.rows.find(row => row.code === this.selectedRow.code)
          this.selectedRow = refreshed || null
          if (!refreshed) {
            this.detailPanelOpen = false
            this.stopL2Watch()
          }
        }
      } catch (error) {
        this.error = error.name === 'AbortError' ? '看板请求超时' : error.message || '无法连接后端股池接口'
      } finally {
        window.clearTimeout(timeout)
        this.dashboardAbortController = null
        this.loading = false
        this.dashboardRequestInFlight = false
      }
    },
    async fetchDatIntelligence () {
      this.fetchDatPoints()
      const results = await Promise.allSettled([
        this.detailApiGet(['dat/status', 'fqgate/detail-status']),
        this.detailApiGet(['dat/insights', 'fqgate/dat-insights']),
        this.datCatalog.entries.length ? Promise.resolve({ payload: this.datCatalog }) : this.detailApiGet(['dat/catalog', 'fqgate/dat-catalog'])
      ])
      const fields = ['datStatus', 'datInsights', 'datCatalog']
      results.forEach((result, index) => {
        if (result.status === 'fulfilled') this[fields[index]] = result.value.payload
        else this[fields[index]] = { ...this[fields[index]], status: 'unavailable', errors: [result.reason.message] }
      })
    },
    async detailApiGet (paths) {
      for (const path of paths) {
        const controller = new window.AbortController()
        const timeout = window.setTimeout(() => controller.abort(), 15000)
        try {
          const response = await fetch(`${API_BASE}/api/v1/${path}`, { headers: { Accept: 'application/json' }, signal: controller.signal })
          if (response.status === 404) continue
          if (!response.ok) throw new Error(`接口 ${path} 返回 ${response.status}`)
          if ((response.headers.get('content-type') || '').includes('text/html')) throw new Error('承接接口返回网页，请检查 API 反向代理')
          return { payload: await response.json(), path }
        } finally {
          window.clearTimeout(timeout)
        }
      }
      throw new Error('承接/积分接口未部署或反向代理未转发，请更新后端并检查 /api/v1/dat/')
    },
    async fetchDatPoints () {
      if (this.datPointsLoading) return
      this.datPointsLoading = true
      try {
        this.datPoints = (await this.detailApiGet(['dat/points', 'fqgate/points'])).payload
      } catch (error) {
        this.datPoints = { ...this.datPoints, status: 'unavailable', paid_available: false, error: error.message }
      } finally {
        this.datPointsLoading = false
      }
    },
    restartTimer () {
      this.stopTimer()
      if (this.refreshSeconds > 0) {
        this.refreshTimer = window.setInterval(() => {
          if (this.viewMode === 'etf') return
          // The dashboard cache refreshes in the background. Polling every
          // 15s during the session makes the new snapshot visible promptly;
          // it does not start a second build while the backend lock is held.
          this.fetchDashboard()
          this.fetchStyleAlpha()
          this.fetchDatIntelligence()
        }, Math.max(15, this.refreshSeconds) * 1000)
      }
    },
    stopTimer () {
      if (this.refreshTimer) {
        window.clearInterval(this.refreshTimer)
        this.refreshTimer = null
      }
    },
    async fetchFQGateSnapshot (dashboardPayload) {
      const requestId = ++this.fqgateRequestId
      const pools = Array.isArray(dashboardPayload.pools) ? dashboardPayload.pools : []
      const codes = [...new Set(pools.reduce((values, pool) => values.concat((pool.rows || []).map(row => row.code)), []).filter(Boolean))]
      if (!codes.length) return
      const controller = new AbortController()
      const timeout = window.setTimeout(() => controller.abort(), 10000)
      try {
        const response = await fetch(`${API_BASE}/api/v1/fqgate/market-snapshot`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ codes }),
          signal: controller.signal
        })
        if (!response.ok) throw new Error(`接口返回 ${response.status}`)
        const snapshot = await response.json()
        if (requestId !== this.fqgateRequestId) return
        this.fqgateMarket = snapshot
        this.applyFQGateQuotes(snapshot.quotes || {}, snapshot.capital_flows || {})
        if (
          this.selectedOpportunityBoard &&
          !this.opportunityBoards.some(board => board.board_name === this.selectedOpportunityBoard)
        ) this.selectedOpportunityBoard = ''
      } catch (error) {
        if (requestId !== this.fqgateRequestId) return
        this.fqgateMarket = {
          ...this.fqgateMarket,
          status: 'error',
          error: error.name === 'AbortError' ? '请求超时' : (error.message || 'FQGate实时聚合失败')
        }
      } finally {
        window.clearTimeout(timeout)
      }
    },
    async fetchFQGateStatus () {
      try {
        const response = await fetch(`${API_BASE}/api/v1/fqgate/status`, { headers: { Accept: 'application/json' } })
        if (!response.ok) throw new Error(`接口返回 ${response.status}`)
        this.fqgateStatus = await response.json()
        if (this.fqgateStatus.status === 'ok' || this.fqgateStatus.status === 'degraded') this.startFQGateAlerts()
      } catch (error) {
        this.fqgateStatus = { ...this.fqgateStatus, status: 'error', error: error.message || 'FQGate状态读取失败' }
      }
    },
    async fetchFQGateOverview (dashboardPayload) {
      const pools = Array.isArray(dashboardPayload.pools) ? dashboardPayload.pools : []
      const codes = [...new Set(pools.reduce((values, pool) => values.concat((pool.rows || []).map(row => row.code)), []).filter(Boolean))]
      const controller = new AbortController()
      const timeout = window.setTimeout(() => controller.abort(), 12000)
      try {
        const response = await fetch(`${API_BASE}/api/v1/fqgate/market-overview`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ codes }),
          signal: controller.signal
        })
        if (!response.ok) throw new Error(`接口返回 ${response.status}`)
        this.fqgateOverview = await response.json()
      } catch (error) {
        this.fqgateOverview = { ...this.fqgateOverview, status: 'error', errors: { dashboard: error.name === 'AbortError' ? '市场排行请求超时' : error.message || '市场概览失败' } }
      } finally {
        window.clearTimeout(timeout)
      }
    },
    startFQGateAlerts () {
      this.stopFQGateAlerts()
      if (typeof window.WebSocket === 'undefined') return
      this.fqgateAlertState = 'connecting'
      this.fqgateAlertError = ''
      const endpoint = new URL(`${API_BASE}/api/v1/fqgate/alerts`, window.location.origin)
      endpoint.protocol = endpoint.protocol === 'https:' ? 'wss:' : 'ws:'
      endpoint.searchParams.set('market', 'USZA')
      const socket = new window.WebSocket(endpoint.toString())
      this.fqgateAlertSocket = socket
      socket.onmessage = event => {
        if (this.fqgateAlertSocket !== socket) return
        let message
        try { message = JSON.parse(event.data) } catch (error) { return }
        if (message.type === 'status') {
          this.fqgateAlertState = message.state || 'subscribing'
          this.fqgateAlertError = message.message || ''
        }
        if (message.type === 'alert') {
          if (!message.event || !message.event.code || ['操作成功', 'success', 'ok'].includes(message.event.title)) return
          this.fqgateAlertState = 'connected'
          this.fqgateAlerts = [{ ...message, received_at: new Date().toISOString() }, ...this.fqgateAlerts].slice(0, 12)
        }
        if (message.type === 'error') {
          this.fqgateAlertState = 'degraded'
          this.fqgateAlertError = message.message || '原生异动流中断'
        }
      }
      socket.onerror = () => { if (this.fqgateAlertSocket === socket) this.fqgateAlertState = 'degraded' }
      socket.onclose = () => {
        if (this.fqgateAlertSocket !== socket) return
        this.fqgateAlertSocket = null
        this.fqgateAlertState = 'offline'
        this.fqgateAlertReconnectTimer = window.setTimeout(() => this.startFQGateAlerts(), 15000)
      }
    },
    stopFQGateAlerts () {
      if (this.fqgateAlertReconnectTimer) {
        window.clearTimeout(this.fqgateAlertReconnectTimer)
        this.fqgateAlertReconnectTimer = null
      }
      const socket = this.fqgateAlertSocket
      this.fqgateAlertSocket = null
      if (socket) socket.close()
      this.fqgateAlertState = 'idle'
    },
    alertText (event) {
      if (!event || typeof event !== 'object') return String(event || '短线异动')
      if (event.code) return `${event.name || ''} ${event.code} · ${event.title || '短线异动'}`.trim()
      for (const key of ['message', 'name', 'reason', 'title', 'security_name', 'code']) {
        if (event[key] !== undefined && event[key] !== '') return String(event[key])
      }
      try {
        const text = JSON.stringify(event)
        return text.length > 120 ? `${text.slice(0, 117)}...` : text
      } catch (error) { return '短线异动' }
    },
    alertTime (value) {
      const date = new Date(value)
      return Number.isNaN(date.getTime()) ? '--:--:--' : date.toLocaleTimeString('zh-CN', { timeZone: 'Asia/Shanghai', hour12: false })
    },
    applyFQGateQuotes (quotes, capitalFlows = {}) {
      if (!quotes || typeof quotes !== 'object') return
      const applyQuotesToPools = pools => (pools || []).map(pool => ({
        ...pool,
        rows: (pool.rows || []).map(row => {
          const candidate = quotes[row.code]
          // Reapplying a cached FQGate frame must not replace newer dashboard prices.
          const rowTime = this.quoteTimestamp(row.quote_updated_at)
          const candidateTime = this.quoteTimestamp(candidate && candidate.updated_at)
          const quote = candidate && !(Number.isFinite(rowTime) && Number.isFinite(candidateTime) && candidateTime < rowTime) ? candidate : null
          const changePct = this.liveChangePct(quote, row)
          const updated = quote
            ? {
                ...row,
                price: this.validLivePrice(quote.price) ? quote.price : row.price,
                // A malformed FQGate prior-close field must not overwrite a
                // valid daily snapshot and turn the UI into +236250%.
                change_pct: changePct,
                pre_close: this.validLivePreClose(quote, row.code) ? quote.pre_close : row.pre_close,
                open: quote.open,
                high: quote.high,
                low: quote.low,
                quote_updated_at: quote.updated_at,
                quote_status: 'live',
                quote_source: 'fqgate',
                speed_1m_pct: quote.speed_1m_pct == null ? null : quote.speed_1m_pct,
                speed_1m_status: quote.speed_1m_status || 'missing',
                speed_1m_updated_at: quote.speed_1m_updated_at,
                speed_1m_base_at: quote.speed_1m_base_at,
                speed_1m_window_seconds: quote.speed_1m_window_seconds,
                speed_1m_price: quote.speed_1m_price,
                speed_1m_source: quote.speed_1m_source
              }
            : row
          return this.applyLiveCapital(updated, capitalFlows[row.code], quote)
        })
      }))
      const pools = applyQuotesToPools(this.rawPools)
      const workPools = applyQuotesToPools(this.workPools)
      this.dashboard = {
        ...this.dashboard,
        pools,
        work_pools: workPools,
        source_status: {
          ...(this.dashboard.source_status || {}),
          fqgate_quotes: Object.keys(quotes).length ? 'ok' : 'missing',
          fqgate_quote_count: Object.keys(quotes).length,
          fqgate_quote_updated_at: this.fqgateMarket.updated_at || ''
        }
      }
      if (this.selectedRow) {
        if (this.viewMode === 'research') {
          this.selectedRow = this.rows.find(row => row.code === this.selectedRow.code) || null
          return
        }
        const refreshed = this.pools
          .find(pool => pool.id === (this.selectedRow.work_pool_id || this.selectedRow.pool_id))
        const row = refreshed && (refreshed.rows || []).find(item => item.code === this.selectedRow.code)
        if (row) this.selectedRow = row
      }
    },
    applyLiveCapital (row, flow, quote) {
      if (!flow || !flow.available || !flow.fresh || flow.main_net_inflow_yuan === null || flow.main_net_inflow_yuan === undefined) return row
      const amount = Number(flow.main_net_inflow_yuan)
      if (!Number.isFinite(amount)) return row
      const old = row.stock_capital_flow || {}
      const completePair = value => value && value.buy_amount_yuan !== null && value.buy_amount_yuan !== undefined && value.sell_amount_yuan !== null && value.sell_amount_yuan !== undefined
      const priorPair = old.gross_flow || old
      const pair = completePair(flow) ? flow : (this.capitalFlowCurrent(priorPair) && completePair(priorPair) ? priorPair : {})
      const gross = completePair(pair) ? Number(pair.buy_amount_yuan) + Number(pair.sell_amount_yuan) : null
      const price = Number(row.price)
      const previousValue = Number(row.float_market_value_yuan)
      const shares = Number(row.capital_float_shares)
      const floatValue = shares > 0 && price > 0 ? shares * price : previousValue
      const turnover = Number((quote && (quote.amount_yuan || quote.amount)) || row.capital_turnover_yuan)
      const ratio = flow.main_net_inflow_ratio_pct !== null && flow.main_net_inflow_ratio_pct !== undefined
        ? Number(flow.main_net_inflow_ratio_pct) : (turnover > 0 ? amount / turnover * 100 : null)
      const floatNet = floatValue > 0 ? amount / floatValue * 100 : null
      const participation = gross !== null && turnover > 0 ? gross / turnover * 100 : null
      const occupancy = gross !== null && floatValue > 0 ? gross / floatValue * 100 : null
      const source = item => ({ source: item.source || '', captured_at: item.captured_at || '' })
      const label = amount < 0 ? '主力撤退' : floatNet >= 0.3 && amount > 0 ? '资金强攻'
        : participation >= 15 && Math.abs(ratio) < 10 ? '主力博弈' : participation >= 15 && amount > 0 ? '主力主导' : amount > 0 ? '资金流入' : '资金中性'
      return {
        ...row,
        stock_capital_flow: { ...flow, gross_flow: pair, kaipanla_snapshot: this.kaipanlaSnapshot(row) || undefined },
        main_net_inflow_yuan: amount,
        main_net_inflow_ratio_pct: ratio,
        float_net_inflow_pct: floatNet,
        main_net_intensity_pct: ratio,
        main_participation_pct: participation,
        main_occupancy_pct: occupancy,
        capital_metric_fresh: true,
        capital_metric_source: flow.source,
        capital_turnover_yuan: turnover > 0 ? turnover : null,
        capital_metric_sources: { float: source(flow), intensity: source(flow), participation: source(pair), occupancy: source(pair) },
        float_net_inflow_sort_pct: floatNet,
        main_participation_sort_pct: amount > 0 ? participation : null,
        main_occupancy_sort_pct: amount > 0 ? occupancy : null,
        capital_flow_badge: { label, tone: amount < 0 ? 'risk' : amount > 0 ? 'positive' : 'neutral' }
      }
    },
    capitalSourceText (row) {
      if (!row) return ''
      const sources = row.capital_metric_sources || {}
      const values = Object.values(sources).filter(item => item.source).map(item => `${this.l2SourceName(item.source)} ${item.captured_at || ''}`)
      return [...new Set(values)].join(' · ') || this.l2SourceName(row.capital_metric_source)
    },
    capitalFlowCurrent (flow) {
      if (!flow || !flow.fresh) return false
      if (!String(flow.source || '').match(/^(dat|fqgate|tq_tdx)/)) return true
      const stamp = String(flow.captured_at || '').replace(' ', 'T')
      const time = Date.parse(/(?:Z|[+-]\d{2}:\d{2})$/.test(stamp) ? stamp : `${stamp}+08:00`)
      const age = (Date.now() - time) / 1000
      const chinaNow = new Date(Date.now() + 8 * 3600000).toISOString()
      const date = String(flow.trade_date || '').replace(/-/g, '')
      const sameDate = date === chinaNow.slice(0, 10).replace(/-/g, '')
      const duringSession = chinaNow.slice(11, 16) >= '09:15' && chinaNow.slice(11, 16) <= '15:05'
      return sameDate && Number.isFinite(age) && age >= -5 && (!duringSession || age <= 180)
    },
    selectPool (poolId) {
      this.activePoolId = poolId
      if (this.viewMode === 'research' && poolId === 'stage_model') this.filtersExpanded = true
      if (poolId === 'bear_overnight' && this.viewMode !== 'research') {
        this.sortKey = 'score'
        this.sortDirection = 'desc'
      }
    },
    selectRow (row) {
      this.marketPanelOpen = false
      if (row && this.detailPanelOpen && this.selectedRow && this.selectedRow.code === row.code) {
        this.selectedRow = row
        return
      }
      this.l2LegacyRoute = false
      this.selectedRow = row
      this.detailPanelOpen = Boolean(row)
      this.stageTimeline = { cycles: [] }
      if (this.viewMode === 'research' && row && row.pool_id === 'stage_model') this.fetchStageTimeline(row.code)
      this.startL2Watch(row)
    },
    closeDetailPanel () {
      this.detailPanelOpen = false
      this.marketPanelOpen = false
      this.stopL2Watch()
    },
    openMarketPanel () {
      this.detailPanelOpen = false
      this.stopL2Watch()
      this.marketPanelOpen = true
      this.$nextTick(() => {
        if (this.$refs.insightPanel) this.$refs.insightPanel.scrollTop = 0
      })
    },
    openAlertStock (alert) {
      const row = this.rows.find(item => item.code === alert.code)
      if (row) {
        this.selectRow(row)
        return
      }
      this.searchText = alert.code || ''
      this.closeDetailPanel()
    },
    trapPanelFocus (event) {
      const panel = this.$refs.insightPanel
      if (!panel) return
      const items = [...panel.querySelectorAll('button, input, select, summary, a[href], [tabindex="0"]')].filter(el => !el.disabled && el.getClientRects().length)
      const first = items[0]
      const last = items[items.length - 1]
      if (!first) { event.preventDefault(); return }
      if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && (document.activeElement === last || document.activeElement === panel)) {
        event.preventDefault()
        first.focus()
      }
    },
    handleGlobalKeydown (event) {
      if (event.key === 'Escape') this.closeDetailPanel()
    },
    startL2Watch (row) {
      this.stopL2Watch()
      if (!row || !row.code) return
      this.fetchDatPoints()
      this.l2Watch = {
        state: 'connecting',
        factor: row.l2_factor || {},
        quote: {},
        streams: {},
        events: [],
        error: ''
      }
      this.refreshDetailSnapshot(row.code)
      this.l2SnapshotTimer = window.setInterval(() => this.refreshDetailSnapshot(row.code), 10000)
      if (typeof window.WebSocket === 'undefined') return
      const watchPath = this.l2LegacyRoute ? 'fqgate/watch' : 'dat/watch'
      const endpoint = new URL(`${API_BASE}/api/v1/${watchPath}/${encodeURIComponent(row.code)}`, window.location.origin)
      endpoint.protocol = endpoint.protocol === 'https:' ? 'wss:' : 'ws:'
      endpoint.searchParams.set('stage', row.stage || '')
      const socket = new window.WebSocket(endpoint.toString())
      this.l2Socket = socket
      let receivedMessage = false
      socket.onmessage = event => {
        if (this.l2Socket !== socket) return
        let message
        try {
          message = JSON.parse(event.data)
          receivedMessage = true
        } catch (error) {
          return
        }
        if (message.type === 'stream_status') {
          this.$set(this.l2Watch.streams, message.kind, message.state)
          this.l2Watch.state = 'live'
        } else if (message.type === 'factor') {
          this.acceptDetailFactor(message.factor || {}, message.status)
        } else if (message.type === 'capital') {
          this.applyFQGateQuotes({}, message.capital_flows || {})
        } else if (message.type === 'points') {
          this.datPoints = message.points || this.datPoints
        } else if (message.type === 'quote') {
          this.l2Watch.quote = message.quote || {}
          this.l2Watch.state = 'live'
        } else if (message.type === 'event') {
          this.l2Watch.events = [{ kind: message.kind, event: message.event }, ...this.l2Watch.events].slice(0, 8)
        } else if (message.type === 'error') {
          if (message.kind) this.$set(this.l2Watch.streams, message.kind, 'error')
          this.l2Watch.error = `${message.source ? this.l2SourceName(message.source) + ' · ' : ''}${message.kind ? message.kind + '：' : ''}${message.message || 'L2流异常'}`
          this.l2Watch.state = Object.values(this.l2Watch.streams).includes('connected') ? 'degraded' : 'offline'
        }
      }
      socket.onerror = () => {
        if (this.l2Socket === socket) this.l2Watch.error = '融合L2连接失败；继续读取快照'
      }
      socket.onclose = () => {
        if (this.l2Socket !== socket) return
        this.l2Socket = null
        if (!receivedMessage) this.l2LegacyRoute = true
        if (!this.detailPanelOpen || !this.selectedRow || this.selectedRow.code !== row.code) return
        this.l2Watch.state = 'offline'
        this.l2ReconnectTimer = window.setTimeout(() => this.startL2Watch(this.selectedRow), 5000)
      }
    },
    stopL2Watch () {
      this.l2SnapshotRequestId += 1
      if (this.l2SnapshotTimer) window.clearInterval(this.l2SnapshotTimer)
      this.l2SnapshotTimer = null
      if (this.l2ReconnectTimer) {
        window.clearTimeout(this.l2ReconnectTimer)
        this.l2ReconnectTimer = null
      }
      const socket = this.l2Socket
      this.l2Socket = null
      if (socket) socket.close()
      this.l2Watch = { state: 'idle', factor: {}, quote: {}, streams: {}, events: [], error: '' }
    },
    async refreshDetailSnapshot (code) {
      const requestId = ++this.l2SnapshotRequestId
      try {
        const { payload, path } = await this.detailApiGet(['dat/detail', 'fqgate/detail', 'fqgate/l2', 'dat/l2'].map(path => `${path}/${encodeURIComponent(code)}`))
        if (requestId !== this.l2SnapshotRequestId || !this.selectedRow || this.selectedRow.code !== code || !this.detailPanelOpen) return
        if (payload.factor) {
          this.acceptDetailFactor(payload.factor, payload.status)
        }
        const currentApi = payload.api_version === 'detail-20261013.1'
        const routeNote = currentApi && path.startsWith('fqgate/') ? '已通过兼容路由连接；请补齐 DAT 反向代理' : !currentApi ? '后端仍为旧版，积分采样和优先观察需同步更新后端并重启' : ''
        this.l2Watch.error = [routeNote, ...(payload.errors || [])].filter(Boolean).join('；')
        this.$set(this.l2Watch, 'd202Message', (payload.d202_request || {}).message || '')
        if (payload.paid_samples) this.$set(this.l2PaidSamples, code, payload.paid_samples)
        if (payload.capital_flows) this.applyFQGateQuotes({}, payload.capital_flows)
        if ((payload.tq || {}).book) this.$set(this.l2Watch, 'tqBook', payload.tq.book)
        else if (this.l2Watch.tqBook) this.$delete(this.l2Watch, 'tqBook')
      } catch (error) {
        if (requestId === this.l2SnapshotRequestId) this.l2Watch.error = error.message
      }
    },
    acceptDetailFactor (factor, status) {
      const current = this.l2Watch.factor || {}
      const stamp = String(current.updated_at || '').replace(' ', 'T')
      const age = (Date.now() - Date.parse(/(?:Z|[+-]\d{2}:\d{2})$/.test(stamp) ? stamp : `${stamp}+08:00`)) / 1000
      const currentRecent = age >= 0 && age <= 90 && !current.review_snapshot && current.data_freshness !== 'stale'
      const older = currentRecent && String(factor.updated_at || '') < String(current.updated_at || '')
      const merged = { ...(older ? current : factor) }
      const sources = { ...(merged.metric_sources || {}) }
      const groups = { book: ['book_imbalance', 'bid_amount', 'ask_amount'], active: ['active_buy_ratio', 'active_buy_amount', 'active_sell_amount'], queue: ['queue_imbalance', 'buy_queue_amount', 'sell_queue_amount'], cancel: ['cancellation_imbalance', 'buy_cancel_amount', 'sell_cancel_amount'], capital_flow: ['capital_flow', 'named_net_flow_yuan'], big_order_flow: ['big_order_flow'] }
      // Keep a newly received supplement when an ordinary watch frame arrives.
      const complement = older ? factor : current
      Object.entries(groups).forEach(([group, fields]) => {
        const info = (complement.metric_sources || {})[group]
        if (!info) return
        const timestamp = String(info.updated_at || '').replace(' ', 'T')
        const metricAge = (Date.now() - Date.parse(/(?:Z|[+-]\d{2}:\d{2})$/.test(timestamp) ? timestamp : `${timestamp}+08:00`)) / 1000
        if (metricAge < 0 || metricAge > 90 || !Number.isFinite(metricAge) || ['stale', 'historical'].includes(info.data_freshness)) return
        if (sources[group] && String(sources[group].updated_at || '') >= String(info.updated_at || '')) return
        fields.forEach(field => { merged[field] = complement[field] })
        sources[group] = info
      })
      merged.metric_sources = sources
      this.l2Watch.factor = merged
      this.l2Watch.state = status === 'ok' ? 'live' : status || 'unknown'
    },
    async samplePaidL2 (channel) {
      if (!this.selectedRow || !this.datPoints.paid_available || this.l2PaidPending) return
      const code = this.selectedRow.code
      const requestId = window.crypto && window.crypto.randomUUID ? window.crypto.randomUUID() : `sample-${Date.now()}-${Math.random().toString(36).slice(2)}`
      this.l2PaidPending = channel
      const samples = { ...(this.l2PaidSamples[code] || {}) }
      const running = { code, channel, request_id: requestId, status: 'running', phase: 'checking' }
      const controller = new window.AbortController()
      let timeout
      this.$set(this.l2PaidSamples, code, { ...samples, [channel]: running })
      try {
        // Resolve the route using GET. A paid POST is sent exactly once.
        const { path } = await this.detailApiGet([`dat/sample/${code}`, `fqgate/detail-sample/${code}`])
        timeout = window.setTimeout(() => controller.abort(), 20000)
        const response = await fetch(`${API_BASE}/api/v1/${path}`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ channel, request_id: requestId }), signal: controller.signal })
        if ((response.headers.get('content-type') || '').includes('text/html')) throw new Error('采样请求返回网页；请检查本次状态后再操作')
        const payload = await response.json()
        if (!response.ok) throw new Error(payload.detail || `采样返回 ${response.status}`)
        this.$set(this.l2PaidSamples, code, { ...samples, [channel]: payload.sample })
        if (payload.points) this.datPoints = payload.points
        if (this.selectedRow && this.selectedRow.code === code && this.detailPanelOpen) {
          this.acceptDetailFactor(payload.factor || {}, payload.status)
          this.l2Watch.error = payload.sample.error || ''
        }
      } catch (error) {
        this.$set(this.l2PaidSamples, code, { ...samples, [channel]: { ...running, status: 'unknown', error: error.message || '采样结果未确认；不会自动重试' } })
        try {
          const result = await this.detailApiGet([`dat/sample/${code}`, `fqgate/detail-sample/${code}`])
          if (result.payload.samples[channel]) this.$set(this.l2PaidSamples, code, result.payload.samples)
        } catch (ignored) { /* Preserve the unresolved request; never retry a paid POST. */ }
      } finally {
        window.clearTimeout(timeout)
        this.l2PaidPending = false
        this.fetchDatPoints()
      }
    },
    hasMetric (value) {
      return value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value))
    },
    paidSampleText (item) {
      const labels = { running: '采样处理中', ok: '已收到数据', error: '采样失败', interrupted: '采样中断', unknown: '执行结果待核对' }
      const bytes = item.received_bytes === undefined ? '' : ` · 接收 ${item.received_bytes} 字节`
      const sent = item.commands_sent && item.commands_sent.length ? ' · 已发送订阅' : item.status === 'error' ? ' · 未发送订阅' : ''
      const debit = item.observed_debit_points === undefined || item.observed_debit_points === null ? '' : ` · 观察到扣减 ${Number(item.observed_debit_points).toFixed(3)} 积分`
      return `${item.channel.toUpperCase()}：${labels[item.status] || item.status}${sent}${bytes}${debit}${item.error ? ' · ' + item.error : ''}`
    },
    l2SourceName (source) {
      const labels = { kaipanla_main: '开盘啦', fqgate_l2: 'FQGate', fqgate_live: 'FQGate', dat_d202: 'DAT D202', dat_d203: 'DAT D203', dat_d204: 'DAT D204', dat_d4: 'DAT D4', dat_d6: 'DAT D6', tq_tdx: 'TQ TDX' }
      return labels[source] || source || '待采集'
    },
    supportMetricSource (group) {
      const item = (this.selectedL2Factor.metric_sources || {})[group] || {}
      return `${this.l2SourceName(item.source)} · ${item.updated_at || '无更新时间'} · ${item.data_freshness || '时效待确认'}`
    },
    supportMoneyText (value) {
      if (value === null || value === undefined || value === '') return '--'
      const number = Number(value)
      if (!Number.isFinite(number)) return '--'
      return `${number > 0 ? '+' : ''}${Math.abs(number) >= 100000000 ? `${(number / 100000000).toFixed(2)}亿` : `${(number / 10000).toFixed(0)}万`}`
    },
    supportScoreText (value) {
      if (!Object.keys(this.selectedL2Factor).length || value === null || value === undefined) return '--'
      if (this.selectedL2Factor.coverage !== undefined && Number(this.selectedL2Factor.coverage) <= 0) return '--'
      return Number.isFinite(Number(value)) ? Number(value).toFixed(1) : '--'
    },
    intradayQualityText (value) {
      return {
        full: '行情+完整L2',
        partial_l2: '行情+部分L2',
        l2_observed: '部分L2观察',
        partial_available: '部分数据可用',
        quote_only: '仅行情'
      }[String(value || '')] || '形态'
    },
    ratioText (value, signed) {
      if (value === null || value === undefined || value === '') return '--'
      const number = Number(value)
      if (!Number.isFinite(number)) return '--'
      return `${signed && number > 0 ? '+' : ''}${(number * 100).toFixed(1)}%`
    },
    selectOpportunity (stock) {
      const pool = this.pools.find(item => item.id === stock.pool_id) || this.pools.find(item => (item.rows || []).some(row => row.code === stock.code))
      const row = pool && (pool.rows || []).find(item => item.code === stock.code)
      if (!row) return
      this.activePoolId = pool.id
      this.$nextTick(() => this.selectRow(row))
    },
    selectOpportunityBoard (boardName) {
      const value = String(boardName || '')
      this.selectedOpportunityBoard = this.selectedOpportunityBoard === value ? '' : value
    },
    opportunityStateClass (state) {
      if (state === '强共振机会' || state === 'L2确认') return 'opportunity-strong'
      if (state === '重点观察') return 'opportunity-focus'
      if (state === '机会失效' || state === 'L2冲突') return 'opportunity-invalid'
      return 'opportunity-wait'
    },
    async fetchStageTimeline (code) {
      const requestId = ++this.stageTimelineRequestId
      this.stageTimelineLoading = true
      this.stageTimeline = { cycles: [] }
      try {
        const response = await fetch(`${API_BASE}/api/v1/pools/stage-timeline/${encodeURIComponent(code)}`, {
          headers: { Accept: 'application/json' }
        })
        if (!response.ok) throw new Error(`接口返回 ${response.status}`)
        const payload = await response.json()
        if (requestId === this.stageTimelineRequestId && this.selectedRow && this.selectedRow.code === code) {
          this.stageTimeline = payload
        }
      } catch (error) {
        if (requestId === this.stageTimelineRequestId) this.stageTimeline = { cycles: [], error: error.message || '阶段时间轴读取失败' }
      } finally {
        if (requestId === this.stageTimelineRequestId) this.stageTimelineLoading = false
      }
    },
    activeMaCrossSignals (row) {
      const signals = row && row.ma_cross_signals
      if (!signals || typeof signals !== 'object') return []
      return Object.values(signals).filter(signal => {
        if (!signal || !signal.active) return false
        if (['daily_reclaim', 'intraday_reclaim'].includes(signal.confirmation_type)) {
          return Number(signal.slow_ma) > 0 && Number(signal.fast_ma) > Number(signal.slow_ma)
        }
        return true
      })
    },
    tradeConfirmationTags (row) {
      const flow = row && row.stock_capital_flow && typeof row.stock_capital_flow === 'object'
        ? row.stock_capital_flow
        : {}
      const boardPassed = row && row.board_flow_confirmed === true
      const flowFresh = flow.available === true && flow.fresh === true
      const capitalPassed = row && row.capital_attack_confirmed === true
      const supportPassed = row && row.support_passed === true
      const supportPending = !row || ['missing', 'stale', 'low', 'medium'].includes(row.support_confidence)
      const crossCount = Number((row && row.golden_cross_count) || 0)
      const signals = row && row.ma_cross_signals && typeof row.ma_cross_signals === 'object'
        ? row.ma_cross_signals
        : {}
      let crossLabel = '金叉待补'
      if (crossCount >= 2) crossLabel = '金叉13/17'
      else if (crossCount === 1) {
        crossLabel = signals.golden_cross_1 && signals.golden_cross_1.active ? '金叉13' : '金叉17'
      } else if (row && row.golden_cross_available) crossLabel = '金叉未成'
      return [
        {
          key: 'board',
          label: boardPassed ? '板块流入' : ((row && row.board_direction) === 'outflow' ? '板块流出' : '板块待补'),
          passed: boardPassed,
          pending: !boardPassed && (!row || !row.board_direction || row.board_direction === 'unconfirmed'),
          failed: !boardPassed && row && row.board_direction === 'outflow'
        },
        {
          key: 'capital',
          label: capitalPassed ? '资金攻进' : (flowFresh ? (row.stock_inflow_confirmed ? '流入偏弱' : '资金未进') : '资金待补'),
          passed: capitalPassed,
          pending: !capitalPassed && !flowFresh,
          failed: !capitalPassed && flowFresh
        },
        {
          key: 'support',
          label: supportPassed ? (row.support_recovery ? 'L2转强' : 'L2承接') : (supportPending ? 'L2待补' : 'L2未确认'),
          passed: supportPassed,
          pending: !supportPassed && supportPending,
          failed: !supportPassed && !supportPending
        },
        {
          key: 'cross',
          label: crossLabel,
          passed: crossCount > 0,
          pending: crossCount === 0 && !(row && row.golden_cross_available),
          failed: crossCount === 0 && row && row.golden_cross_available
        }
      ]
    },
    capitalFlowText (row) {
      const flow = row && row.stock_capital_flow && typeof row.stock_capital_flow === 'object'
        ? row.stock_capital_flow
        : {}
      if (!flow.available) return '等待数据'
      if (flow.snapshot) {
        const amount = Number(flow.main_net_inflow_yuan)
        if (!Number.isFinite(amount)) return '等待数据'
        const text = Math.abs(amount) >= 100000000
          ? `${(amount / 100000000).toFixed(2)}亿` : `${(amount / 10000).toFixed(0)}万`
        return `${amount >= 0 ? '+' : ''}${text} · ${this.kaipanlaSnapshotStale(flow) ? '快照过期' : this.kaipanlaChangeCurrent(flow) ? '变动观察' : '快照观察'} · ${this.l2SourceName(flow.source)}`
      }
      if (!flow.fresh && flow.date_quality !== 'unknown') return flow.stale ? '数据过期' : '等待数据'
      const amount = Number(flow.main_net_inflow_yuan || 0)
      const ratio = Number(flow.main_net_inflow_ratio_pct || 0)
      const amountText = Math.abs(amount) >= 100000000
        ? `${amount >= 0 ? '+' : '-'}${(Math.abs(amount) / 100000000).toFixed(2)}亿`
        : `${amount >= 0 ? '+' : '-'}${(Math.abs(amount) / 10000).toFixed(0)}万`
      const ratioText = flow.main_net_inflow_ratio_pct === null || flow.main_net_inflow_ratio_pct === undefined
        ? '' : ` · ${ratio >= 0 ? '+' : ''}${ratio.toFixed(1)}%`
      return `${amountText}${ratioText}${flow.fresh ? '' : ' · 日期待核'} · ${this.l2SourceName(flow.source)}`
    },
    maCrossClass (signal) {
      if (!signal || !signal.active) return 'ma-cross-pending'
      if (signal && signal.intraday_triggered) return 'ma-cross-intraday'
      if (signal && signal.confirmation_type === 'daily_reclaim') return 'ma-cross-confirmed'
      return 'ma-cross-active'
    },
    maCrossStateText (signal) {
      if (!signal) return '状态未知'
      return signal.state || '未触发'
    },
    maCrossDetailText (signal) {
      if (!signal) return '数据不足'
      const dateText = signal.reference_trade_date ? `${signal.reference_trade_date} · ` : ''
      if (signal.confirmation_type === 'daily_bullish') {
        const relationship = Number(signal.fast_ma) > Number(signal.slow_ma) ? '>' : '≤'
        return `${dateText}MA${signal.fast_period} ${this.priceText(signal.fast_ma)} ${relationship} MA55 ${this.priceText(signal.slow_ma)}${signal.newly_crossed ? ' · 当日上穿' : signal.active ? ' · 多头延续' : ' · 未形成多头'}`
      }
      const band = Number(signal.band_lower) > 0 && Number(signal.band_upper) > 0
        ? ` · 回踩容差 ${signal.reclaim_lower_multiplier}～${signal.reclaim_upper_multiplier} 倍（${this.priceText(signal.band_lower)}～${this.priceText(signal.band_upper)}）`
        : ''
      const relationship = Number(signal.fast_ma) > Number(signal.slow_ma) ? '>' : '≤'
      return `${dateText}MA${signal.fast_period} ${this.priceText(signal.line_ma || signal.fast_ma)} ${relationship} MA55 ${this.priceText(signal.slow_ma)}${band} · 低点 ${this.priceText(signal.session_low)} · ${signal.confirmation_type === 'daily_reclaim' ? '收盘' : '当前'} ${this.priceText(signal.current_price)}${signal.reason ? ` · ${signal.reason}` : ''}`
    },
    channelDirectionTitle (row) {
      const detail = row && row.channel_direction_detail
      if (!detail || typeof detail !== 'object') return '通道方向数据'
      const slope = Number(detail.slope_pct)
      const slopeText = Number.isFinite(slope) ? `${slope > 0 ? '+' : ''}${slope.toFixed(2)}%` : '--'
      const model = detail.source === 'tdx_formula_endpoint' ? `通达信公式${detail.price_basis || '日线端点'}` : 'MA10近似通道'
      return `${model}中轨${detail.lookback_bars || 5}日变化 ${slopeText} · ${detail.compare_trade_date || '--'} → ${detail.trade_date || '--'}`
    },
    poolDisplayCount (pool) {
      return new Set((pool.rows || []).map(row => row.code).filter(Boolean)).size
    },
    tradePoolDescription (pool) {
      const selected = this.poolDisplayCount(pool)
      const source = Number(pool.source_total_count || pool.total_count || selected)
      const snapshot = this.poolSnapshotCaption(pool)
      return `${pool.description || '交易精选'} · 精选${selected}/${source} · ${snapshot}`
    },
    poolSnapshotCaption (pool) {
      if (!pool) return '暂无股池状态'
      const status = String(pool.generation_status || '').toLowerCase()
      if (status === 'waiting') return `等待生成 · ${pool.generation_message || '数据尚未就绪'}`
      const generated = pool.snapshot_at ? `生成 ${this.formatSnapshot(pool.snapshot_at)}` : '未生成'
      const tradeDate = pool.trade_date ? ` · 数据 ${pool.trade_date}` : ''
      if (status === 'partial') return `${generated}${tradeDate} · ${pool.generation_message || '盘中数据覆盖不足'}`
      return `${generated}${tradeDate}${pool.stale ? ' · 过期' : ''}`
    },
    tradeScoreText (row) {
      const value = this.viewMode === 'trade'
        ? Number(row && (row.decision_score !== undefined ? row.decision_score : row.score))
        : Number(row && row.score)
      return this.scoreText(Number.isFinite(value) ? value : 0)
    },
    poolScoreCaption (row) {
      if ((row.limit_pullback_repair_detail || {}).structure_only) return '仅结构预筛 · 未进入回踩观察'
      if (row && row.buy_score_status === '已校准' && row.buy_score != null) {
        const ret = row.expected_return_pct == null ? '' : ` · T+3 ${this.signedPercent(row.expected_return_pct)}`
        return `样本外校准分${ret}`
      }
      const label = row && row.pool_label ? `${row.pool_label} · ` : ''
      return `${label}形态分/未验证，不代表胜率`
    },
    poolDisplayScore (row) {
      if (!row) return '--'
      if ((row.limit_pullback_repair_detail || {}).structure_only) return '--'
      if (row.buy_score_status === '已校准' && row.buy_score != null) return row.buy_score
      const value = Number(row.score_display != null ? row.score_display : row.score)
      return Number.isFinite(value) ? Math.max(0, Math.min(100, value)) : '--'
    },
    dedupePoolRows (rows) {
      const selected = new Map()
      rows.forEach(row => {
        const previous = selected.get(row.code)
        if (!previous || this.rowStagePriority(row) > this.rowStagePriority(previous)) {
          selected.set(row.code, row)
        }
      })
      return Array.from(selected.values())
    },
    repairStageLabel (row) {
      const detail = row.limit_pullback_repair_detail || {}
      if (detail.structure_only || row.stage === '强势结构') return '强势结构'
      if (detail.signal_confirmed === true && detail.scan_mode !== 'daily_close') return '盘中确认'
      return '回踩观察'
    },
    dedupeResearchRows (pools) {
      const grouped = new Map()
      pools.forEach(pool => {
        const rows = pool.rows || []
        rows.forEach((row, index) => {
          const code = String(row.code || '').trim()
          const key = code || `${pool.id}-${index}`
          const candidate = { ...row, code, pool_id: row.pool_id || pool.id, pool_label: row.pool_label || pool.label || pool.short_label }
          if (!grouped.has(key)) grouped.set(key, { row: candidate, memberships: new Map(), boards: new Map() })
          const group = grouped.get(key)
          // Keep one complete signal row for prices, scores, L2 and details.
          // Only membership and display metadata are combined across pools.
          if (this.rowStagePriority(candidate) > this.rowStagePriority(group.row)) group.row = candidate
          const poolId = pool.id || candidate.pool_id
          if (!group.memberships.has(poolId)) {
            group.memberships.set(poolId, { id: poolId, label: pool.short_label || pool.label || candidate.pool_label || poolId, stages: [] })
          }
          const membership = group.memberships.get(poolId)
          this.researchStageLabels(candidate).forEach(label => {
            if (!membership.stages.includes(label)) membership.stages.push(label)
          })
          this.rowBoardEntries(row).forEach(entry => group.boards.set(`${entry.type}::${entry.name}`, entry))
        })
      })
      return Array.from(grouped.values()).map(group => ({
        ...group.row,
        pool_memberships: Array.from(group.memberships.values()),
        research_boards: Array.from(group.boards.values())
      }))
    },
    researchStageLabels (row) {
      const rawStage = row.candidate_stage_label || row.stage
      const canonicalStage = rawStage === '通达信操' ? '操' : rawStage
      if (row.channel_provisional) return [...new Set([canonicalStage,
        row.channel_candidate_basis === 'cao' ? '盘中操信号' : '盘中底部候选',
        row.tdx_channel_cao ? '操' : '', '待收盘确认'].filter(Boolean))]
      const labels = [canonicalStage, row.signal_tier]
      if (row.pool_id === 'limit_pullback_repair' && row.limit_pullback_repair_detail) {
        const detail = row.limit_pullback_repair_detail
        if (detail.strength_label) labels.push(`${detail.strength_label}后第${detail.sessions_after_streak}日`)
        if (detail.support_kind) labels.push(detail.support_kind)
        if ((detail.floor || {}).label) labels.push(detail.floor.label)
        if ((detail.peers || {}).best) labels.push(`${detail.peers.best.group}样本${detail.peers.best.rank}/${detail.peers.best.count}`)
        if ((detail.chips || {}).label) labels.push(detail.chips.label)
        if (detail.structure_only) labels.push(...(detail.rejection_reasons || []).slice(0, 2))
        else if (!(detail.intraday || {}).complete) labels.push('分时待补')
      }
      if (row.pool_id === 'macd_bear_rebound' && row.macd_bear_rebound_detail) {
        const shrink = Number(row.macd_bear_rebound_detail.shrink_pct)
        const volumeRatio = Number(row.macd_bear_rebound_detail.volume_ratio_to_previous)
        if (Number.isFinite(shrink)) labels.push(`峰后首缩${shrink.toFixed(2)}%${Number.isFinite(volumeRatio) ? `，放量${volumeRatio.toFixed(2)}倍` : ''}`)
      }
      if (row.tracking_status && row.tracking_status !== '当前阶段确认') labels.push(row.tracking_status)
      if (row.bullish_oversold_confirmation_ready) labels.push('承接确认')
      if (row.pool_id === 'stage_model') {
        STAGE_MODEL_ROW_LABELS.forEach(label => {
          if (this.matchesResearchLabel(row, label)) labels.push(label)
        })
      }
      return [...new Set(labels.filter(label => label && label !== '未分组'))]
    },
    researchPoolText (row) {
      return (row.pool_memberships || []).map(pool => pool.label).join('、') || row.pool_label || ''
    },
    researchStageText (row) {
      const memberships = row.pool_memberships || []
      return memberships.length
        ? memberships.filter(pool => pool.stages.length).map(pool => `${memberships.length > 1 ? `${pool.label}：` : ''}${pool.stages.join('、')}`).join(' · ')
        : this.researchStageLabels(row).join('、')
    },
    rowStagePriority (row) {
      if (row.pool_id === 'limit_pullback_repair') {
        return { '强势结构': 1, '回踩观察': 2, '盘中确认': 3 }[this.repairStageLabel(row)]
      }
      const stage = String(row.stage || '')
      if (/已封首板|炸板|未封/.test(stage)) return 4
      if (/承接|复筛|确认/.test(stage)) return 3
      if (/初筛|开盘|竞价/.test(stage)) return 2
      return 1
    },
    sortBy (key) {
      if (this.sortKey === key) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
        this.resetVisibleRows()
        return
      }
      this.sortKey = key
      this.sortDirection = 'desc'
      this.resetVisibleRows()
    },
    sortIndicator (key) {
      if (this.sortKey !== key) return '↕'
      return this.sortDirection === 'asc' ? '↑' : '↓'
    },
    matchesResearchLabel (row, stage) {
      if (!row || !stage) return false
      const stageAliases = {
        '当前行动池': '短周期跟踪',
        '优先关注层': '优先关注',
        '通道未滚': '未出通道',
        '筹码近峰': '靠近主成本区',
        '筹码趋势T5': 'T+5观察条件',
        '通达信操': '操'
      }
      stage = stageAliases[stage] || stage
      if (row.pool_id === 'bullish_oversold') {
        if (['A级', 'B级', 'C级'].includes(stage)) return row.signal_tier === stage
        if (['今日新增', '跟踪中', '承接确认', '已失效', '已完成'].includes(stage)) {
          return row.stage === stage || row.tracking_status === stage
        }
      }
      if (row.pool_id === 'first_board_discovery' && ['A级', 'B级', 'C级'].includes(stage)) {
        return row.signal_tier === stage
      }
      if (!STAGE_MODEL_ROW_LABELS.includes(stage)) return row.stage === stage
      if (row.pool_id !== 'stage_model') return false
      if (stage === '吸筹候选') return ['建仓吸筹', '吸筹候选'].includes(row.stage) || row.candidate_stage_label === stage
      if (stage === '洗盘候选') return ['洗盘企稳', '洗盘候选'].includes(row.stage) || row.candidate_stage_label === stage
      if (stage === '操') return row.tdx_channel_cao === true || row.stage === '通达信操' || row.candidate_stage_label === '通达信操'
      if (['试盘突破', '出货风险'].includes(stage)) return row.stage === stage
      if (stage === '通道底部') {
        const bottom = row.channel_bottom || {}
        return bottom.matched === true && ['close', 'current'].includes(bottom.match_basis)
      }
      if (stage === '短周期跟踪') return row.observation_tier === '当前行动池'
      if (stage === '长周期观察池') return row.observation_tier === '长周期观察池'
      if (stage === '未出通道') return row.tdx_channel_not_gun === true
      if (stage === '靠近主成本区') return row.near_chip_peak === true
      if (stage === '筹码持续收敛') return row.chip_converging === true
      if (stage === '筹码重新发散') return row.chip_expanding === true
      if (stage === '收敛但价格偏离') return row.chip_converging_price_deviated === true
      if (stage === '筹码峰下移') return row.chip_peak_falling === true
      if (stage === 'T+5观察条件') return row.chip_trend_t5 === true
      if (stage === '优先关注') return ['确认关注', '早期洗盘'].includes(this.actionReference(row))
      if (stage === '风险回避') return this.isRiskAvoidance(row)
      if (stage === '拉升跟踪') return row.stage === stage
      return this.actionReference(row) === stage
    },
    poolMark (id) {
      return {
        core_action: 'A',
        conditional_watch: 'B',
        risk_review: '!',
        auction_limit_potential: '↗',
        auction_dragon: '◆',
        trend_start: '≈',
        oversold_reversal: '⌁',
        stage_model: '◒',
        bullish_oversold: '◓',
        first_board_discovery: '◇',
        bear_overnight: '☾',
        macd_bear_rebound: '↶',
        limit_pullback_repair: '↗'
      }[id] || '•'
    },
    poolMarkClass (id) {
      return `mark-${id}`
    },
    macdHistogramText (value) {
      if (value === null || value === undefined || value === '') return '--'
      const number = Number(value)
      return Number.isFinite(number) ? number.toPrecision(4) : '--'
    },
    volumeHandText (value) {
      if (value === null || value === undefined || value === '' || !Number.isFinite(Number(value))) return '--'
      const number = Number(value)
      return number >= 10000 ? `${(number / 10000).toFixed(2)} 万手` : `${number.toFixed(0)} 手`
    },
    macdVolumeRatioText (value) {
      if (value === null || value === undefined || value === '') return '--'
      const number = Number(value)
      return Number.isFinite(number) ? number.toFixed(2) : '--'
    },
    roleClass (role) {
      if (['market_leader', 'board_leader'].includes(role)) return 'role-core'
      if (role === 'front_runner') return 'role-front'
      if (['middle_follow', 'rear'].includes(role)) return 'role-follow'
      return 'role-pending'
    },
    decisionStatusClass (row) {
      if (row.decision_state === 'core_action') return 'status-confirmed'
      if (row.decision_state === 'risk_review') return 'status-risk'
      if (row.recovery_confirmed) return 'status-positive'
      if (row.decision_state === 'conditional_watch') return 'status-watch'
      return this.statusClass(row)
    },
    statusClass (row) {
      if (row.intraday_status && /跌破|风险/.test(row.intraday_status)) return 'status-risk'
      if (row.intraday_status && /突破/.test(row.intraday_status)) return 'status-positive'
      if (row.stage === '出货风险' || ((row.risks || []).length > 0 && row.change_pct < 0)) return 'status-risk'
      if (row.status === '已确认' || row.status === '承接确认') return 'status-confirmed'
      if (row.change_pct > 0) return 'status-positive'
      return 'status-watch'
    },
    actionTierClass (tier) {
      return {
        '确认关注': 'tier-confirmed-attention',
        '早期洗盘': 'tier-early-wash',
        '等待确认': 'tier-wait',
        '结构观察': 'tier-structure',
        '防追高': 'tier-no-chase',
        '长周期观察': 'tier-long',
        '风险回避': 'tier-risk'
      }[tier] || 'tier-neutral'
    },
    actionReference (row) {
      if (!row) return ''
      if (row.channel_provisional) return row.channel_candidate_basis === 'cao' ? '盘中操信号（待收盘确认）' : '盘中底部候选（待收盘确认）'
      if (row.tdx_signal_only) return '结构观察'
      return row.action_reference || row.action_tier || ''
    },
    isRiskAvoidance (row) {
      if (!row) return false
      if (this.actionReference(row) === '风险回避') return true
      if (['出货风险', '出货观察', '出货结束', '风险审计'].includes(row.stage)) return true
      const riskLevel = String(row.risk_level || '')
      // Keep unconfirmed one-day audits visible in details without treating
      // an otherwise positive tracking row as an actionable avoid signal.
      if (riskLevel && !['无明显风险', '无风险', '低风险', '正常', '风险审计'].includes(riskLevel)) {
        if (/出货|破位|卖压|高位/.test(riskLevel)) return true
      }
      return /风险回避|出货|跌破|破位|失效|卖压/.test(`${row.tracking_status || ''} ${row.intraday_status || ''}`)
    },
    statusText (row) {
      if (row.channel_provisional) return this.actionReference(row)
      if (row.tdx_signal_only) return row.tdx_channel && row.tdx_channel.cao_reclaimed ? '操 · 已收回下轨' : '操 · 尚未收回下轨'
      if (row.intraday_status) return row.intraday_status
      if (row.tracking_status && row.tracking_status !== '当前阶段确认') return row.tracking_status
      if (row.stage === '出货风险') return '风险'
      if (row.status === '已确认' || row.status === '承接确认') return '已确认'
      if (row.status) return row.status
      return row.change_pct > 0 ? '走强' : '观察'
    },
    stageTrackingText (row) {
      if (!row || !['stage_model', 'bullish_oversold'].includes(row.pool_id)) return ''
      if (row.intraday_status) return row.intraday_status
      if (row.tracking_status && row.tracking_status !== '当前阶段确认') return row.tracking_status
      return ''
    },
    stageTrackingClass (row) {
      const text = this.stageTrackingText(row)
      if (/跌破|风险|失效/.test(text)) return 'stage-state-risk'
      if (/突破/.test(text)) return 'stage-state-breakout'
      if (/延续|观察/.test(text)) return 'stage-state-watch'
      return 'stage-state-neutral'
    },
    primaryReason (row) {
      return row.reasons && row.reasons.length ? row.reasons[0] : (row.quote_status === 'live' ? '实时行情已刷新' : '等待更多信号')
    },
    factorSourceText (row) {
      const profile = row && Array.isArray(row.factor_profile) ? row.factor_profile : []
      const sources = [...new Set(profile.filter(item => item && item.available).map(item => item.source).filter(Boolean))]
      return sources.length ? sources.slice(0, 2).join(' · ') : '等待因子数据'
    },
    supportToneClass (row) {
      const tone = String((row && (row.tone || row.support_confidence)) || '')
      if (tone === 'high' || tone === 'recovery') return 'support-positive'
      if (tone === 'conflict') return 'support-negative'
      if (tone === 'medium' || tone === 'low') return 'support-caution'
      return 'support-muted'
    },
    platformToneClass (levels) {
      const state = String((levels && (levels.breakout_state || levels.state)) || '')
      if (/failed|跌破|失效/.test(state)) return 'box-failed'
      if (/confirmed|量能确认|有效突破/.test(state)) return 'box-confirmed'
      if (/testing|试突破|retest|回踩|回到箱内|near|临近|extended|延伸/.test(state)) return 'box-testing'
      return 'box-inside'
    },
    platformTitle (levels) {
      const kind = String((levels && levels.box_kind) || '')
      if (kind === 'n_breakout') return 'N字结构'
      if (kind === 'dynamic_range') return '区间位置'
      return '模型平台'
    },
    platformDefenseLabel (levels) {
      const kind = String((levels && levels.box_kind) || '')
      if (kind === 'n_breakout') return '回踩L2'
      if (kind === 'dynamic_range') return '区间底'
      return '防守位'
    },
    platformAttackLabel (levels) {
      const kind = String((levels && levels.box_kind) || '')
      if (kind === 'n_breakout') return 'N顶H1'
      if (kind === 'dynamic_range') return '区间顶'
      return '突破位'
    },
    platformLevelsAtPrice (source, price) {
      const levels = { ...source }
      const defense = Number(levels.defense_price || 0)
      const attack = Number(levels.attack_price || 0)
      const kind = String(levels.box_kind || 'model')
      if (kind === 'dynamic_range') {
        levels.breakout_state = 'reference'
        levels.state = price >= attack * 0.97 ? '临近区间上沿 · 等待N字结构' : '动态区间内 · 等待N字结构'
      } else if (price < defense) {
        levels.breakout_state = 'failed'
        levels.state = kind === 'n_breakout' ? '跌破回踩防守位 · 结构失效' : '盘中低于防守位（待收盘确认）'
      } else if (kind === 'n_breakout' && levels.breakout_confirmed && price >= attack * 1.08) {
        levels.breakout_state = 'extended'
        levels.state = '突破延伸 · 高于N顶8%以上不追高'
      } else if (levels.breakout_confirmed && price >= attack) {
        levels.breakout_state = 'confirmed'
        levels.state = kind === 'n_breakout' ? 'N字有效突破 · 收盘量能确认' : '有效突破 · 收盘确认'
      } else if (price >= attack) {
        levels.breakout_state = 'testing'
        levels.state = kind === 'n_breakout' ? '盘中试突破N顶 · 待收盘/量能确认' : '盘中站上进攻位（待收盘/量能确认）'
      } else if (levels.historical_breakout || levels.close_broke_box) {
        levels.breakout_state = 'retest'
        levels.state = kind === 'n_breakout' ? '突破后回踩N顶 · 观察承接' : '突破后回到平台 · 观察承接'
      } else if (price >= attack * 0.97) {
        levels.breakout_state = 'near'
        levels.state = kind === 'n_breakout' ? '临近N顶 · 等待有效突破' : '临近突破位 · 等待确认'
      } else {
        levels.breakout_state = kind === 'n_breakout' ? 'forming' : 'inside'
        levels.state = kind === 'n_breakout' ? 'N字右侧构建 · 等待接近N顶' : '平台区间内'
      }
      return levels
    },
    platformDistanceText (levels) {
      const distance = Number((levels && levels.distance_to_attack_pct) || 0)
      const kind = String((levels && levels.box_kind) || '')
      const target = kind === 'n_breakout' ? 'N顶' : (kind === 'dynamic_range' ? '区间顶' : '突破位')
      if (!Number.isFinite(distance)) return `${target}距离待补`
      return distance >= 0
        ? `高于${target} ${this.signedPercent(distance)}`
        : `距${target} ${Math.abs(distance).toFixed(2)}%`
    },
    platformPositionText (levels) {
      const position = Number((levels && levels.position_pct) || 0)
      if (!Number.isFinite(position)) return '--'
      if (position < 0) return '下方'
      if (position > 100) return '上方'
      return `${position.toFixed(0)}%`
    },
    platformListText (row) {
      const levels = (row && row.platform_levels) || {}
      if (!levels.available) return '结构位待补'
      const state = String(levels.breakout_state || levels.state || '')
      const top = this.priceText(levels.attack_price)
      const distance = Number(levels.distance_to_attack_pct || 0)
      const kind = String(levels.box_kind || 'model')
      if (kind === 'dynamic_range') return `区间参考 · ${this.platformDistanceText(levels)}`
      const target = kind === 'n_breakout' ? 'N顶' : '突破位'
      const defense = kind === 'n_breakout' ? 'L2' : '防守位'
      if (/failed|跌破|失效/.test(state)) return `结构失效 · 跌破${defense} ${this.priceText(levels.defense_price)}`
      if (/extended|延伸/.test(state)) return `突破延伸 · ${this.platformDistanceText(levels)}`
      if ((/confirmed|量能确认|有效突破/.test(state)) && distance >= 0) return `有效突破 · ${target} ${top}`
      if (/retest|回踩|回到箱内/.test(state)) return `${target}回踩 · ${this.platformDistanceText(levels)}`
      if (/testing|试突破/.test(state) || distance >= 0) return `试突破 · ${target} ${top}`
      return `${target} ${top} · ${this.platformDistanceText(levels)}`
    },
    validLivePrice (value) {
      const number = Number(value)
      return Number.isFinite(number) && number > 0
    },
    dailyMoveLimit (code) {
      const normalized = String(code || '').replace(/\D/g, '').slice(-6)
      if (/^(4|8|920)/.test(normalized)) return 32
      if (/^(300|301|688|689)/.test(normalized)) return 22
      return 12
    },
    validLivePreClose (quote, code = '') {
      if (!quote || quote.change_pct_valid === false) return false
      const price = Number(quote.price)
      const previous = Number(quote.pre_close)
      const change = price > 0 && previous > 0 ? (price / previous - 1) * 100 : NaN
      const limit = this.dailyMoveLimit(code || quote.code)
      return Number.isFinite(change) && change >= -limit && change <= limit
    },
    liveChangePct (quote, row) {
      const snapshotChange = Number((row && row.change_pct) || 0)
      if (!quote || !this.validLivePrice(quote.price)) return snapshotChange
      if (this.validLivePreClose(quote, row && row.code)) {
        return (Number(quote.price) / Number(quote.pre_close) - 1) * 100
      }
      // Fall back to the dashboard's date-aware previous close only when the
      // relay explicitly marks its previous-close field invalid.
      const snapshotClose = Number((row && (row.pre_close || row.prev_close)) || 0)
      const change = snapshotClose > 0 ? (Number(quote.price) / snapshotClose - 1) * 100 : NaN
      const limit = this.dailyMoveLimit(row && row.code)
      if (Number.isFinite(change) && change >= -limit && change <= limit) return change
      return snapshotChange
    },
    scoreLabel (score) {
      if (score >= 85) return '高优先级'
      if (score >= 70) return '中优先级'
      return '观察'
    },
    isLiveActionPool (row) {
      return Boolean(row && ['stage_model', 'bullish_oversold', 'first_board_discovery'].includes(row.pool_id))
    },
    priceText (value) {
      const number = Number(value)
      return number > 0 ? number.toFixed(number >= 100 ? 2 : 3) : '--'
    },
    dailyMaTouched (key) {
      const signal = this.dailyMaReclaimSignal(key)
      if (signal) return signal.touched === true
      const position = this.selectedDailyPositions
      const movingAverage = Number(position[key] || 0)
      const intradayLow = Number(position.intraday_low || 0)
      return Boolean(
        position.available &&
        movingAverage > 0 &&
        intradayLow > 0 &&
        intradayLow <= movingAverage
      )
    },
    dailyMaBelow (key) {
      const signal = this.dailyMaReclaimSignal(key)
      if (signal) return signal.touched === true && signal.reclaimed !== true
      const position = this.selectedDailyPositions
      const movingAverage = Number(position[key] || 0)
      const currentPrice = Number(this.selectedRow && this.selectedRow.price || 0)
      return Boolean(
        this.dailyMaTouched(key) &&
        currentPrice > 0 &&
        movingAverage > 0 &&
        currentPrice < movingAverage
      )
    },
    dailyMaClass (key) {
      if (!this.dailyMaTouched(key)) return ''
      return this.dailyMaBelow(key) ? 'daily-ma-below' : 'daily-ma-touched'
    },
    dailyMaReclaimSignal (key) {
      const period = { ma13: 13, ma17: 17 }[key]
      const signals = this.selectedRow && this.selectedRow.ma_cross_signals
      if (!period || !signals) return null
      const incomplete = this.selectedDailyPositions.daily_bar_incomplete
      return (incomplete ? signals[`intraday_reclaim_${period}`] : signals[`daily_reclaim_${period}`]) || null
    },
    dailyMaStateText (key) {
      const signal = this.dailyMaReclaimSignal(key)
      if (signal) return this.maCrossStateText(signal)
      if (!this.selectedDailyPositions.available) return '暂无日线'
      if (!this.dailyMaTouched(key)) return '今日未回踩'
      return this.dailyMaBelow(key) ? '日内回踩后仍在下方' : '日内回踩后收回'
    },
    signedPercent (value) {
      const number = Number(value)
      return `${number >= 0 ? '+' : ''}${number.toFixed(2)}%`
    },
    signedInteger (value) {
      const number = Number(value)
      return `${number > 0 ? '+' : ''}${Math.round(number)}`
    },
    percentOrDash (value) {
      return Number(value) ? this.signedPercent(value) : '--'
    },
    unsignedPercentOrDash (value) {
      const number = Number(value)
      return Number.isFinite(number) && number > 0 ? `${number.toFixed(2)}%` : '--'
    },
    signedPointsOrDash (value) {
      if (value === null || value === undefined || value === '') return '--'
      const number = Number(value)
      if (!Number.isFinite(number)) return '--'
      return `${number >= 0 ? '+' : ''}${number.toFixed(2)}个百分点`
    },
    densityScoreText (value) {
      if (value === null || value === undefined || value === '') return '--'
      const number = Number(value)
      return Number.isFinite(number) ? `${number.toFixed(1)} / 100` : '--'
    },
    chipBandWidth (detail) {
      if (!detail || typeof detail !== 'object') return null
      const value = detail.cost_band_width_pct !== undefined
        ? detail.cost_band_width_pct
        : detail.concentration_pct
      const number = Number(value)
      return Number.isFinite(number) && number > 0 ? number : null
    },
    chipDensityScore (detail) {
      if (!detail || typeof detail !== 'object') return null
      if (detail.density_score !== null && detail.density_score !== undefined && detail.density_score !== '') {
        return detail.density_score
      }
      const width = this.chipBandWidth(detail)
      return width === null ? null : Math.max(0, Math.min(100, (1 - width / 12) * 100))
    },
    amountText (value) {
      const number = Number(value)
      if (!number) return '--'
      return number >= 100000000
        ? `${(number / 100000000).toFixed(2)}亿`
        : `${(number / 10000).toFixed(0)}万`
    },
    scoreText (value) {
      const number = Number(value)
      return number ? number.toFixed(1) : '--'
    },
    changeClass (value) {
      return Number(value) > 0 ? 'up' : Number(value) < 0 ? 'down' : 'flat'
    },
    formatSnapshot (value) {
      if (!value) return '暂无时间'
      return value.slice(5, 16).replace('-', '/')
    }
  }
}
</script>

<style>
.stock-daily-trigger { margin-top: 8px; padding: 5px 9px; color: var(--mint); background: transparent; border: 1px solid rgba(255,255,255,.25); border-radius: 4px; font-size: 11px; }

:root {
  --ink: #141922;
  --ink-soft: #303845;
  --muted: #6d7480;
  --hairline: #d9ddd8;
  --canvas: #f5f3ee;
  --paper: #fcfbf8;
  --dark: #151c27;
  --cream: #efe5cf;
  --mint: #afd9c4;
  --coral: #b24e32;
  --yellow: #e9c764;
  --blue: #79a7c1;
  --green: #1d8b63;
  --red: #c34e47;
  --shadow: 0 16px 40px rgba(23, 31, 40, .07);
}

* { box-sizing: border-box; }
html, body, #app { min-height: 100%; margin: 0; }
body {
  color: var(--ink);
  background: var(--canvas);
  font-family: "Avenir Next", "PingFang SC", "Helvetica Neue", sans-serif;
  -webkit-font-smoothing: antialiased;
}
button, input, select { font: inherit; }
button { cursor: pointer; }
body.panel-open, body.stock-chart-open { overflow: hidden; }
button:focus-visible, summary:focus-visible, select:focus-visible, input:focus-visible { outline: 2px solid var(--green); outline-offset: 3px; }

.app-shell { min-height: 100vh; }
.topbar {
  height: 72px;
  padding: 0 46px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--paper);
  border-bottom: 1px solid var(--hairline);
}
.brand-lockup, .topbar-actions, .topbar-center, .market-state { display: flex; align-items: center; }
.brand-lockup { gap: 11px; min-width: 210px; }
.brand-mark {
  display: grid;
  width: 31px; height: 31px; place-items: center;
  background: var(--dark); color: #fff;
  border-radius: 7px;
  font-family: Georgia, serif; font-size: 18px; font-weight: 700;
}
.brand-name { font-size: 13px; font-weight: 800; letter-spacing: .14em; }
.brand-caption { margin-top: 1px; color: var(--muted); font-family: Georgia, serif; font-size: 11px; font-style: italic; }
.topbar-center { gap: 15px; color: var(--muted); font-size: 11px; letter-spacing: .12em; }
.topbar-kicker { color: var(--ink); font-weight: 700; }
.topbar-divider { width: 26px; height: 1px; background: var(--hairline); }
.topbar-date { letter-spacing: .08em; }
.topbar-actions { gap: 20px; }
.alert-setting-button { min-height: 31px; padding: 0 9px; color: var(--muted); background: transparent; border: 1px solid var(--hairline); border-radius: 16px; font-size: 9px; font-weight: 700; white-space: nowrap; }
.alert-setting-button.enabled { color: #126b4b; background: #e5f4eb; border-color: #9bcab0; }
.alert-button { position: relative; min-height: 35px; padding: 0 12px; color: var(--muted); background: transparent; border: 1px solid var(--hairline); border-radius: 18px; font-size: 11px; font-weight: 700; }
.alert-button > span { display: inline-grid; width: 17px; height: 17px; margin-right: 4px; place-items: center; border-radius: 50%; background: #e7e5de; }
.alert-button b { display: inline-grid; min-width: 18px; height: 18px; margin-left: 5px; padding: 0 4px; place-items: center; color: #fff; background: var(--red); border-radius: 9px; font-size: 9px; }
.alert-button.active { color: #8a3a31; background: #fff0eb; border-color: #dfaaa0; box-shadow: 0 0 0 3px rgba(195, 78, 71, .08); }
.alert-button.active > span { color: #fff; background: var(--red); }
.dat-points-badge { padding: 5px 9px; border: 1px solid var(--hairline); border-radius: 6px; color: var(--ink); background: transparent; font-size: 11px; white-space: nowrap; }
.dat-points-badge small { display: block; margin-top: 2px; color: var(--muted); font-size: 9px; }
.market-state { gap: 8px; color: var(--muted); font-size: 12px; }
.state-dot { width: 7px; height: 7px; border-radius: 50%; background: var(--yellow); }
.state-dot.live { background: var(--green); box-shadow: 0 0 0 4px rgba(29, 139, 99, .12); }
.icon-button {
  width: 35px; height: 35px; padding: 0;
  color: var(--ink); background: transparent;
  border: 1px solid var(--hairline); border-radius: 50%;
  font-size: 20px; line-height: 1;
}
.icon-button:hover, .ghost-button:hover, .pool-item:hover { border-color: #a4aaa5; }
.icon-button.spinning { animation: spin 1s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

.urgent-alert-banner { position: fixed; top: 76px; left: 50%; z-index: 120; display: flex; width: min(920px, calc(100vw - 32px)); overflow: hidden; color: #fff; background: linear-gradient(105deg, #b51e26, #e0442e 62%, #ef6c2f); border: 1px solid rgba(255,255,255,.42); border-radius: 12px; box-shadow: 0 18px 55px rgba(134, 20, 23, .42), 0 0 0 5px rgba(224, 68, 46, .13); transform: translateX(-50%); animation: urgentPulse 1.3s ease-in-out infinite alternate; }
.urgent-alert-main { display: grid; min-width: 0; flex: 1; grid-template-columns: 38px minmax(0, 1fr) auto; gap: 12px; align-items: center; padding: 14px 18px; color: inherit; text-align: left; background: transparent; border: 0; }
.urgent-alert-icon { display: grid; width: 38px; height: 38px; place-items: center; color: #b51e26; background: #fff; border-radius: 50%; font-size: 24px; font-weight: 900; }
.urgent-alert-copy strong, .urgent-alert-copy small { display: block; }
.urgent-alert-copy strong { font-size: 15px; letter-spacing: .02em; }
.urgent-alert-copy small { margin-top: 5px; overflow: hidden; color: rgba(255,255,255,.88); font-size: 11px; text-overflow: ellipsis; white-space: nowrap; }
.urgent-alert-action { padding: 7px 10px; border: 1px solid rgba(255,255,255,.5); border-radius: 16px; font-size: 10px; font-weight: 800; white-space: nowrap; }
.urgent-alert-close { width: 46px; color: #fff; background: rgba(92,0,0,.16); border: 0; border-left: 1px solid rgba(255,255,255,.2); font-size: 25px; }
.urgent-alert-enter-active, .urgent-alert-leave-active { transition: opacity .2s ease, transform .25s ease; }
.urgent-alert-enter, .urgent-alert-leave-to { opacity: 0; transform: translate(-50%, -18px); }
@keyframes urgentPulse { from { box-shadow: 0 16px 45px rgba(134,20,23,.34), 0 0 0 3px rgba(224,68,46,.1); } to { box-shadow: 0 20px 60px rgba(134,20,23,.52), 0 0 0 8px rgba(224,68,46,.18); } }

.dashboard { max-width: 1760px; margin: 0 auto; padding: 30px 40px 28px; }
.hero { display: flex; justify-content: space-between; align-items: flex-end; padding-bottom: 54px; }
.eyebrow { margin: 0 0 13px; color: var(--coral); font-size: 10px; font-weight: 800; letter-spacing: .18em; }
.hero h1 { margin: 0; font-family: Georgia, "Songti SC", serif; font-size: clamp(39px, 5vw, 70px); font-weight: 400; letter-spacing: -.045em; line-height: .98; }
.hero h1 em { color: var(--coral); font-style: normal; }
.hero-copy { max-width: 475px; margin: 24px 0 0; color: var(--muted); font-family: Georgia, "Songti SC", serif; font-size: 16px; line-height: 1.7; }
.hero-aside { width: 190px; padding: 18px 0 2px 22px; border-left: 1px solid var(--hairline); }
.hero-aside-label { color: var(--muted); font-size: 11px; letter-spacing: .12em; }
.hero-aside-value { margin-top: 9px; font-size: 15px; font-weight: 700; line-height: 1.4; }
.hero-aside-meta { margin-top: 7px; color: var(--muted); font-size: 11px; }

.metric-strip { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 56px; }
.metric-card { min-height: 145px; padding: 22px 24px; border-radius: 11px; }
.metric-dark { color: #fff; background: var(--dark); }
.metric-cream { background: var(--cream); }
.metric-mint { background: var(--mint); }
.metric-coral { color: #fff; background: var(--coral); }
.metric-label { font-size: 11px; font-weight: 700; letter-spacing: .12em; text-transform: uppercase; opacity: .72; }
.metric-value { margin-top: 20px; font-family: Georgia, serif; font-size: 42px; line-height: .9; }
.metric-foot { margin-top: 21px; font-size: 11px; opacity: .7; }

.opportunity-radar { margin-bottom: 52px; padding: 27px; background: var(--paper); border: 1px solid var(--hairline); border-radius: 12px; box-shadow: var(--shadow); }
.opportunity-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 25px; }
.opportunity-head h2 { margin: 0; font-family: Georgia, "Songti SC", serif; font-size: 28px; font-weight: 400; }
.opportunity-head p:not(.eyebrow) { max-width: 620px; margin: 10px 0 0; color: var(--muted); font-size: 11px; line-height: 1.6; }
.opportunity-market { min-width: 150px; padding-left: 22px; border-left: 1px solid var(--hairline); text-align: right; }
.opportunity-market span, .opportunity-market small { display: block; color: var(--muted); font-size: 10px; }
.opportunity-market strong { display: block; margin: 7px 0 3px; color: var(--coral); font-family: Georgia, serif; font-size: 31px; font-weight: 400; }
.opportunity-warning { margin-top: 18px; padding: 9px 11px; color: #78572a; background: #f5ead0; border: 1px solid #e5cf9f; border-radius: 5px; font-size: 10px; }
.opportunity-summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 1px; margin-top: 23px; overflow: hidden; background: var(--hairline); border: 1px solid var(--hairline); border-radius: 7px; }
.opportunity-summary div { padding: 13px 15px; background: #f7f5ef; }
.opportunity-summary span { display: block; color: var(--muted); font-size: 9px; }
.opportunity-summary strong { display: block; margin-top: 6px; font-family: Georgia, serif; font-size: 20px; font-weight: 400; }
.opportunity-evaluation { display: flex; flex-wrap: wrap; gap: 8px 18px; align-items: center; margin-top: 12px; color: var(--muted); font-size: 9px; }
.opportunity-evaluation > span { color: var(--ink); font-weight: 700; letter-spacing: .06em; }
.opportunity-evaluation small { font-size: 9px; }
.opportunity-body { display: grid; grid-template-columns: .78fr 1.5fr; gap: 25px; margin-top: 25px; }
.opportunity-section-title { display: flex; align-items: baseline; justify-content: space-between; margin-bottom: 9px; }
.opportunity-section-title span { font-size: 11px; font-weight: 700; letter-spacing: .08em; }
.opportunity-section-title small { color: var(--muted); font-size: 9px; }
.opportunity-board-card, .opportunity-stock-row { border-bottom: 1px solid #e8e6df; }
.opportunity-board-card { display: flex; width: 100%; align-items: center; justify-content: space-between; min-height: 53px; gap: 15px; padding: 0 7px; color: var(--ink); background: transparent; border-top: 0; border-right: 0; border-left: 0; text-align: left; }
.opportunity-board-card:hover { background: #f7f3ea; }
.opportunity-board-card.active { background: #edf5ef; box-shadow: inset 3px 0 0 var(--green); }
.opportunity-board-card > div:first-child { min-width: 0; }
.opportunity-board-card strong { display: block; overflow: hidden; font-size: 12px; text-overflow: ellipsis; white-space: nowrap; }
.opportunity-board-card small { display: block; margin-top: 4px; color: var(--muted); font-size: 9px; }
.opportunity-board-score { flex: 0 0 auto; text-align: right; }
.opportunity-board-score strong { color: var(--coral); font-family: Georgia, serif; font-size: 16px; font-weight: 400; }
.opportunity-board-score small { max-width: 130px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.opportunity-clear-board { padding: 3px 7px; color: var(--green); background: transparent; border: 1px solid rgba(29, 139, 99, .35); border-radius: 4px; font-size: 9px; }
.opportunity-stock-row { display: grid; width: 100%; min-height: 53px; grid-template-columns: minmax(150px, 1fr) 66px 48px 125px; gap: 10px; align-items: center; padding: 0 4px; color: var(--ink); background: transparent; border-top: 0; border-right: 0; border-left: 0; text-align: left; }
.opportunity-stock-row:hover { background: #f7f3ea; }
.opportunity-stock-name { min-width: 0; }
.opportunity-stock-name strong, .opportunity-stock-name small { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.opportunity-stock-name strong { font-size: 12px; }
.opportunity-stock-name small { margin-top: 4px; color: var(--muted); font-size: 9px; }
.opportunity-stock-row > .up, .opportunity-stock-row > .down, .opportunity-stock-row > .flat, .opportunity-score { text-align: right; font-family: Georgia, serif; font-size: 12px; }
.opportunity-score { color: var(--coral); }
.opportunity-score strong, .opportunity-score small { display: block; }
.opportunity-score strong { font-size: 15px; font-weight: 400; }
.opportunity-score small { margin-top: 2px; color: var(--muted); font-family: inherit; font-size: 8px; white-space: nowrap; }
.opportunity-state { padding: 5px 7px; border-radius: 4px; font-size: 9px; text-align: center; white-space: nowrap; }
.opportunity-strong { color: #fff; background: var(--green); }
.opportunity-focus { color: #15573f; background: #cfe9d9; }
.opportunity-wait { color: #735a20; background: #f3e8c7; }
.opportunity-invalid { color: #8a3a31; background: #f4d6cc; }
.opportunity-empty { padding: 25px 0; color: var(--muted); border-top: 1px solid #e8e6df; font-size: 10px; }

.decision-overview { display: grid; grid-template-columns: 240px minmax(0, 1fr); gap: 0 24px; margin-bottom: 12px; padding: 16px 22px 8px; background: var(--paper); border: 1px solid var(--hairline); border-radius: 10px; box-shadow: var(--shadow); }
.overview-notes { grid-column: 1 / -1; margin-top: 10px; color: var(--muted); font-size: 11px; }
.overview-notes > summary { cursor: pointer; min-height: 28px; line-height: 28px; }
.overview-warning-count { margin-left: 8px; color: #8a6814; }
.overview-context { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 6px 18px; padding: 8px 0; line-height: 1.6; }
.overview-context p { margin: 0; }
.market-brief { display: flex; align-items: center; gap: 16px; width: 100%; min-height: 48px; margin-bottom: 24px; padding: 10px 16px; text-align: left; color: var(--ink); background: var(--cream); border: 1px solid #e6dcc1; border-radius: 8px; }
.market-brief-label { flex-shrink: 0; color: var(--coral); font-size: 11px; }
.market-brief strong { flex-shrink: 0; font-size: 13px; }
.market-brief-summary { flex: 1; min-width: 0; overflow: hidden; color: var(--muted); font-size: 12px; text-overflow: ellipsis; white-space: nowrap; }
.market-brief-link { flex-shrink: 0; margin-left: auto; color: var(--ink-soft); font-size: 11px; }
.bottom-repair-row { display: block; overflow: hidden; max-width: 100%; color: #a36914; font-size: 10px; line-height: 1.6; white-space: nowrap; text-overflow: ellipsis; }
.decision-overview-head { display: flex; flex-direction: column; align-items: stretch; justify-content: center; gap: 14px; }
.decision-overview-head .eyebrow { display: none; }
.decision-overview-head h1 { margin: 0; font-size: 21px; font-weight: 600; letter-spacing: 0; line-height: 1.2; }
.view-mode-switch { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); width: 100%; flex-shrink: 0; padding: 3px; background: #e9e8e3; border: 1px solid var(--hairline); border-radius: 7px; }
.view-mode-switch button { min-height: 38px; padding: 0 10px; color: var(--muted); background: transparent; border: 0; border-radius: 5px; font-size: 12px; white-space: nowrap; }
.view-mode-switch button.active { color: #fff; background: var(--dark); }
.decision-metrics { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); border: 1px solid var(--hairline); border-radius: 7px; overflow: hidden; }
.decision-metrics article { min-width: 0; min-height: 80px; padding: 12px 16px; background: #f7f6f2; border-right: 1px solid var(--hairline); }
.decision-metrics article:last-child { border-right: 0; }
.decision-metrics span, .decision-metrics small { display: block; overflow: hidden; color: var(--muted); font-size: 11px; text-overflow: ellipsis; white-space: nowrap; }
.decision-metrics strong { display: block; overflow: hidden; margin: 6px 0 4px; font-size: 20px; font-weight: 600; line-height: 1.2; text-overflow: ellipsis; white-space: nowrap; }
.decision-metrics .decision-positive { background: #edf6f0; }
.decision-metrics .decision-positive strong { color: var(--green); }
.decision-metrics .decision-risk { background: #f8efec; }
.decision-metrics .decision-risk strong { color: var(--red); }
.intraday-prompt { grid-column: 1 / -1; display: flex; gap: 4px 14px; align-items: flex-start; margin-top: 10px; padding: 10px 13px; color: #354353; background: #edf1f3; border-left: 4px solid #6c8799; font-size: 12px; }
.intraday-prompt strong { flex: 0 0 auto; color: #293744; font-size: 12px; white-space: nowrap; }
.intraday-prompt-body { display: flex; min-width: 0; flex: 1 1 auto; flex-wrap: wrap; gap: 4px 12px; align-items: baseline; }
.intraday-prompt-body > span { min-width: 0; overflow-wrap: anywhere; }
.intraday-prompt .intraday-metrics { color: #293744; font-variant-numeric: tabular-nums; }
.intraday-prompt small { flex: 1 0 100%; color: #68737b; font-size: 10px; line-height: 1.5; overflow-wrap: anywhere; }
.intraday-prompt.caution { color: #75531a; background: #fff4d9; border-left-color: #d59a24; }
.intraday-prompt.caution strong { color: #75531a; }
.intraday-prompt.warning { color: #812e28; background: #ffebe7; border-left-color: #cb493d; }
.intraday-prompt.warning strong { color: #812e28; }
.calibration-strip { display: flex; flex-wrap: wrap; gap: 8px 18px; align-items: center; margin-top: 12px; color: var(--muted); font-size: 9px; }
.calibration-strip > span { color: var(--ink); font-weight: 700; }
.compact-warning { margin-top: 12px; }
.funds-observations { margin-top: 12px; font-size: 12px; }
.funds-observations summary { cursor: pointer; color: var(--muted); }
.funds-observations > div { display: flex; flex-wrap: wrap; gap: 12px; padding: 7px 0; border-bottom: 1px solid var(--hairline); }
.funds-observations small { color: var(--muted); }

.workspace { display: grid; grid-template-columns: 200px minmax(0, 1fr); gap: 28px; align-items: start; }
.pool-rail { position: sticky; top: 20px; max-height: calc(100vh - 40px); overflow-y: auto; padding-top: 4px; }
.rail-heading { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 16px; }
.rail-heading h2 { margin: 0; font-family: Georgia, serif; font-size: 25px; font-weight: 400; }
.rail-count { min-width: 26px; padding: 4px 7px; color: var(--muted); background: #e3e2dd; border-radius: 10px; font-size: 11px; text-align: center; }
.pool-item {
  width: 100%; min-height: 66px; padding: 9px 10px;
  display: grid; grid-template-columns: 28px minmax(0, 1fr) auto; gap: 9px; align-items: center;
  color: var(--ink); background: transparent; border: 1px solid transparent; border-radius: 8px;
  text-align: left; transition: background .15s ease, border-color .15s ease;
}
.pool-item.active { background: var(--paper); border-color: var(--hairline); box-shadow: var(--shadow); }
.pool-item.stale { opacity: .6; }
.pool-item-mark { width: 27px; height: 27px; display: grid; place-items: center; border-radius: 6px; color: var(--ink); background: var(--cream); font-size: 16px; }
.mark-all { color: #fff; background: var(--dark); }
.mark-core_action { color: #fff; background: var(--green); }
.mark-conditional_watch { color: #614d1d; background: #eadcae; }
.mark-risk_review { color: #fff; background: var(--red); }
.mark-auction_dragon { background: #d6c5e4; }
.mark-trend_start { background: var(--mint); }
.mark-oversold_reversal { background: #f2c7aa; }
.mark-stage_model { background: var(--yellow); }
.mark-bullish_oversold { background: #f4d39c; }
.mark-first_board_discovery { color: #173b32; background: #a9d7c5; }
.mark-yaogu_sniffer { color: #fff; background: #7d536d; }
.pool-item-copy { min-width: 0; }
.pool-item-copy strong { display: block; overflow: hidden; font-size: 12px; font-weight: 700; text-overflow: ellipsis; white-space: nowrap; }
.pool-item-copy small { display: block; margin-top: 4px; overflow: hidden; color: var(--muted); font-size: 10px; text-overflow: ellipsis; white-space: nowrap; }
.pool-item-count { color: var(--muted); font-family: Georgia, serif; font-size: 14px; }
.rail-note { display: flex; gap: 10px; margin: 28px 12px 0 9px; color: var(--muted); }
.note-line { flex: 0 0 1px; background: var(--coral); }
.rail-note p { margin: -3px 0 0; font-family: Georgia, serif; font-size: 12px; line-height: 1.6; }

.pool-view { min-width: 0; }
.mark-bear_overnight { background: #b8c4d5; }
.mark-macd_bear_rebound { background: #b8d8c4; }
.mark-limit_pullback_repair { background: #d6c4a6; }
.overnight-method { margin-bottom: 14px; padding: 12px 14px; border: 1px solid var(--hairline); border-radius: 8px; background: var(--paper); font-size: 12px; line-height: 1.65; }
.overnight-method-head { display: flex; flex-wrap: wrap; gap: 8px 16px; justify-content: space-between; }
.overnight-method-head span, .overnight-method details { color: var(--muted); }
.overnight-method p { margin: 8px 0; }
.overnight-data-state { color: var(--ink); font-weight: 600; }
.overnight-method summary { cursor: pointer; }
.overnight-checks p { font-size: 11px; color: var(--muted); line-height: 1.6; }
.overnight-check { display: flex; justify-content: space-between; gap: 12px; padding: 5px 0; font-size: 11px; border-bottom: 1px solid var(--hairline); }
.view-header { display: grid; grid-template-columns: minmax(180px, 1fr) minmax(500px, 1.3fr); gap: 24px; align-items: center; padding: 0 0 16px; }
.view-kicker { display: flex; align-items: center; gap: 8px; color: var(--coral); font-size: 11px; font-weight: 800; letter-spacing: .1em; }
.active-pool-dot { width: 7px; height: 7px; border-radius: 50%; background: var(--coral); }
.view-header h2 { margin: 9px 0 0; font-family: Georgia, serif; font-size: 26px; font-weight: 400; }
.pool-title { position: relative; min-width: 0; }
.view-title-line { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-top: 8px; }
.view-title-line h2 { min-width: 0; overflow: hidden; margin: 0; text-overflow: ellipsis; white-space: nowrap; }
.pool-title > p { display: -webkit-box; overflow: hidden; -webkit-box-orient: vertical; -webkit-line-clamp: 2; margin: 8px 0 0; color: var(--muted); font-size: 12px; line-height: 1.6; }
.mobile-filter-toggle { display: none; }
.view-controls { min-width: 0; display: flex; flex-direction: column; gap: 8px; align-items: stretch; }
.view-filter-row { display: grid; grid-template-columns: minmax(155px, 1fr) minmax(190px, 1.25fr) auto auto; gap: 8px; align-items: center; }
.view-order-row { display: flex; justify-content: flex-end; gap: 8px; align-items: center; }
.toolbar-caption { color: var(--muted); font-size: 10px; }
.search-box { width: auto; min-width: 0; height: 36px; display: flex; align-items: center; gap: 8px; padding: 0 11px; color: var(--muted); background: var(--paper); border: 1px solid var(--hairline); border-radius: 6px; }
.search-box input { width: 100%; border: 0; outline: 0; color: var(--ink); background: transparent; font-size: 12px; }
.board-filter { width: auto; min-width: 0; height: 36px; display: flex; align-items: center; gap: 7px; padding: 0 7px 0 10px; color: var(--muted); background: var(--paper); border: 1px solid var(--hairline); border-radius: 6px; font-size: 10px; white-space: nowrap; }
.board-filter select { min-width: 0; width: 100%; padding: 6px 3px; overflow: hidden; color: var(--ink); background: transparent; border: 0; outline: 0; font-size: 11px; text-overflow: ellipsis; }
.ghost-button { height: 36px; padding: 0 11px; color: var(--ink-soft); background: transparent; border: 1px solid var(--hairline); border-radius: 6px; font-size: 11px; }
.ghost-button.selected { color: var(--green); background: rgba(175, 217, 196, .4); border-color: var(--green); }
.filter-glyph { margin-right: 4px; font-size: 15px; }
.mobile-sort-controls { display: grid; grid-template-columns: minmax(170px, 1fr) 32px; gap: 5px; min-width: 225px; }
.mobile-sort-controls label { height: 36px; display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: 0 7px 0 9px; color: var(--muted); background: var(--paper); border: 1px solid var(--hairline); border-radius: 5px; font-size: 10px; }
.mobile-sort-controls select { min-width: 0; width: 100%; padding: 5px 2px; color: var(--ink); background: transparent; border: 0; outline: 0; font-size: 10px; }
.mobile-sort-controls button { display: grid; place-items: center; color: var(--ink); background: var(--paper); border: 1px solid var(--hairline); border-radius: 5px; font-size: 15px; }
.refresh-control { height: 36px; display: flex; align-items: center; gap: 7px; padding-left: 6px; color: var(--muted); font-size: 10px; white-space: nowrap; }
.refresh-control select { padding: 7px 5px; color: var(--ink); background: var(--paper); border: 1px solid var(--hairline); border-radius: 5px; outline: 0; font-size: 11px; }
.count-legend { min-height: 14px; margin: -5px 0 10px; color: #8a918c; font-size: 9px; line-height: 1.5; }
.factor-research-panel { margin: 0 0 14px; padding: 16px; background: var(--paper); border: 1px solid var(--hairline); border-radius: 8px; }
.factor-panel-head { display: flex; justify-content: space-between; gap: 18px; align-items: flex-start; }
.factor-panel-head .eyebrow { margin-bottom: 6px; }
.factor-panel-head h3 { margin: 0; font-family: Georgia, serif; font-size: 20px; font-weight: 400; }
.factor-panel-head > span { color: var(--muted); font-size: 10px; }
.factor-loading, .factor-error { padding: 18px 0 5px; color: var(--muted); font-size: 11px; }
.factor-error, .factor-warning { color: #8a3a31; }
.factor-groups { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 14px; margin-top: 15px; }
.factor-group { min-width: 0; padding: 9px 10px; background: #f4f3ee; border: 1px solid #e1e3dd; border-radius: 6px; }
.factor-group-title { display: flex; justify-content: space-between; margin-bottom: 7px; color: var(--ink-soft); font-size: 10px; }
.factor-group-title span { color: var(--muted); }
.factor-option { display: grid; grid-template-columns: 16px minmax(0, 1fr) 45px; gap: 6px; align-items: center; min-height: 32px; color: var(--ink-soft); font-size: 10px; }
.factor-option input[type="checkbox"] { accent-color: var(--green); }
.factor-option-copy { min-width: 0; }
.factor-option-copy strong, .factor-option-copy small { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.factor-option-copy small { margin-top: 2px; color: var(--muted); font-size: 8px; }
.factor-weight { width: 45px; padding: 4px 3px; color: var(--ink); background: var(--paper); border: 1px solid var(--hairline); border-radius: 4px; font-size: 10px; text-align: right; }
.factor-runbar { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; margin-top: 13px; }
.factor-runbar label { display: flex; align-items: center; gap: 5px; color: var(--muted); font-size: 9px; }
.factor-runbar label input[type="number"], .factor-runbar label input[type="date"] { width: 105px; padding: 6px 5px; color: var(--ink); background: var(--paper); border: 1px solid var(--hairline); border-radius: 4px; font-size: 10px; }
.factor-runbar label input[type="number"] { width: 55px; }
.factor-runbar .factor-check { height: 29px; padding: 0 8px; border: 1px solid var(--hairline); border-radius: 4px; }
.factor-action-button, .factor-secondary-button { min-height: 30px; padding: 0 10px; border-radius: 4px; font-size: 10px; }
.factor-action-button { color: #fff; background: var(--dark); border: 1px solid var(--dark); }
.factor-secondary-button { color: var(--ink-soft); background: transparent; border: 1px solid var(--hairline); }
.factor-action-button:disabled, .factor-secondary-button:disabled { cursor: wait; opacity: .55; }
.factor-metrics { display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); gap: 7px; margin-top: 13px; }
.factor-metrics > div { padding: 8px; background: #eef3ef; border-radius: 4px; }
.factor-metrics span, .factor-metrics strong { display: block; }
.factor-metrics span { color: var(--muted); font-size: 8px; }
.factor-metrics strong { margin-top: 4px; color: var(--ink); font-family: Georgia, serif; font-size: 13px; font-weight: 500; }
.factor-warning { margin-top: 9px; font-size: 9px; }

.notice-bar { display: flex; align-items: center; gap: 10px; margin: 14px 0 -2px; padding: 10px 13px; border-radius: 7px; }
.error-notice { color: #72342d; background: #f0d4c9; }
.confirmation-notice { color: #66501e; background: #f4ead0; border: 1px solid #e2c98d; }
.notice-bar > span { display: grid; width: 22px; height: 22px; place-items: center; color: #fff; background: var(--coral); border-radius: 50%; font-weight: 700; }
.confirmation-notice > span { background: #b69032; }
.notice-bar strong { font-size: 12px; }
.notice-bar p { margin: 3px 0 0; color: #8b5b51; font-size: 11px; }
.confirmation-notice p { color: #796332; }
.notice-bar button { margin-left: auto; padding: 5px 9px; color: #72342d; background: transparent; border: 1px solid rgba(114, 52, 45, .35); border-radius: 4px; font-size: 11px; }

.table-card { max-height: min(74vh, 900px); overflow: auto; margin-top: 14px; background: var(--paper); border: 1px solid var(--hairline); border-radius: 10px; box-shadow: var(--shadow); }
.table-head, .stock-row { display: grid; grid-template-columns: 32px minmax(210px, 1.35fr) 140px 100px minmax(180px, 1fr) 90px; gap: 18px; align-items: center; }
.table-head { position: sticky; top: 0; z-index: 4; min-height: 38px; padding: 0 18px; color: var(--muted); background: #f0efea; border-bottom: 1px solid var(--hairline); font-size: 10px; letter-spacing: .08em; text-transform: uppercase; }
.head-rank { padding-left: 3px; }
.head-price, .head-score, .head-action { text-align: right; }
.head-price { display: flex; justify-content: flex-end; flex-wrap: wrap; gap: 3px; }
.head-score { display: flex; justify-content: flex-end; }
.sort-button {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 4px 3px;
  color: var(--muted);
  background: transparent;
  border: 1px solid transparent;
  border-radius: 4px;
  font-size: 10px;
  letter-spacing: .04em;
  white-space: nowrap;
}
.sort-button:hover { color: var(--ink); background: #e6e5df; }
.sort-button.active { color: var(--ink); border-color: var(--hairline); }
.sort-indicator { width: 9px; color: var(--coral); font-size: 11px; line-height: 1; }
.stock-row { width: 100%; min-height: 82px; padding: 14px 18px; color: var(--ink); background: transparent; border: 0; border-bottom: 1px solid #e9e8e3; text-align: left; }
.stock-row:last-of-type { border-bottom: 0; }
.stock-row:hover, .stock-row.selected { background: #f7f3ea; }
.row-rank { color: #9b9f9b; font-family: Georgia, serif; font-size: 13px; }
.stock-identity { min-width: 0; }
.stock-name-line { display: flex; align-items: center; gap: 6px; }
.stock-name-line strong { overflow: hidden; font-size: 14px; text-overflow: ellipsis; white-space: nowrap; }
.capital-flow-badge { flex: 0 0 auto; padding: 2px 5px; border-radius: 3px; font-size: 9px; line-height: 1.2; }
.capital-flow-positive { color: #1e6047; background: #d6ecdf; }
.capital-flow-neutral { color: #685622; background: #efe4b9; }
.capital-flow-risk { color: #8b342d; background: #f1d0c8; }
.capital-flow-pending { color: #69706b; background: #e6e7e3; }
.stock-identity small, .score-cell small, .signal-cell small { display: block; margin-top: 5px; overflow: hidden; color: var(--muted); font-size: 10px; text-overflow: ellipsis; white-space: nowrap; }
.stock-identity .pool-membership-summary { overflow: visible; line-height: 1.65; white-space: normal; overflow-wrap: anywhere; }
.stock-identity .research-stage-summary { display: -webkit-box; overflow: hidden; -webkit-box-orient: vertical; -webkit-line-clamp: 2; color: #776340; font-size: 11px; line-height: 1.6; white-space: normal; overflow-wrap: anywhere; }
.stock-identity .evidence-summary { color: #858c92; font-size: 9px; }
.trade-confirmation-tags { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 6px; }
.trade-confirmation-badge { padding: 3px 5px; color: #667078; background: #e8e9e6; border: 1px solid #d2d5d2; border-radius: 4px; font-size: 8px; line-height: 1; white-space: nowrap; }
.trade-confirmation-badge.passed { color: #155b41; background: #d8eee1; border-color: #9ecdb1; }
.trade-confirmation-badge.pending { color: #765d25; background: #f3e8c7; border-color: #dbc98e; }
.trade-confirmation-badge.failed { color: #8a3a31; background: #f4d6cc; border-color: #dfaaa0; }
.role-badge { flex: 0 0 auto; max-width: 74px; overflow: hidden; padding: 3px 5px; border: 1px solid transparent; border-radius: 4px; font-size: 8px; line-height: 1; text-overflow: ellipsis; white-space: nowrap; }
.role-core { color: #155b41; background: #d8eee1; border-color: #9ecdb1; }
.role-front { color: #66501e; background: #f3e7bd; border-color: #dbc67f; }
.role-follow { color: #626970; background: #e7e9e7; border-color: #ced2cf; }
.role-pending { color: #596876; background: #e1eaee; border-color: #c4d2d8; }
.ma-cross-tags { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 6px; }
.factor-tags { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 6px; }
.factor-tag { padding: 3px 5px; border-radius: 4px; font-size: 8px; line-height: 1; white-space: nowrap; }
.factor-tag-positive { color: #155b41; background: #d8eee1; border: 1px solid #9ecdb1; }
.factor-tag-neutral { color: #6f5d2b; background: #f3e8c7; border: 1px solid #dbc98e; }
.ma-cross-badge { display: inline-block; padding: 3px 5px; border: 1px solid transparent; border-radius: 4px; font-size: 9px; line-height: 1; white-space: nowrap; }
.ma-cross-active { color: #4d6672; background: #dce9ed; border-color: #b8d0d8; }
.ma-cross-confirmed { color: #176044; background: #d7ede0; border-color: #9ccdae; }
.ma-cross-intraday { color: #795814; background: #f5e6b6; border-color: #dcc36e; box-shadow: 0 0 0 1px rgba(220, 195, 110, .18); }
.action-tier-badge { display: inline-block; margin-top: 7px; padding: 3px 6px; border: 1px solid transparent; border-radius: 4px; font-size: 10px; line-height: 1; white-space: nowrap; }
.tier-confirmed-attention { color: #14563c; background: #cfe9d9; border-color: #86c29e; }
.tier-early-wash { color: #6f4e0c; background: #f7e7aa; border-color: #d9bd58; }
.tier-tdx-channel { color: #155066; background: #d9f1ee; border-color: #86bbb8; }
.tier-channel-up { color: #176044; background: #d7ede0; border-color: #9ccdae; }
.tier-channel-down { color: #8a3a31; background: #f5ddd6; border-color: #dfaaa0; }
.tier-chip-peak { color: #694b1d; background: #f4e7c7; border-color: #d5ba81; }
.tier-chip-trend-t5 { color: #214f63; background: #dbecef; border-color: #93bcc5; }
.tier-chip-trend-ready { color: #14563c; background: #cfe9d9; border-color: #73b48d; box-shadow: 0 0 0 1px rgba(115, 180, 141, .2); }
.tier-bullish-oversold { color: #734715; background: #f7dfb9; border-color: #d8a85d; }
.tier-bullish-oversold-ready { color: #0f5942; background: #c8ead9; border-color: #61b18d; box-shadow: 0 0 0 1px rgba(97, 177, 141, .24); }
.tier-wait { color: #735a20; background: #f3e8c7; border-color: #d8c58a; }
.tier-structure { color: #4d5961; background: #e7e9e7; border-color: #c9cecb; }
.tier-no-chase { color: #854b2b; background: #f5dfd0; border-color: #e1b9a1; }
.tier-long { color: #4f6672; background: #dce9ed; border-color: #b8d0d8; }
.tier-risk { color: #8a3a31; background: #f4d6cc; border-color: #dfaaa0; }
.tier-neutral { color: #56616b; background: #e6e8e5; border-color: #ccd0cd; }
.live-badge { padding: 2px 4px; color: var(--green); background: rgba(175, 217, 196, .42); border-radius: 3px; font-size: 8px; letter-spacing: .06em; }
.l2-row-badge { padding: 2px 5px; border: 1px solid transparent; border-radius: 3px; font-size: 8px; letter-spacing: .03em; }
.l2-row-badge.l2-confirm { color: #0f5942; background: #d3ecdf; border-color: #86c4a7; }
.l2-row-badge.l2-conflict { color: #8a3a31; background: #f5d9d2; border-color: #dfaaa0; }
.stock-price { text-align: right; }
.stock-price strong { display: block; font-family: Georgia, serif; font-size: 15px; font-weight: 500; }
.stock-price span { display: block; margin-top: 5px; font-family: Georgia, serif; font-size: 12px; }
.stock-price .stock-speed { display: block; margin-top: 4px; font-size: 10px; white-space: nowrap; }
.up { color: var(--green); }
.down { color: var(--red); }
.flat { color: var(--muted); }
.score-cell { text-align: right; }
.score-cell strong { font-family: Georgia, serif; font-size: 16px; font-weight: 500; }
.score-cell small { color: var(--coral); }
.signal-cell { min-width: 0; }
.sector-name { display: block; overflow: hidden; color: var(--ink-soft); font-size: 11px; text-overflow: ellipsis; white-space: nowrap; }
.support-signal { display: block; margin-top: 5px; overflow: hidden; font-size: 9px; font-weight: 600; text-overflow: ellipsis; white-space: nowrap; }
.box-row-signal { display: block; margin-top: 4px; overflow: hidden; font-size: 9px; font-weight: 500; text-overflow: ellipsis; white-space: nowrap; }
.box-row-signal.box-confirmed { color: #247253; }
.box-row-signal.box-testing { color: #8a6814; }
.box-row-signal.box-failed { color: var(--red); }
.box-row-signal.box-inside { color: #66747d; }
.support-positive { color: var(--green); }
.support-caution { color: #8a6814; }
.support-negative { color: var(--red); }
.support-muted { color: var(--muted); }
.row-status { display: flex; align-items: center; justify-content: flex-end; gap: 7px; }
.status-chip { padding: 5px 7px; border-radius: 4px; font-size: 10px; white-space: nowrap; }
.status-confirmed { color: #166646; background: #d7ede0; }
.status-positive { color: #7a641a; background: #f4e8b9; }
.status-risk { color: #8a3a31; background: #f4d6cc; }
.status-watch { color: #56616b; background: #e6e8e5; }
.row-chevron { color: #a2a6a3; font-size: 19px; line-height: 1; }
.table-foot { position: sticky; bottom: 0; z-index: 3; padding: 12px 18px; color: var(--muted); background: rgba(251, 250, 246, .96); border-top: 1px solid #e9e8e3; font-size: 10px; backdrop-filter: blur(8px); }
.load-more-button { display: block; width: calc(100% - 28px); min-height: 40px; margin: 12px 14px 4px; color: var(--ink-soft); background: #f0efea; border: 1px solid var(--hairline); border-radius: 6px; font-size: 11px; }
.load-more-button:hover { color: var(--ink); background: #e7e5de; }
.loading-state, .empty-state { padding: 65px 20px; color: var(--muted); text-align: center; }
.empty-symbol { color: var(--coral); font-family: Georgia, serif; font-size: 33px; }
.empty-state h3 { margin: 12px 0 6px; color: var(--ink); font-family: Georgia, serif; font-size: 20px; font-weight: 400; }
.empty-state p { margin: 0; font-size: 12px; }

.insight-grid { position: fixed; top: 0; right: 0; z-index: 70; width: min(560px, calc(100vw - 32px)); height: 100dvh; overflow-y: auto; overscroll-behavior: contain; padding: 16px; background: var(--canvas); box-shadow: -18px 0 48px rgba(20, 25, 34, .22); scrollbar-width: thin; scrollbar-color: rgba(122, 128, 124, .45) transparent; }
.insight-card { min-height: 250px; padding: 24px; border-radius: 10px; }
.detail-card { color: #fff; background: var(--dark); }
.pulse-card { background: var(--cream); }
.card-topline { display: flex; justify-content: space-between; align-items: center; gap: 12px; }
.detail-card .card-topline { position: sticky; top: -24px; z-index: 5; margin: -24px -24px 0; padding: 20px 24px 12px; background: linear-gradient(180deg, var(--dark) 78%, rgba(20, 25, 34, 0)); }
.detail-top-actions { display: flex; min-width: 0; align-items: center; justify-content: flex-end; gap: 9px; }
.detail-close { display: grid; width: 40px; height: 40px; flex-shrink: 0; padding: 0; place-items: center; color: #fff; background: rgba(255, 255, 255, .08); border: 1px solid rgba(255, 255, 255, .16); border-radius: 50%; font-size: 24px; line-height: 1; }
.detail-backdrop { position: fixed; inset: 0; z-index: 69; display: block; padding: 0; background: rgba(20, 25, 34, .4); border: 0; backdrop-filter: blur(2px); }
.pulse-card .detail-close { color: var(--ink); border-color: var(--hairline); background: var(--paper); }
.pulse-card .card-topline { position: sticky; top: -24px; z-index: 5; margin: -24px -24px 0; padding: 16px 24px 12px; background: var(--cream); }
.detail-card, .pulse-card { min-height: calc(100dvh - 32px); }
.detail-card .eyebrow { color: var(--mint); }
.pulse-card .eyebrow { color: var(--coral); }
.auction-board-panel, .dashboard-alert-panel { margin-top: 18px; padding: 14px; background: rgba(255,255,255,.52); border: 1px solid #e0d7c1; border-radius: 8px; }
.auction-board-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
.auction-board-head strong, .auction-board-head small { display: block; }
.auction-board-head strong { color: var(--ink); font-size: 12px; }
.auction-board-head small { margin-top: 4px; color: var(--muted); font-size: 9px; }
.auction-board-head > span { padding: 3px 6px; color: var(--green); background: #d7ede0; border-radius: 4px; font-size: 8px; text-transform: uppercase; }
.auction-board-head > span.stale { color: #8a6814; background: #f4e8b9; }
.auction-board-list { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 7px; margin-top: 12px; }
.auction-board-list > div { min-width: 0; padding: 9px; background: var(--paper); border-radius: 5px; }
.auction-board-list span, .auction-board-list strong, .auction-board-list small { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.auction-board-list span { color: var(--ink-soft); font-size: 9px; }
.auction-board-list strong { margin-top: 4px; color: var(--green); font-family: Georgia, serif; font-size: 14px; }
.auction-board-list small { margin-top: 4px; color: var(--muted); font-size: 8px; }
.auction-board-panel > p { margin: 10px 0 0; color: var(--muted); font-size: 9px; line-height: 1.5; }
.dashboard-alert-panel > button { display: grid; width: 100%; grid-template-columns: minmax(0, 1fr) auto; gap: 4px 10px; margin-top: 8px; padding: 10px; text-align: left; background: #fff4ef; border: 1px solid #edc9ba; border-radius: 6px; }
.dashboard-alert-panel > button span { overflow: hidden; color: var(--ink); font-size: 10px; font-weight: 700; text-overflow: ellipsis; white-space: nowrap; }
.dashboard-alert-panel > button strong { color: var(--coral); font-family: Georgia, serif; font-size: 12px; }
.dashboard-alert-panel > button small { grid-column: 1 / -1; color: var(--muted); font-size: 8px; line-height: 1.5; }
.observation-count { color: #725b24; font-size: 10px; white-space: nowrap; }
.observation-panel > button { background: #fffbed; border-color: #e4d5a7; }
.observation-panel > button strong { color: #725b24; }
.alert-evidence-note { color: var(--muted); font-size: 10px; line-height: 1.6; }
.alert-evidence-error { color: #a93830; font-size: 11px; line-height: 1.6; }
.stale-market-data { opacity: .58; filter: grayscale(.35); }
.selected-pool, .pulse-time { color: rgba(255, 255, 255, .56); font-size: 10px; }
.pulse-time { color: var(--muted); }
.selected-content { display: flex; flex-direction: column; margin-top: 25px; }
.selected-title { order: 1; }
.selected-models { order: 2; margin-top: 14px; color: rgba(255,255,255,.7); font-size: 12px; line-height: 1.7; }
.selected-models summary { cursor: pointer; }
.selected-models p { margin: 8px 0; overflow-wrap: anywhere; }
.platform-levels { order: 2; }
.daily-position-panel { order: 4; }
.selected-metrics { order: 5; }
.ma-cross-panel { order: 6; }
.reason-list { order: 7; }
.selected-stage-state { order: 8; }
.risk-line { order: 9; }
.stage-timeline-panel { order: 10; }
.selected-title, .pulse-title { display: flex; justify-content: space-between; align-items: flex-start; gap: 20px; }
.selected-title h3, .pulse-title h3 { margin: 0; font-family: Georgia, serif; font-size: 27px; font-weight: 400; }
.factor-profile-detail { margin-top: 16px; padding: 12px; background: rgba(255, 255, 255, .06); border: 1px solid rgba(255, 255, 255, .12); border-radius: 6px; }
.factor-profile-head { display: flex; justify-content: space-between; gap: 10px; color: #fff; font-size: 11px; }
.factor-profile-head small { color: rgba(255, 255, 255, .55); font-size: 9px; }
.factor-profile-list { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 5px 9px; margin-top: 10px; }
.factor-profile-row { display: grid; grid-template-columns: minmax(0, 1fr) 30px 48px; gap: 5px; align-items: center; padding: 5px 0; border-bottom: 1px solid rgba(255, 255, 255, .08); }
.factor-profile-row > span { min-width: 0; }
.factor-profile-row strong, .factor-profile-row small { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.factor-profile-row strong { color: #fff; font-size: 9px; }
.factor-profile-row small { margin-top: 2px; color: rgba(255, 255, 255, .46); font-size: 8px; }
.factor-profile-row b { color: var(--mint); font-family: Georgia, serif; font-size: 10px; text-align: right; }
.factor-profile-row em { overflow: hidden; color: rgba(255, 255, 255, .66); font-size: 8px; font-style: normal; text-align: right; text-overflow: ellipsis; white-space: nowrap; }
.factor-profile-row.unavailable { opacity: .55; }
.selected-title p { margin: 7px 0 0; color: rgba(255, 255, 255, .58); font-size: 11px; }
.selected-price { text-align: right; }
.selected-price strong { display: block; font-family: Georgia, serif; font-size: 25px; font-weight: 400; }
.selected-price span { display: block; margin-top: 5px; font-size: 12px; }
.selected-metrics { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px 10px; margin-top: 27px; }
.selected-metrics div { padding-right: 10px; border-right: 1px solid rgba(255, 255, 255, .14); }
.selected-metrics div:nth-child(2n) { border-right: 0; }
.selected-metrics span { display: block; color: rgba(255, 255, 255, .5); font-size: 10px; }
.selected-metrics strong { display: block; margin-top: 8px; font-family: Georgia, serif; font-size: 16px; font-weight: 400; }
.decision-detail-summary { order: 2; display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 1px; margin-top: 21px; overflow: hidden; background: rgba(255, 255, 255, .12); border: 1px solid rgba(255, 255, 255, .12); border-radius: 6px; }
.decision-detail-summary div { min-width: 0; padding: 11px; background: rgba(255, 255, 255, .055); }
.decision-detail-summary span { display: block; color: rgba(255, 255, 255, .48); font-size: 9px; }
.decision-detail-summary strong { display: block; margin-top: 6px; overflow-wrap: anywhere; font-size: 11px; font-weight: 500; line-height: 1.45; }
.decision-detail-summary .decision-invalid { grid-column: 1 / -1; }
.decision-detail-summary .decision-invalid strong { color: #ffc1b5; }
.support-strip { order: 3; margin-top: 12px; padding: 13px; background: rgba(255, 255, 255, .045); border: 1px solid rgba(255, 255, 255, .13); border-radius: 6px; }
.support-strip.support-positive { background: rgba(58, 151, 105, .09); border-color: rgba(112, 205, 158, .38); }
.support-strip.support-caution { background: rgba(220, 174, 74, .08); border-color: rgba(230, 185, 78, .35); }
.support-strip.support-negative { background: rgba(195, 78, 71, .10); border-color: rgba(221, 115, 104, .46); }
.support-strip-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
.support-strip-head span { display: block; color: rgba(255, 255, 255, .54); font-size: 9px; }
.support-strip-head small { display: block; margin-top: 4px; color: rgba(255, 255, 255, .4); font-size: 8px; }
.support-strip-head strong { color: #fff; font-size: 12px; font-weight: 600; text-align: right; }
.support-strip.support-positive .support-strip-head strong { color: #b9efd2; }
.support-strip.support-caution .support-strip-head strong { color: #ffe2a0; }
.support-strip.support-negative .support-strip-head strong { color: #ffc1b5; }
.support-strip-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 1px; margin-top: 11px; overflow: hidden; background: rgba(255, 255, 255, .09); border-radius: 4px; }
.support-strip-grid div { min-width: 0; padding: 8px 7px; background: rgba(8, 12, 18, .34); }
.support-strip-grid span { display: block; color: rgba(255, 255, 255, .4); font-size: 8px; }
.support-strip-grid strong { display: block; margin-top: 5px; overflow: hidden; color: #fff; font-family: Georgia, serif; font-size: 12px; font-weight: 400; text-overflow: ellipsis; white-space: nowrap; }
.support-strip-updated { display: block; margin-top: 8px; color: rgba(255, 255, 255, .34); font-size: 8px; }
.support-source-note { display: block; margin-top: 8px; color: rgba(255,255,255,.65); font-size: 9px; line-height: 1.6; }
.support-paid-actions { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 10px; }
.support-paid-label { flex-basis: 100%; color: #d6eadf; font-size: 11px; }
.support-paid-actions button { padding: 7px 9px; color: #d6eadf; background: rgba(91,174,129,.16); border: 1px solid rgba(91,174,129,.5); border-radius: 4px; font-size: 11px; cursor: pointer; }
.support-paid-actions button:disabled { opacity: .45; cursor: default; }
.support-paid-actions small { flex-basis: 100%; color: rgba(255,255,255,.55); font-size: 9px; line-height: 1.5; }
.support-paid-actions .support-paid-status { color: #b9efd2; font-size: 11px; }
.support-paid-actions .support-paid-status.blocked { color: #ffe2a0; }
.evidence-matrix { order: 4; display: flex; flex-wrap: wrap; gap: 6px; margin-top: 12px; }
.evidence-matrix span { padding: 5px 7px; color: rgba(255, 255, 255, .55); background: rgba(255, 255, 255, .07); border: 1px solid rgba(255, 255, 255, .12); border-radius: 4px; font-size: 9px; }
.evidence-matrix span.passed { color: #b9efd2; background: rgba(58, 151, 105, .13); border-color: rgba(112, 205, 158, .38); }
.evidence-matrix span.unknown { color: #ffe2a0; background: rgba(220, 174, 74, .1); border-color: rgba(230, 185, 78, .32); }
.selected-content .platform-levels { order: 5; }
.selected-content .daily-position-panel { order: 7; }
.selected-content .selected-metrics { order: 8; }
.selected-content .ma-cross-panel { order: 9; }
.selected-content .reason-list { order: 10; }
.selected-content .selected-stage-state { order: 11; }
.selected-content .risk-line { order: 12; }
.selected-content .stage-timeline-panel { order: 13; }
.daily-position-panel { margin-top: 22px; padding-top: 17px; border-top: 1px solid rgba(255, 255, 255, .14); }
.daily-position-head { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; }
.daily-position-head span { color: var(--mint); font-size: 11px; letter-spacing: .04em; }
.daily-position-head small { color: rgba(255, 255, 255, .46); font-size: 9px; }
.daily-ma-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin-top: 11px; }
.daily-ma-item { position: relative; padding: 10px 8px; background: rgba(255, 255, 255, .06); border: 1px solid rgba(255, 255, 255, .1); border-radius: 5px; }
.daily-ma-item.daily-ma-touched { background: rgba(241, 193, 89, .2); border-color: #e6b94e; box-shadow: inset 0 0 0 1px rgba(230, 185, 78, .18); }
.daily-ma-item.daily-ma-below { background: rgba(224, 111, 93, .2); border-color: #e07b69; box-shadow: inset 0 0 0 1px rgba(224, 123, 105, .2); }
.daily-ma-item span, .daily-range span { display: block; color: rgba(255, 255, 255, .5); font-size: 9px; }
.daily-ma-item strong { display: block; margin-top: 7px; color: #fff; font-family: Georgia, serif; font-size: 15px; font-weight: 400; }
.daily-ma-item.daily-ma-touched strong { color: #ffe2a0; }
.daily-ma-item.daily-ma-below strong { color: #ffc1b4; }
.daily-ma-item small { display: block; min-height: 11px; margin-top: 6px; color: #ffe2a0; font-size: 8px; line-height: 1.3; }
.daily-ma-item.daily-ma-below small { color: #ffc1b4; }
.daily-range { display: grid; grid-template-columns: repeat(2, 1fr) auto; gap: 10px; align-items: end; margin-top: 10px; }
.daily-range > div { padding: 10px; background: rgba(175, 217, 196, .08); border: 1px solid rgba(175, 217, 196, .14); border-radius: 5px; }
.daily-range strong { display: block; margin-top: 7px; color: #fff; font-family: Georgia, serif; font-size: 16px; font-weight: 400; }
.daily-range small { padding-bottom: 10px; color: rgba(255, 255, 255, .4); font-size: 9px; white-space: nowrap; }
.ma-cross-panel { margin-top: 22px; padding-top: 17px; border-top: 1px solid rgba(255, 255, 255, .14); }
.ma-cross-panel-head { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; }
.ma-cross-panel-head span { color: var(--mint); font-size: 11px; letter-spacing: .04em; }
.ma-cross-panel-head small { color: rgba(255, 255, 255, .46); font-size: 9px; }
.ma-cross-detail-list { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 11px; }
.ma-cross-detail { min-width: 145px; padding: 10px; border: 1px solid rgba(255, 255, 255, .14); border-radius: 5px; }
.ma-cross-detail strong { display: block; font-family: Georgia, serif; font-size: 15px; font-weight: 400; }
.ma-cross-detail span { display: block; margin-top: 5px; color: rgba(255, 255, 255, .72); font-size: 10px; }
.ma-cross-detail small { display: block; margin-top: 6px; color: rgba(255, 255, 255, .42); font-size: 9px; }
.ma-cross-detail.ma-cross-active { background: rgba(184, 208, 216, .12); border-color: rgba(184, 208, 216, .6); }
.ma-cross-detail.ma-cross-confirmed { background: rgba(156, 205, 174, .12); border-color: rgba(156, 205, 174, .7); }
.ma-cross-detail.ma-cross-intraday { background: rgba(220, 195, 110, .14); border-color: rgba(220, 195, 110, .85); }
.ma-cross-detail.ma-cross-intraday strong { color: #ffe2a0; }
.ma-cross-detail.ma-cross-confirmed strong { color: #c6efd5; }
.ma-cross-detail.ma-cross-active strong { color: #c8e0e7; }
.ma-cross-detail.ma-cross-pending { border-style: dashed; opacity: .8; }
.ma-cross-empty { margin-top: 11px; color: rgba(255, 255, 255, .42); font-size: 10px; }
.reason-list { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 23px; }
.reason-list span { padding: 5px 7px; color: #d9e8de; background: rgba(175, 217, 196, .14); border: 1px solid rgba(175, 217, 196, .2); border-radius: 4px; font-size: 10px; }
.stage-timeline-panel { margin-top: 20px; padding-top: 15px; border-top: 1px solid rgba(255, 255, 255, .14); }
.stage-timeline-head, .stage-cycle-heading { display: flex; flex-wrap: wrap; align-items: baseline; justify-content: space-between; gap: 8px; }
.stage-timeline-head strong { color: var(--mint); font-size: 11px; font-weight: 500; }
.stage-timeline-head span, .stage-cycle-heading span { color: rgba(255, 255, 255, .45); font-size: 9px; }
.stage-timeline-message, .stage-timeline-note { color: rgba(255, 255, 255, .48); font-size: 9px; line-height: 1.5; }
.stage-cycle-list { margin-top: 10px; }
.stage-cycle + .stage-cycle { margin-top: 13px; padding-top: 12px; border-top: 1px solid rgba(255, 255, 255, .1); }
.stage-cycle-heading strong { font-size: 10px; font-weight: 500; }
.stage-phase-row { display: grid; grid-template-columns: minmax(90px, .8fr) minmax(150px, 1.2fr); gap: 5px 12px; padding: 8px 0; border-bottom: 1px solid rgba(255, 255, 255, .07); }
.stage-phase-row > strong { grid-row: span 2; color: #e5eee8; font-size: 10px; font-weight: 500; }
.stage-phase-row > span, .stage-phase-row > small { color: rgba(255, 255, 255, .55); font-size: 9px; line-height: 1.4; }
.stage-phase-row > small { grid-column: 2; color: rgba(255, 255, 255, .38); }
.platform-levels { margin-top: 18px; padding: 15px 0 0; border-top: 1px solid rgba(255, 255, 255, .14); }
.platform-levels-head { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; }
.platform-levels-head span { color: var(--mint); font-size: 11px; letter-spacing: .04em; }
.platform-levels-head small { color: rgba(255, 255, 255, .46); font-size: 9px; text-align: right; }
.platform-state-line { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; margin-top: 12px; }
.platform-state-line strong { color: #fff; font-size: 12px; font-weight: 600; }
.platform-state-line span { color: rgba(255, 255, 255, .56); font-size: 9px; text-align: right; }
.platform-levels.box-confirmed .platform-state-line strong { color: #b9efd2; }
.platform-levels.box-testing .platform-state-line strong { color: #ffe2a0; }
.platform-levels.box-failed .platform-state-line strong { color: #ffc1b5; }
.platform-track { position: relative; height: 18px; margin: 9px 2px 3px; }
.platform-track-fill { position: absolute; top: 8px; right: 0; left: 0; height: 2px; background: rgba(255, 255, 255, .24); }
.platform-track-fill::before, .platform-track-fill::after { position: absolute; top: -3px; width: 1px; height: 8px; content: ''; background: rgba(255, 255, 255, .48); }
.platform-track-fill::before { left: 0; }
.platform-track-fill::after { right: 0; }
.platform-track-marker { position: absolute; top: 4px; width: 10px; height: 10px; margin-left: -5px; background: #fff; border: 2px solid var(--dark); border-radius: 50%; box-shadow: 0 0 0 1px rgba(255, 255, 255, .48); }
.platform-levels.box-confirmed .platform-track-marker { background: #8ed6b1; }
.platform-levels.box-testing .platform-track-marker { background: #e6b94e; }
.platform-levels.box-failed .platform-track-marker { background: #df7a6d; }
.platform-level-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); margin-top: 3px; border-top: 1px solid rgba(255, 255, 255, .1); border-bottom: 1px solid rgba(255, 255, 255, .1); }
.platform-level { min-width: 0; padding: 10px 8px; }
.platform-level + .platform-level { border-left: 1px solid rgba(255, 255, 255, .1); }
.current-level { text-align: center; }
.attack-level { text-align: right; }
.platform-level-label { color: rgba(255, 255, 255, .64); font-size: 10px; }
.platform-level strong { display: block; margin-top: 6px; overflow: hidden; font-family: Georgia, serif; font-size: 18px; font-weight: 400; text-overflow: ellipsis; white-space: nowrap; }
.platform-level small { display: block; margin-top: 5px; overflow: hidden; color: rgba(255, 255, 255, .48); font-size: 8px; text-overflow: ellipsis; white-space: nowrap; }
.platform-structure-metrics { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 1px; margin-top: 8px; overflow: hidden; background: rgba(255, 255, 255, .1); border: 1px solid rgba(255, 255, 255, .1); border-radius: 4px; }
.platform-structure-metrics span { min-width: 0; padding: 7px 6px; color: rgba(255, 255, 255, .42); background: rgba(255, 255, 255, .025); font-size: 8px; white-space: nowrap; }
.platform-structure-metrics strong { display: block; margin-top: 4px; overflow: hidden; color: rgba(255, 255, 255, .78); font-size: 9px; font-weight: 500; text-overflow: ellipsis; }
.platform-rules { display: flex; flex-wrap: wrap; gap: 5px 12px; margin-top: 9px; color: rgba(255, 255, 255, .42); font-size: 8px; line-height: 1.45; }
.platform-rules span:first-child { color: rgba(185, 239, 210, .68); }
.stage-tracking-badge { display: inline-block; max-width: 170px; margin-left: 5px; padding: 2px 5px; border: 1px solid transparent; border-radius: 3px; font-size: 9px; line-height: 1.25; vertical-align: middle; }
.stage-tracking-badge.stage-state-watch { color: #3f5f20; background: #e5efd5; border-color: #b7cf92; }
.stage-tracking-badge.stage-state-breakout { color: #745000; background: #fff0bd; border-color: #d9b84f; }
.stage-tracking-badge.stage-state-risk { color: #8a2f26; background: #f8dcd5; border-color: #dd9d91; }
.stage-tracking-badge.stage-state-neutral { color: #4d5961; background: #e7e9e7; border-color: #c9cecb; }
.selected-stage-state { display: flex; align-items: baseline; flex-wrap: wrap; gap: 8px; margin-top: 16px; padding: 10px 12px; border: 1px solid rgba(255, 255, 255, .14); border-radius: 5px; }
.selected-stage-state > span { color: rgba(255, 255, 255, .54); font-size: 10px; }
.selected-stage-state > strong { font-size: 11px; font-weight: 500; }
.selected-stage-state > small { flex-basis: 100%; color: rgba(255, 255, 255, .42); font-size: 9px; }
.selected-stage-state.stage-state-watch { color: #d9e6bd; background: rgba(159, 190, 118, .1); border-color: rgba(159, 190, 118, .45); }
.selected-stage-state.stage-state-breakout { color: #ffe0a1; background: rgba(220, 174, 74, .11); border-color: rgba(220, 174, 74, .65); }
.selected-stage-state.stage-state-risk { color: #ffc1b5; background: rgba(195, 78, 71, .11); border-color: rgba(221, 115, 104, .7); }
.selected-stage-state.stage-state-neutral { color: rgba(255, 255, 255, .65); background: rgba(255, 255, 255, .08); border-color: rgba(255, 255, 255, .18); }
.risk-line { margin-top: 15px; color: #f0b09c; font-size: 10px; line-height: 1.5; }
.placeholder-detail { display: flex; min-height: 185px; flex-direction: column; align-items: center; justify-content: center; color: rgba(255, 255, 255, .58); text-align: center; }
.placeholder-mark { color: var(--mint); font-family: Georgia, serif; font-size: 35px; }
.placeholder-detail h3 { margin: 9px 0 5px; color: #fff; font-family: Georgia, serif; font-size: 20px; font-weight: 400; }
.placeholder-detail p { max-width: 240px; margin: 0; font-size: 11px; line-height: 1.6; }
.pulse-title { margin-top: 24px; }
.pulse-title h3 { color: var(--ink); }
.pulse-badge { padding: 6px 8px; color: #166646; background: var(--mint); border-radius: 4px; font-family: Georgia, serif; font-size: 13px; }
.bar-list { margin-top: 23px; }
.bar-item + .bar-item { margin-top: 13px; }
.bar-label { display: flex; justify-content: space-between; color: var(--muted); font-size: 10px; }
.bar-label strong { color: var(--ink); font-family: Georgia, serif; font-size: 12px; font-weight: 400; }
.bar-track { height: 5px; margin-top: 6px; overflow: hidden; background: rgba(20, 25, 34, .09); border-radius: 4px; }
.bar-track span { display: block; height: 100%; border-radius: inherit; }
.bar-mint { background: var(--green); }
.bar-blue { background: var(--blue); }
.bar-yellow { background: #c6a323; }
.bar-coral { background: var(--coral); }
.pulse-note { margin: 20px 0 0; color: var(--muted); font-family: Georgia, serif; font-size: 11px; line-height: 1.5; }
.fqgate-radar { margin-top: 22px; padding-top: 16px; border-top: 1px solid var(--hairline); }
.fqgate-radar-head { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; }
.fqgate-radar-head span { display: block; color: var(--coral); font-size: 9px; letter-spacing: .08em; }
.fqgate-radar-head small { display: block; margin-top: 5px; color: var(--muted); font-size: 9px; }
.fqgate-radar-head > strong { color: var(--muted); font-family: ui-monospace, monospace; font-size: 10px; font-weight: 400; }
.fqgate-diagnosis { margin-top: 12px; padding: 11px; background: rgba(255, 255, 255, .42); border-left: 3px solid var(--muted); border-radius: 4px; }
.fqgate-diagnosis.diagnosis-positive { border-left-color: var(--green); }
.fqgate-diagnosis.diagnosis-risk { border-left-color: var(--coral); }
.fqgate-diagnosis > div { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; }
.fqgate-diagnosis strong { color: var(--ink); font-size: 11px; font-weight: 600; }
.fqgate-diagnosis span, .fqgate-diagnosis small { color: var(--muted); font-size: 8px; }
.fqgate-diagnosis p { margin: 7px 0 5px; color: var(--ink-soft); font-size: 9px; line-height: 1.55; }
.fqgate-diagnosis small { display: block; line-height: 1.5; }
.fqgate-overview-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1px; margin-top: 12px; overflow: hidden; background: var(--hairline); border: 1px solid var(--hairline); border-radius: 5px; }
.fqgate-overview-grid div { padding: 8px 7px; background: rgba(255, 255, 255, .42); }
.fqgate-overview-grid span { display: block; color: var(--muted); font-size: 8px; }
.fqgate-overview-grid strong { display: block; margin-top: 5px; color: var(--ink); font-family: Georgia, serif; font-size: 14px; font-weight: 400; }
.fqgate-alert-list { margin-top: 10px; }
.fqgate-diagnostics { margin-top: 10px; color: var(--muted); font-size: 10px; }
.fqgate-diagnostics summary { cursor: pointer; }
.fqgate-diagnostics p { overflow-wrap: anywhere; line-height: 1.5; }
.fqgate-alert-row { display: grid; grid-template-columns: 48px minmax(0, 1fr); gap: 7px; padding: 7px 0; border-bottom: 1px solid rgba(20, 25, 34, .08); }
.fqgate-alert-row span { color: var(--muted); font-family: ui-monospace, monospace; font-size: 8px; }
.fqgate-alert-row strong { overflow: hidden; color: var(--ink); font-size: 9px; font-weight: 500; text-overflow: ellipsis; white-space: nowrap; }
.fqgate-empty { display: block; margin-top: 10px; color: var(--muted); font-size: 9px; }
.dat-intelligence { margin-top: 12px; padding-top: 11px; border-top: 1px solid var(--hairline); }
.dat-intelligence-head { display: flex; justify-content: space-between; gap: 10px; color: var(--muted); font-size: 9px; }
.dat-intelligence-head strong { color: var(--ink); font-size: 10px; }
.dat-module-strip { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 1px; margin-top: 8px; background: var(--hairline); }
.dat-module-strip span { min-width: 0; padding: 6px; background: rgba(255, 255, 255, .5); color: var(--muted); font-size: 8px; }
.dat-module-strip span strong { display: block; margin-top: 3px; color: var(--ink); font-size: 10px; }
.dat-module-strip .dat-stale { box-shadow: inset 2px 0 var(--coral); }
.dat-event-row strong i { color: var(--muted); font-size: 7px; font-style: normal; }
.dat-catalog { margin-top: 10px; color: var(--muted); font-size: 9px; }
.dat-catalog summary { cursor: pointer; }
.dat-catalog input { box-sizing: border-box; width: 100%; margin: 8px 0 4px; padding: 6px; border: 1px solid var(--hairline); border-radius: 4px; background: rgba(255, 255, 255, .5); color: var(--ink); font-size: 9px; }
.dat-catalog-list { max-height: 170px; overflow: auto; }
.dat-catalog-list span { display: grid; grid-template-columns: 44px minmax(70px, .8fr) minmax(100px, 1fr); gap: 5px; padding: 5px 0; border-bottom: 1px solid var(--hairline); }
.dat-catalog-list i { color: var(--coral); font-style: normal; }
.dat-catalog-list strong { overflow: hidden; color: var(--ink); text-overflow: ellipsis; white-space: nowrap; }
.dat-catalog-list code { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.dashboard-footer { display: flex; justify-content: space-between; gap: 15px; margin-top: 40px; padding-top: 17px; color: var(--muted); border-top: 1px solid var(--hairline); font-size: 10px; letter-spacing: .06em; }

@media (max-width: 1100px) {
  .topbar { padding: 0 24px; }
  .dashboard { padding: 24px 24px 26px; }
  .topbar-center { display: none; }
  .table-head, .stock-row { grid-template-columns: 34px minmax(170px, 1.2fr) 96px 68px minmax(135px, 1fr) 72px; gap: 9px; padding-left: 13px; padding-right: 13px; }
  .workspace { grid-template-columns: minmax(0, 1fr); gap: 18px; }
  .daily-range { grid-template-columns: repeat(2, 1fr); }
  .daily-range small { grid-column: 1 / -1; padding: 0; }
  .pool-rail {
    position: sticky;
    top: 0;
    z-index: 12;
    display: flex;
    max-height: none;
    gap: 8px;
    min-width: 0;
    overflow-x: auto;
    margin: 0 -24px;
    padding: 10px 24px;
    background: rgba(238, 237, 231, .95);
    border-top: 1px solid var(--hairline);
    border-bottom: 1px solid var(--hairline);
    backdrop-filter: blur(10px);
    scrollbar-width: none;
  }
  .pool-rail::-webkit-scrollbar { display: none; }
  .rail-heading, .rail-note { display: none; }
  .pool-item { flex: 0 0 auto; width: auto; min-height: 44px; padding: 8px 12px; grid-template-columns: 20px auto auto; gap: 8px; background: var(--paper); border-color: var(--hairline); border-radius: 6px; }
  .pool-item-mark { width: 20px; height: 20px; font-size: 12px; border-radius: 4px; }
  .pool-item-copy small { display: none; }
  .pool-item.active { border-color: var(--ink-soft); }
  .view-controls { width: 100%; }
  .view-filter-row { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) auto; }
  .view-order-row { justify-content: space-between; }
  .factor-groups { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .factor-metrics { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}
@media (min-width: 1101px) and (max-width: 1280px) {
  .workspace { grid-template-columns: 180px minmax(0, 1fr); gap: 22px; }
  .table-head, .stock-row { grid-template-columns: 28px minmax(170px, 1.2fr) 100px 75px minmax(130px, 1fr) 80px; gap: 12px; }
}
@media (max-width: 820px) {
  .decision-overview { grid-template-columns: 200px minmax(0, 1fr); gap: 0 16px; padding: 16px 16px 8px; }
  .decision-metrics article { padding: 12px 8px; }
  .decision-metrics strong { font-size: 17px; }
  .decision-overview-head h1 { font-size: 22px; }
  .view-header { grid-template-columns: minmax(0, 1fr); gap: 14px; }
  .market-brief-summary { display: none; }
  .view-controls { align-items: stretch; }
  .view-filter-row { grid-template-columns: minmax(0, 1fr) auto; gap: 8px; }
  .mobile-filter-toggle { display: block; min-height: 44px; padding: 0 12px; border: 1px solid var(--hairline); border-radius: 6px; background: var(--paper); color: var(--ink-soft); font-size: 12px; white-space: nowrap; }
  .mobile-filter-toggle.selected { color: var(--green); border-color: var(--green); }
  .view-controls:not(.expanded) .view-filter-row > .board-filter, .view-controls:not(.expanded) .view-filter-row > .ghost-button, .view-controls:not(.expanded) .view-order-row { display: none; }
  .search-box { height: 44px; }
  .view-filter-row > .board-filter { grid-column: 1 / -1; height: 44px; }
  .view-filter-row > .ghost-button { grid-column: 1 / -1; height: 44px; }
  .board-filter select, .refresh-control select, .mobile-sort-controls select { font-size: 13px; }
  .view-order-row { justify-content: space-between; flex-wrap: wrap; gap: 10px; }
  .toolbar-caption { display: none; }
  .mobile-sort-controls { flex: 1; min-width: 170px; grid-template-columns: minmax(0, 1fr) 44px; }
  .mobile-sort-controls label, .refresh-control { height: 44px; }
  .refresh-control select { min-height: 44px; }
  .table-card { max-height: none; overflow: visible; }
  .table-head { display: none; }
  .stock-row { min-width: 0; min-height: 0; grid-template-columns: minmax(0, 1fr) 96px; gap: 10px 16px; padding: 18px; }
  .row-rank { display: none; }
  .stock-identity { grid-column: 1; grid-row: 1 / span 2; }
  .stock-name-line { flex-wrap: wrap; gap: 6px; }
  .stock-name-line strong { font-size: 15px; }
  .stock-identity small, .score-cell small, .signal-cell small { font-size: 11px; line-height: 1.6; }
  .stock-price { grid-column: 2; grid-row: 1; }
  .stock-price strong { font-size: 19px; }
  .stock-price span { font-size: 14px; }
  .stock-price .stock-speed { font-size: 11px; }
  .score-cell { grid-column: 2; grid-row: 2; align-self: start; }
  .score-cell small { display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 2; white-space: normal; }
  .signal-cell { grid-column: 1 / -1; grid-row: 3; padding-top: 10px; border-top: 1px solid #ece9e1; }
  .sector-name { font-size: 12px; }
  .row-status { grid-column: 1 / -1; grid-row: 4; justify-content: space-between; }
  .trade-confirmation-badge, .ma-cross-badge, .factor-tag { font-size: 10px; line-height: 1.3; }
  .table-foot { position: static; font-size: 11px; line-height: 1.7; }
}
@media (max-width: 600px) {
  .topbar { height: 62px; padding: 0 14px; }
  .topbar-actions { gap: 8px; }
  .alert-setting-button { min-width: 34px; padding: 0 6px; font-size: 0; }
  .alert-setting-button::first-letter { font-size: 13px; }
  .urgent-alert-banner { top: 70px; width: calc(100vw - 20px); }
  .urgent-alert-main { grid-template-columns: 34px minmax(0, 1fr); padding: 12px; }
  .urgent-alert-icon { width: 34px; height: 34px; }
  .urgent-alert-action { display: none; }
  .urgent-alert-copy strong { font-size: 12px; }
  .urgent-alert-copy small { font-size: 9px; }
  .alert-button { padding: 0 8px; }
  .alert-button b { position: absolute; top: -5px; right: -5px; }
  .auction-board-list { grid-template-columns: 1fr; }
  .dat-points-badge { min-height: 40px; padding: 4px 8px; font-size: 10px; }
  .dat-points-badge small { font-size: 8px; }
  .brand-caption { display: none; }
  .brand-lockup { gap: 7px; }
  .brand-name { font-size: 12px; letter-spacing: .08em; }
  .brand-lockup { min-width: 0; }
  .market-state { display: none; }
  .dashboard { padding: 16px 14px 20px; }
  .decision-overview { display: block; margin-bottom: 10px; padding: 14px 12px 6px; }
  .decision-overview-head { align-items: stretch; flex-direction: column; gap: 12px; }
  .decision-overview-head > div:first-child { position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); }
  .decision-overview-head h1 { font-size: 20px; }
  .view-mode-switch { width: 100%; }
  .decision-metrics { margin-top: 12px; }
  .decision-metrics article { min-height: 68px; padding: 10px 6px; }
  .decision-metrics strong { margin: 8px 0 0; font-size: 15px; }
  .decision-metrics span { font-size: 10px; }
  .decision-metrics small { display: none; }
  .intraday-prompt { display: block; font-size: 11px; }
  .intraday-prompt strong { display: block; margin-bottom: 5px; }
  .intraday-prompt-body { display: block; }
  .intraday-prompt-body > span { display: block; margin-bottom: 4px; }
  .intraday-prompt small { display: block; }
  .overview-context { display: block; font-size: 12px; line-height: 1.6; }
  .overview-context p { margin: 8px 0; }
  .overview-notes { margin-top: 6px; }
  .market-brief { gap: 10px; min-height: 44px; margin-bottom: 16px; padding: 10px 12px; }
  .market-brief-label { display: none; }
  .market-brief strong { font-size: 12px; }
  .market-brief-link { font-size: 11px; }
  .calibration-strip { align-items: flex-start; flex-direction: column; gap: 5px; }
  .pool-rail { margin-right: -14px; margin-left: -14px; padding-right: 14px; padding-left: 14px; }
  .view-header { gap: 12px; padding-bottom: 12px; }
  .view-header h2 { font-size: 22px; }
  .view-title-line { margin-top: 0; }
  .view-kicker { display: none; }
  .pool-title > p { -webkit-line-clamp: 1; margin-top: 6px; font-size: 11px; }
  .search-box input, .board-filter select { font-size: 16px; }
  .count-legend { font-size: 11px; }
  .factor-research-panel { padding: 12px; }
  .factor-panel-head { align-items: flex-start; flex-direction: column; gap: 5px; }
  .factor-groups { grid-template-columns: 1fr; gap: 8px; }
  .factor-runbar { align-items: stretch; flex-direction: column; }
  .factor-runbar label { justify-content: space-between; min-height: 30px; }
  .factor-runbar label input[type="date"], .factor-runbar label input[type="number"] { width: 150px; }
  .factor-runbar .factor-check { justify-content: flex-start; }
  .factor-action-button, .factor-secondary-button { width: 100%; min-height: 36px; }
  .factor-metrics { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .factor-profile-list { grid-template-columns: 1fr; }
  .stock-row { padding: 16px 14px; gap: 10px 12px; }
  .sector-name, .support-signal { display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 2; white-space: normal; }
  .insight-grid { width: 100vw; padding: 0; background: var(--dark); }
  .insight-card { min-height: 100dvh; padding: 20px 18px calc(30px + env(safe-area-inset-bottom)); border-radius: 0; }
  .insight-grid.market-open { background: var(--cream); }
  .pulse-card .card-topline { top: -20px; margin: -20px -18px 0; padding: 16px 18px 12px; }
  .detail-card .card-topline { top: -20px; margin: -20px -18px 0; padding: 16px 18px 12px; }
  .decision-detail-summary span { font-size: 11px; }
  .decision-detail-summary strong { font-size: 12px; }
  .support-strip-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); }
  .selected-metrics { grid-template-columns: repeat(2, 1fr); row-gap: 15px; }
  .platform-levels-head { align-items: flex-start; flex-direction: column; }
  .platform-levels-head small { text-align: left; }
  .platform-structure-metrics { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .dashboard-footer { align-items: flex-start; flex-direction: column; }
}
.repair-stage-tags { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin: 12px 0; }
.repair-stage-tags > span { color: var(--ink-soft); font-size: 11px; }
@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after { scroll-behavior: auto !important; animation: none !important; transition: none !important; }
}
</style>
